var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { repeat } from 'lit/directives/repeat.js';
import { marked } from 'marked';
// Importing these named exports also registers their custom elements
// (kr-dialog, kr-tab-group, kr-tab, kr-text-field for the browse dialog;
// kr-checkbox for the settings panel's Tools toggles).
import { KRFilePreview, KRSnackbar } from '@kodaris/krubble-components';
import { KRHttp } from '@kodaris/krubble-http';
/**
 * The action-confirmation decisions, in the left-to-right order they render and
 * the order the arrow keys walk. Yes is first so it's the default highlight.
 * Shared by the render and the keyboard handler so the two never drift.
 */
const CONFIRMATION_DECISIONS = [
    {
        decision: 'approve',
        label: 'Yes',
    },
    {
        decision: 'approveAlways',
        label: 'Always allow',
    },
    {
        decision: 'reject',
        label: 'No',
    },
];
/**
 * The modes as shown in the composer's mode selector, in menu order. `label` is
 * the short trigger text (Gemini-style inline label); `description` is the menu
 * sublabel. Written for a non-technical user — no "guarded tool" jargon.
 */
const CHATBOT_MODES = [
    {
        mode: 'NORMAL',
        label: 'Ask',
        description: 'Asks before making changes',
    },
    {
        mode: 'EDIT',
        label: 'Auto',
        description: 'Makes changes automatically',
    },
    {
        mode: 'PLAN',
        label: 'Plan',
        description: 'Suggests a plan, makes no changes',
    },
];
/** Image extensions the chatbot renders as thumbnails (used when MIME is absent). */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
/**
 * Video extensions the picker accepts. The AI cannot watch video — these upload
 * and store for playback and tool use only, and the backend tells the model so.
 * Matches what kr-file-preview can play back.
 */
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov'];
/**
 * File extensions the picker accepts. Mirrors what the backend can read: the
 * document/spreadsheet family Bedrock ingests, the plain-text family read as
 * fallback, and the image formats. Videos are accepted for upload/storage even
 * though the model can't read them. This is a UX convenience — the backend
 * accepts anything and just tells the model when a type can't be read.
 */
const ACCEPTED_FILE_EXTENSIONS = [
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'html', 'md', 'txt', 'json', 'xml', 'vtt', 'srt',
    ...IMAGE_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
];
/**
 * AI chatbot component with SSE streaming.
 *
 * Accepts a `send` callback that returns a streaming Response (SSE format).
 * Parses the stream for text tokens, status updates, and errors.
 * Dispatches a `stream-event` custom event for every SSE message so consumers can react.
 *
 * ## SSE Stream Format
 * The component expects `data:` lines with JSON payloads containing a `type` field:
 * - `STATUS` — `{ type: "STATUS", message: "Thinking..." }`
 * - `RESPONSE_PART` — `{ type: "RESPONSE_PART", message: "token text" }`
 * - `TOOL_RESULT` — `{ type: "TOOL_RESULT", message: "tool_name", data: {...} }`
 * - `ERROR` — `{ type: "ERROR", message: "Something went wrong" }`
 * - `HEARTBEAT` — `{ type: "HEARTBEAT" }` (ignored)
 * - `ACTION_CONFIRMATION_REQUIRED` — a guarded tool paused for approval;
 *   `{ type, data: { actions: [...] }, finished: true }` (Rail 3).
 *
 * The final event should include `finished: true` to signal stream completion.
 *
 * ## Turn locking (when the composer is free to send)
 * `finished: true` is the single reliable "turn over, safe to send" signal — the
 * component unlocks the composer on it, and also on stream close/error as a
 * fallback so a dropped stream never wedges the UI. The one exception is
 * `ACTION_CONFIRMATION_REQUIRED`: it too carries `finished: true`, but the
 * conversation is now awaiting an approve/deny decision, so the composer stays
 * blocked and only a confirmation decision (not a new message) is a valid next
 * call.
 *
 * ## Usage
 * ```html
 * <kr-chatbot
 *   label="AI Assistant"
 *   .send=${(params) => {
 *     return fetch('/api/ai/chat/stream', {
 *       method: 'POST',
 *       headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
 *       credentials: 'include',
 *       body: JSON.stringify({ userText: params.message })
 *     });
 *   }}
 * ></kr-chatbot>
 * ```
 *
 * @slot - Optional slot content displayed above the messages area
 *
 * @fires message-submit - When the user sends a message. Cancelable. Detail: `{ message: string }`
 * @fires stream-event - Dispatched for every SSE message received. Detail contains the parsed event data.
 * @fires conversation-created - When a turn with no target creates a new conversation and the chatbot pins it. Detail: `{ conversation }` (the full conversation object).
 * @fires conversation-title - When the server sends the generated title for a new conversation (end of its first turn). The header label is already updated; this is for consumers with their own chrome to sync (breadcrumbs, tab titles). Detail: `{ title }`.
 */
export class KRChatbot extends LitElement {
    constructor() {
        super(...arguments);
        // --- Private properties ---
        /**
         * Internal toggle for how the action-confirmation prompt (Rail 3) is presented:
         * - `false`: the compact card that floats over the messages, pinned above the
         *   composer — plain-language ask + Yes/Always/No only, no tool arguments.
         * - `true`: the prompt renders inline in the message stream (Claude-CLI style),
         *   grows to any height (no inner scroll — the transcript scrolls), and offers a
         *   "Show details" disclosure of the raw tool input for technical users.
         * Flip here to switch approaches; the floating path is unchanged when false.
         */
        this._inlineConfirm = true;
        /**
         * Internal toggle for how the assistant's reasoning ("thinking") is presented:
         * - `false`: the current collapsible grey "Thinking" disclosure (kept so we can
         *   revert).
         * - `true`: reasoning renders as a plain agentic message — same text style as a
         *   normal assistant reply, no box, no collapse.
         * Flip here to switch approaches.
         */
        this._reasoningAsMessage = true;
        this._reader = null;
        this._decoder = new TextDecoder();
        this._buffer = '';
        /**
         * True once the current turn ended cleanly (a `finished:true` chunk arrived).
         * Distinguishes a real completion from a dropped stream: if the SSE stream
         * closes (`result.done`) while this is still false, the turn is NOT over — the
         * connection dropped (5-min emitter timeout, tab was backgrounded, network
         * blip) — so we reconnect instead of unlocking the composer. Reset per turn.
         */
        this._turnFinishedCleanly = false;
        /**
         * Consecutive failed reconnect attempts for the current drop. Bounds the retry
         * loop so a genuinely dead turn (server gone) can't spin forever — after the
         * cap we give up and finish the turn. Reset when a reconnect succeeds or a new
         * turn starts.
         */
        this._reconnectAttempts = 0;
        this._userHasScrolledUp = false;
        /**
         * Keep the end of the streaming response in view. Set at the start of every
         * turn (send, confirmation decision, reconnect); cleared when the user scrolls
         * up on their own (wheel, touch drag, scrollbar drag) and restored when they
         * scroll back to the bottom. Checked in updated() after every streamed chunk.
         */
        this._followStream = false;
        this._isDragging = false;
        this._isResizing = false;
        this._overlay = null;
        this._resizeDir = '';
        this._dragStartX = 0;
        this._dragStartY = 0;
        this._dragStartLeft = 0;
        this._dragStartTop = 0;
        this._resizeStartW = 0;
        this._resizeStartH = 0;
        /** Cursor X at drawer resize start. */
        this._drawerResizeStartX = 0;
        /** Drawer width at resize start. */
        this._drawerResizeStartW = 0;
        /** Counter for depth-aware dragenter/dragleave tracking. */
        this._dragDepth = 0;
        /** Monotonic id source for pending attachments. */
        this._pendingSeq = 0;
        /**
         * The `aiAnalyze` mode for the next picked files — set when the attach action
         * opens the picker (true = "Attach Files", false = "Attach Files for
         * Reference"), read by the file-input change handler. Not reactive: it's read
         * synchronously in the change handler, never rendered.
         */
        this._pendingAttachAnalyze = true;
        /**
         * Request-id guards for skill searches — bumped on each search so out-of-order
         * responses are dropped (mirrors the kr-combo-box `_requestId` pattern). Org and
         * global searches run concurrently into separate buffers, so each has its own.
         */
        this._orgSkillRequestId = 0;
        this._globalSkillRequestId = 0;
        this._invocableSkillRequestId = 0;
        this._suggestedActionRequestId = 0;
        /**
         * Codes of global skills with an in-flight enable/disable request. The `+`
         * button reads this to show a pending state and ignore repeat clicks. Mutated
         * in place; call requestUpdate() after changing it to re-render.
         */
        this._globalSkillPending = new Set();
        /**
         * The live Web Speech recognizer while dictating, else null. Not reactive —
         * `_isDictating` drives the UI; this is just the handle used to stop it.
         */
        this._speechRecognition = null;
        /**
         * The composer text as it stood when dictation started. Each `result` event
         * re-reports the whole utterance (final + interim), so the value is rebuilt as
         * this base + the transcript rather than appended to — appending would repeat
         * the interim text on every event.
         */
        this._speechBaseValue = '';
        /**
         * Transcript of utterances the recognizer has marked final this session.
         * Held separately because `results` is pruned in continuous mode on some
         * browsers; without this, earlier sentences would vanish mid-dictation.
         */
        this._speechFinalTranscript = '';
        // --- @property (public API) ---
        /**
         * Callback function for sending messages. Receives the user's message and
         * current conversation history. Must return a Response with a streaming SSE body.
         *
         * Optional — when not set, the chatbot uses its built-in default that streams
         * from the Kodaris Bedrock conversation endpoints. Set this to override.
         */
        this.send = null;
        /**
         * Callback function called on init to load existing conversation messages.
         * Must return a Promise that resolves with an array of KRChatbotMessage.
         *
         * Optional — when not set, the chatbot uses its built-in default that loads
         * existing messages from the Kodaris Bedrock conversation. Set this to override.
         */
        this.load = null;
        /**
         * Conversation messages. Can be set externally for initial messages.
         * Updated internally as the conversation progresses.
         */
        this.messages = [];
        /**
         * Header label text.
         */
        this.label = 'Kat';
        /**
         * Input placeholder text.
         */
        this.placeholder = 'Type a message...';
        /**
         * Whether the chat panel is open. Only relevant in floating mode.
         */
        this.opened = false;
        /**
         * Hide the built-in floating toggle button. Use this when an external
         * control (e.g. the site-editor bar) owns opening the chat — the panel is
         * then driven via the `open()` / `close()` / `toggle()` methods.
         */
        this.hideToggle = false;
        /**
         * Layout mode.
         * - `'floating'` (default): the chatbot is a fixed bottom-right widget with a
         *   toggle button, and the panel can be dragged and resized. This is the
         *   traditional chat-widget appearance.
         * - `'inline'`: the chatbot drops its fixed positioning and fills its parent
         *   container (100% width/height, no border radius/shadow, no toggle, no
         *   drag/resize). The panel is always open. Use this to embed the chat as a
         *   full page or panel region.
         */
        this.variant = 'floating';
        /**
         * The conversation this chatbot's turns target (backend `conversationID`).
         *
         * - Set → every turn is sent with this `conversationID`, so the chat continues
         *   that specific conversation.
         * - Unset → the first turn is sent with `createNew: true` (a fresh
         *   conversation); the component then resolves the new conversation's id (via
         *   `/conversation/last`) and pins it here so the rest of the session stays one
         *   conversation instead of spawning a new one per message.
         *
         * Only used by the built-in `send` default; a custom `send` owns its own
         * targeting.
         */
        this.conversationId = null;
        // --- @state (internal) ---
        this._inputValue = '';
        this._isStreaming = false;
        this._error = '';
        /**
         * The current conversation's title. In the floating widget it replaces the
         * static header label so the user sees which conversation they're in. Set from
         * the loaded/switched conversation and from the live `CONVERSATION_TITLE` event
         * (the server generates a title at the end of a new conversation's first turn);
         * empty for a brand-new conversation, where the header falls back to `label`.
         */
        this._conversationTitle = '';
        /**
         * User-facing context warning text (`CONVERSATION_TOKEN_WARNING`). Shown as a
         * dismissible banner in all variants. Carries only friendly guidance, no numbers.
         */
        this._contextWarning = '';
        /**
         * True once the conversation hit the context-window limit
         * (`CONVERSATION_LIMIT_REACHED`, or `contextExceeded` on load). Drives a
         * blocking "start a new conversation" notice. User-facing, all variants.
         */
        this._contextLimitReached = false;
        /** Server-provided limit text (`CONVERSATION_LIMIT_REACHED`), if any. */
        this._contextLimitMessage = '';
        /**
         * The guarded tool awaiting the user's decision, from the turn's
         * `ACTION_CONFIRMATION_REQUIRED` event (`data.actions[]`, of which we handle the
         * first — the framework confirms one guarded tool at a time). Non-empty means
         * the conversation is paused on a confirmation: the normal composer is blocked
         * (a new chat message would be rejected by the server with
         * `awaitingConfirmation`) and the approve/deny prompt is shown instead. Cleared
         * once a decision is sent.
         *
         * This is the one case where `finished:true` unlocks the turn lock but does
         * NOT free the composer — the only valid next call is a confirmation decision.
         */
        this._pendingActions = [];
        /**
         * Keyboard-highlighted confirmation option, as an index into the decision list
         * (0 = Yes, 1 = Always allow, 2 = No). Yes is highlighted by default so Enter
         * approves. Arrow Left/Right move it; Enter activates it. -1 when no batch is
         * pending.
         */
        this._confirmationHighlighted = -1;
        /**
         * True while a confirmation decision (approve/deny) is being sent and its
         * follow-up turn streams. Disables the approve/deny buttons so the batch can't
         * be resolved twice.
         */
        this._confirmationInFlight = false;
        /**
         * INTERNAL-ONLY latest telemetry snapshot (last-value-wins). Populated from
         * `TOKEN_USAGE` events during streaming and hydrated from the conversation
         * DTO on load. Only surfaced in the `inline` variant's debug panel.
         */
        this._telemetry = null;
        /**
         * INTERNAL-ONLY per-iteration telemetry log for the debug panel's verbose
         * table. Append-only within a turn; reset at the start of each send. Each row
         * may be augmented with its `CACHE_CHECKPOINT` info.
         */
        this._iterationLog = [];
        /** Latest cache-checkpoint info keyed by iteration, for the verbose table. */
        this._cacheCheckpoints = {};
        /**
         * Whether the side drawer is open (slid in). The drawer stays mounted and
         * animates via a CSS transform, so this drives the slide and the content push.
         */
        this._drawerOpen = false;
        /**
         * Which view the drawer shows — settings, debug, history, or a single tool
         * call's full detail. One drawer holds them all; header buttons switch
         * settings/debug/history and clicking a tool line switches to `toolDetail`. The
         * content/width swap inside. Never null, so the content stays visible while the
         * drawer slides shut. `history` is floating-only (the previous-conversations
         * picker); in floating the drawer fills the panel over the transcript.
         */
        this._drawerView = 'settings';
        /**
         * The user's previous conversations, loaded from `/conversation/list` when the
         * history view opens. Each is the raw ConversationViewDto (we read conversationID,
         * title, lastModified). Empty until the first open; reloaded on each open so a
         * freshly-created or renamed conversation shows.
         */
        this._conversations = [];
        /** True while the history list is being fetched (drives a loading state). */
        this._conversationsLoading = false;
        /**
         * The tool call whose full input/output the `toolDetail` drawer view shows —
         * the clicked `tool_use` message and its paired `tool_result` message, stored
         * whole. The render reads `.data.*` off them (and formats for display) so we
         * don't duplicate the message shape here. Transient — set on click, never
         * persisted (its bytes can be large and the view is a peek). Null when none.
         */
        this._toolDetail = null;
        /** Which tool-detail block ('input' | 'output') just flashed "Copied" (null = none). */
        this._toolDetailCopied = null;
        /** Whether the tool-detail Input section is expanded. Both open on open. */
        this._toolDetailInputOpen = true;
        /** Whether the tool-detail Output section is expanded. */
        this._toolDetailOutputOpen = true;
        /** Current width of the drawer in px (user-resizable, persisted). */
        this._drawerWidth = 430;
        /** True while the user is dragging the drawer's resize handle. */
        this._isDrawerResizing = false;
        /** Files staged in the composer, before the message is sent. */
        this._pendingConversationFiles = [];
        /** True while a file is being dragged over the panel (shows the drop overlay). */
        this._isDraggingFile = false;
        /** Whether the `/` skill menu is open above the composer. */
        this._skillMenuOpened = false;
        /** Current `/` query (text after the slash), sent to `_searchOrgSkills`. */
        this._skillQuery = '';
        /**
         * Latest org skill search results (this instance's own skills). Backs the
         * browse dialog's "Your Org" tab only — global skills never appear here.
         */
        this._orgSkills = [];
        /**
         * Latest shared-Kodaris (global) skill results, for the browse dialog's
         * "Kodaris" tab. Kept separate from `_orgSkills` so switching tabs shows
         * fresh results immediately instead of the other tab's stale list — the browse
         * search fills both buffers at once.
         */
        this._globalSkills = [];
        /**
         * Latest results for the `/` composer menu — everything the current user can
         * actually invoke, merged and role-filtered server-side by
         * `/conversation/availableSkills`. A separate buffer from the two browse-tab lists
         * so installed globals in the menu never leak into the "Your Org" tab.
         */
        this._invocableSkills = [];
        /**
         * Context-aware action chips for the empty state, from `/conversation/suggestedActions`.
         * Loaded fresh every time the panel opens on an empty conversation (see `_loadSuggestedActions`);
         * cleared when the first message is sent so they don't linger over a live conversation.
         */
        this._suggestedActions = [];
        /**
         * AI framework UI config flags fetched from `aiFrameworkConfig` on connect.
         * `debugTelemetryEnabled` (same server-side gate as TOKEN_USAGE emission) turns
         * on the debug drawer toggle and the `/test-context-warning` / `/test-context-limit` composer
         * options — known up front, before any message.
         */
        this._aiFrameworkConfig = {};
        /** Highlighted item in the skill menu for keyboard navigation (-1 = none). */
        this._skillHighlighted = 0;
        /** Index of the message whose copy button just flashed "Copied" (-1 = none). */
        this._copiedIndex = -1;
        /** Whether the "Browse all skills" dialog is open. */
        this._skillBrowseOpened = false;
        /**
         * The skill whose detail view is showing inside the browse dialog, else null.
         * While set, the browse dialog shows that skill's summary, author, price, roles
         * and actions in place of the search field and tabs.
         */
        this._skillBrowseDetail = null;
        /**
         * The billable global skill awaiting the user's install confirmation, else null.
         * Set when Install is clicked on a billable skill; the confirm dialog renders
         * while it is set and the request only fires once the user confirms.
         */
        this._skillInstallConfirm = null;
        /**
         * The transcript offer (an offer_skill tool_result) with paid skills awaiting the user's
         * install confirmation, else null. An offer IS that tool call: its result string is the offer
         * JSON (`{skills, canInstall, note}`) and, once the user acts, its row gains
         * `skillOfferOutcome` (`installed` or `declined`), which the server stores so a reload shows
         * the same outcome.
         */
        this._skillOfferConfirm = null;
        /**
         * Active tab in the browse dialog. `org` shows `_orgSkills` (this instance's
         * skills); `kodaris` shows `_globalSkills` (the shared global catalog).
         */
        this._skillBrowseSource = 'org';
        /** Search text in the browse dialog, routed to the active tab's search. */
        this._skillBrowseQuery = '';
        /** True while the mic is live and speech is being transcribed into the composer. */
        this._isDictating = false;
        /**
         * Conversation posture for guarded tools (the composer's mode selector). Sent on
         * each turn's `mode` field and kept in sync with the server's `CONVERSATION_STATE`
         * event. Defaults to NORMAL (ask before each guarded tool).
         */
        this._mode = 'NORMAL';
        /** Whether the composer's mode menu (Ask / Auto / Plan) is open. */
        this._modeMenuOpened = false;
        /**
         * Whether to show the tools the assistant ran, as activity lines in the
         * transcript. On by default; a user can hide them from the settings panel.
         * User-controlled in the settings panel; persisted in localStorage.
         */
        this._showTools = true;
        /**
         * Whether to also show each tool's inputs and results under its line. Only
         * has an effect when `_showTools` is on. Off by default.
         */
        this._showToolInputs = false;
        /**
         * Whether to show the raw tool input inside the confirmation card (the JSON the
         * model proposed for a guarded action). The plain-language ask + Yes/No always
         * show; this only reveals the technical detail. Off by default.
         */
        this._showConfirmationInput = false;
        /**
         * Close the `/` skill menu when the click lands outside it.
         *
         * Bound at the document because a click anywhere — including outside this
         * component entirely — should dismiss the menu. `composedPath()` rather than
         * `e.target` since the menu lives in this component's shadow root, where
         * `target` on a document listener is retargeted to the host element and every
         * inside click would look like an outside one.
         */
        this._handleDocumentClick = (e) => {
            // Close the mode menu on any click outside the mode selector (the trigger
            // owns its own toggle, so exclude the whole `.mode` block).
            if (this._modeMenuOpened) {
                const modePath = e.composedPath();
                if (!modePath.some((el) => el instanceof HTMLElement && el.classList.contains('mode'))) {
                    this._modeMenuOpened = false;
                }
            }
            if (!this._skillMenuOpened) {
                return;
            }
            // Excluded: the textarea, since clicking it keeps editing the same `/` command
            // and dismissing there would fight the user still typing it; and the plus
            // button, which owns the menu's open/close itself. The plus exclusion matters
            // in both directions — this listener runs after the button's own handler, so
            // without it a click that just opened the menu would immediately close it
            // again, and a click meant to toggle it shut would be double-handled.
            const path = e.composedPath();
            if (path.some((el) => el instanceof HTMLElement && (el.classList.contains('skill-menu') || el.classList.contains('input') || el.classList.contains('input-plus')))) {
                return;
            }
            this._skillMenuOpened = false;
        };
        this._handleMouseMove = (e) => {
            if (this._isDragging) {
                let newLeft = this._dragStartLeft + (e.clientX - this._dragStartX);
                let newTop = this._dragStartTop + (e.clientY - this._dragStartY);
                const w = this._panelEl.offsetWidth;
                newLeft = Math.max(-w + 48, Math.min(newLeft, window.innerWidth - 48));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - 48));
                this._panelEl.style.left = newLeft + 'px';
                this._panelEl.style.top = newTop + 'px';
            }
            else if (this._isResizing) {
                const dx = e.clientX - this._dragStartX;
                const dy = e.clientY - this._dragStartY;
                const minW = 340;
                const minH = 380;
                const maxW = window.innerWidth * 0.9;
                const maxH = window.innerHeight - 72;
                let newW = this._resizeStartW;
                let newH = this._resizeStartH;
                let newLeft = this._dragStartLeft;
                let newTop = this._dragStartTop;
                if (this._resizeDir.includes('e')) {
                    newW = Math.max(minW, Math.min(this._resizeStartW + dx, maxW));
                }
                if (this._resizeDir.includes('w')) {
                    newW = Math.max(minW, Math.min(this._resizeStartW - dx, maxW));
                    newLeft = this._dragStartLeft + (this._resizeStartW - newW);
                }
                if (this._resizeDir.includes('s')) {
                    newH = Math.max(minH, Math.min(this._resizeStartH + dy, maxH));
                }
                if (this._resizeDir.includes('n')) {
                    newH = Math.max(minH, Math.min(this._resizeStartH - dy, maxH));
                    newTop = this._dragStartTop + (this._resizeStartH - newH);
                }
                newLeft = Math.max(0, newLeft);
                newTop = Math.max(0, newTop);
                this._panelEl.style.width = newW + 'px';
                this._panelEl.style.height = newH + 'px';
                this._panelEl.style.left = newLeft + 'px';
                this._panelEl.style.top = newTop + 'px';
            }
        };
        this._handleMouseUp = () => {
            if (this._isDragging) {
                this._isDragging = false;
                this._panelEl.classList.remove('panel--dragging');
            }
            if (this._isResizing) {
                this._isResizing = false;
                this._panelEl.classList.remove('panel--resizing');
            }
            if (this._overlay) {
                this._overlay.style.display = 'none';
            }
            document.removeEventListener('mousemove', this._handleMouseMove);
            document.removeEventListener('mouseup', this._handleMouseUp);
            this._saveState();
        };
        /**
         * Keyboard control for the confirmation prompt. Bound at the document so it
         * works even though the composer (where focus usually sits) is blocked while a
         * batch is pending: Left/Right move the highlight across Yes / Always allow /
         * No, Enter activates the highlighted one. No-op when nothing is pending or a
         * decision is already in flight.
         */
        this._handleDocumentKeydown = (e) => {
            if (!this._pendingActions.length || this._confirmationInFlight) {
                return;
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                this._confirmationHighlighted = (this._confirmationHighlighted + 1) % CONFIRMATION_DECISIONS.length;
                return;
            }
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                this._confirmationHighlighted =
                    (this._confirmationHighlighted - 1 + CONFIRMATION_DECISIONS.length) % CONFIRMATION_DECISIONS.length;
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                const option = CONFIRMATION_DECISIONS[this._confirmationHighlighted];
                if (option) {
                    this._handleConfirmationDecision(option.decision);
                }
            }
        };
        this._handleDrawerResizeMouseMove = (e) => {
            if (!this._isDrawerResizing) {
                return;
            }
            // Handle is on the drawer's left edge; dragging left (negative dx) widens it.
            const dx = e.clientX - this._drawerResizeStartX;
            const minW = 300;
            // Never let the chat column shrink below 300px — cap the drawer at the
            // container width minus that reserved chat width.
            const minChatWidth = 300;
            const maxW = Math.max(minW, this.offsetWidth - minChatWidth);
            this._drawerWidth = Math.max(minW, Math.min(this._drawerResizeStartW - dx, maxW));
        };
        this._handleDrawerResizeMouseUp = () => {
            this._isDrawerResizing = false;
            document.removeEventListener('mousemove', this._handleDrawerResizeMouseMove);
            document.removeEventListener('mouseup', this._handleDrawerResizeMouseUp);
            this._saveState();
        };
    }
    // --- Lifecycle ---
    connectedCallback() {
        super.connectedCallback();
        // Default to loading a conversation's messages when the consumer hasn't
        // provided their own `load`. With `conversationId` set we rebuild that
        // conversation; with none set the user wants a NEW conversation, so we start
        // blank (no resume — the first message creates one via `send`). Rows already
        // match KRChatbotMessage ({ type, content }); we only parse each row's
        // `conversationFileIDs` (a JSON array string of ids) into `conversationFiles`.
        if (!this.load) {
            this.load = () => {
                if (this.conversationId == null) {
                    return Promise.resolve([]);
                }
                return KRHttp.fetch({ url: `/api/system/conversation/${this.conversationId}/messages` })
                    .then((r) => r.json())
                    .then((res) => Promise.all((res?.data || []).map((msg) => {
                    // Persisted tool_use / tool_result rows — the message IS the row: keep
                    // the type and parse content into `data`, the same {type, data} shape
                    // the live stream pushes. The render reads data.* and pairs a result to
                    // its use by data.toolUseId. No transform, so live and reload match.
                    if (msg.type === 'tool_use' || msg.type === 'tool_result') {
                        let data = {};
                        try {
                            data = JSON.parse(msg.content || '{}');
                        }
                        catch { /* malformed — render a bare line from what we have */ }
                        return Promise.resolve({
                            type: msg.type,
                            content: '',
                            data,
                        });
                    }
                    // Persisted reasoning row — the DB stores the model's thinking as its own
                    // row with the text in `content` and type "reasoning". Live, reasoning
                    // instead arrives as REASONING_PART deltas accumulated onto an assistant
                    // bubble's `reasoning` property, which is the ONLY shape the render shows
                    // (via msg.reasoning). Map the DB row into that same shape — an assistant
                    // bubble carrying the text in `reasoning` — so reloaded/reconciled
                    // reasoning renders identically to live instead of vanishing.
                    if (msg.type === 'reasoning') {
                        return Promise.resolve({
                            type: 'assistant',
                            content: '',
                            reasoning: msg.content || '',
                        });
                    }
                    // A persisted approve/deny record — content is {outcome, tools:[...]}.
                    // Keep the type, parse content into `data`; the render reads msg.data.*.
                    if (msg.type === 'action_decision') {
                        let data = {};
                        try {
                            data = JSON.parse(msg.content || '{}');
                        }
                        catch { /* malformed — render a bare line from what we have */ }
                        return Promise.resolve({
                            type: 'action_decision',
                            content: '',
                            data,
                        });
                    }
                    // Resolve this message's `conversationFileIDs` (a JSON array string of
                    // ids) into full file objects — each fetch response IS the
                    // KRChatbotConversationFile (id + niceName + mimeType), and niceName
                    // drives the previewer's file-type detection. A failed fetch drops
                    // that file rather than rendering a broken thumbnail.
                    let ids = [];
                    try {
                        ids = JSON.parse(msg.conversationFileIDs || '[]');
                    }
                    catch { /* no/invalid attachments — leave empty */ }
                    if (!ids.length) {
                        return Promise.resolve(msg);
                    }
                    return Promise.all(ids.map((id) => KRHttp.fetch({ url: `/api/system/conversationFile/${id}` })
                        .then((r) => r.json())
                        .then((meta) => meta?.data)
                        .catch(() => null)))
                        .then((files) => {
                        msg.conversationFiles = files.filter((f) => f != null);
                        return msg;
                    });
                })));
            };
        }
        // Tab-scoped resume (floating only): a reload should redisplay the exact
        // conversation this tab was on. The pinned id lives in sessionStorage (per-tab —
        // unlike localStorage it isn't shared across tabs, so two tabs can sit on
        // different conversations). A brand-new tab has nothing pinned and starts blank:
        // the user is likely opening the chat to ask about something new, and the
        // history drawer gets them back to earlier conversations. We resolve the id
        // FIRST, then run the built-in load so it rebuilds the right transcript. Skipped
        // when the consumer owns targeting (an explicit `conversation-id`) or in inline
        // mode (embedded as a page — the host route, not the tab, decides the
        // conversation).
        let resolveConversationId = Promise.resolve();
        if (this.variant === 'floating' && this.conversationId == null) {
            const stored = Number(sessionStorage.getItem('kr-chatbot-conversation-id'));
            if (stored > 0) {
                this.conversationId = stored;
                // Fetch the conversation so the header shows its title on reload (the
                // transcript load doesn't carry it). Best-effort — the header falls back
                // to the default label if this fails.
                resolveConversationId = KRHttp.fetch({ url: `/api/system/conversation/${stored}` })
                    .then((r) => r.json())
                    .then((res) => {
                    this._conversationTitle = res?.data?.title || '';
                })
                    .catch(() => { });
            }
        }
        // `this.load` is always set by here (defaulted above when the consumer didn't
        // supply one); the `!` is because TS can't narrow the nullable property across
        // the promise boundary.
        resolveConversationId.then(() => this.load()).then((messages) => {
            if (messages && messages.length) {
                this.messages = messages;
            }
            // A turn may still be running server-side for this conversation (the page was
            // reloaded mid-turn, or opened on another device while a turn streams). After
            // the transcript loads, try to attach to that live turn: _reconnectStream hits
            // the read-only attach endpoint — 200 resumes streaming (locks the composer +
            // shows the typing indicator so the user knows the AI is still working), 204
            // means nothing is running (stays idle). Only meaningful with a pinned
            // conversation and the built-in stream (a custom `send` owns its own turns).
            if (this.conversationId && !this._isStreaming) {
                this._reconnectStream();
            }
        }).catch(() => { });
        // Fetch the framework config on load (debugTelemetryEnabled, v2ChatEnabled,
        // registryServer). debugTelemetryEnabled gates the debug affordances and is
        // known before any message.
        KRHttp.fetch({
            url: '/api/system/integration/aws/bedrock/aiFrameworkConfig',
        })
            .then((r) => r.json())
            .then((res) => {
            this._aiFrameworkConfig = res?.data || {};
        })
            .catch(() => { });
        // Inline mode fills its container and is always open — the floating widget's
        // toggle/drag/resize state doesn't apply, but the composer message and debug
        // drawer state (restored inside _restoreState) do.
        if (this.variant === 'inline') {
            this.opened = true;
        }
        this._restoreState();
        document.addEventListener('click', this._handleDocumentClick);
        // Drive the confirmation prompt from the keyboard even when focus isn't in the
        // (blocked) composer: Left/Right move the highlight, Enter activates it.
        document.addEventListener('keydown', this._handleDocumentKeydown);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._reader) {
            this._reader.cancel();
            this._reader = null;
        }
        document.removeEventListener('mousemove', this._handleMouseMove);
        document.removeEventListener('mouseup', this._handleMouseUp);
        document.removeEventListener('click', this._handleDocumentClick);
        document.removeEventListener('keydown', this._handleDocumentKeydown);
        // Release the mic if the chatbot is torn down mid-dictation — otherwise the
        // recognizer keeps the browser's recording indicator lit with nothing to
        // transcribe into. abort() (not stop()) since the discarded text has nowhere to go.
        if (this._speechRecognition) {
            this._speechRecognition.abort();
            this._speechRecognition = null;
            this._isDictating = false;
        }
        // Release any object URLs still held by staged attachments.
        this._pendingConversationFiles.forEach((p) => {
            if (p.previewUrl) {
                URL.revokeObjectURL(p.previewUrl);
            }
        });
    }
    updated(changed) {
        if (changed.has('messages')) {
            if (!this._userHasScrolledUp && !this._isStreaming && this._messagesEl) {
                requestAnimationFrame(() => {
                    if (this._messagesEl) {
                        this._messagesEl.scrollTop = this._messagesEl.scrollHeight;
                    }
                });
            }
        }
        // Streamed chunks mutate the message in place and call requestUpdate(), so
        // `changed` never has 'messages' mid-turn — run the follow on every update
        // instead. The user message stays pinned at the top (Gemini-style) until the
        // response grows past the bottom of the view; from then on the end stays in
        // view. Never scrolls to the empty runway below the content.
        if (this._isStreaming && this._followStream && this._messagesEl) {
            requestAnimationFrame(() => {
                const container = this._messagesEl;
                if (!container || !this._isStreaming || !this._followStream) {
                    return;
                }
                const turnEls = container.querySelectorAll('.agent-turn');
                const turnEl = turnEls[turnEls.length - 1];
                const lastEl = turnEl?.lastElementChild;
                if (!lastEl) {
                    return;
                }
                // Bottom edge of the turn's last rendered child, in the container's
                // scroll coordinates — the runway (min-height on the turn) sits below it.
                const contentBottom = lastEl.getBoundingClientRect().bottom
                    - container.getBoundingClientRect().top
                    + container.scrollTop
                    + 24;
                const visibleBottom = container.scrollTop + container.clientHeight;
                if (contentBottom > visibleBottom) {
                    container.scrollTop = Math.min(contentBottom - container.clientHeight, container.scrollHeight - container.clientHeight);
                }
            });
        }
        if (changed.has('opened') && this.opened && this._inputEl) {
            setTimeout(() => {
                if (this._inputEl) {
                    this._inputEl.focus();
                }
            }, 200);
        }
        // Load context-aware action chips every time the panel opens on an empty conversation. Fires for
        // the floating variant on toggle-open and for the inline variant on its first render (which sets
        // `opened` true at connect). Only when empty — a resumed conversation shows its transcript, not chips.
        // The request-id guard inside handles a stale reply if the user closes/reopens quickly.
        if (changed.has('opened') && this.opened && this.messages.length === 0) {
            this._loadSuggestedActions();
        }
        // Position the fixed skill menu above the composer, following the krubble
        // select-field pattern (fixed element positioned from the trigger's rect).
        if (this._skillMenuOpened) {
            this._positionSkillMenu();
        }
        // Keep the keyboard-highlighted skill scrolled into view.
        if (changed.has('_skillHighlighted') && this._skillMenuOpened) {
            this._skillMenuEl
                ?.querySelector('.skill-menu__item--highlighted')
                ?.scrollIntoView({ block: 'nearest' });
        }
    }
    /** Anchor the fixed skill menu to the composer, opening upward above it. */
    _positionSkillMenu() {
        requestAnimationFrame(() => {
            if (!this._skillMenuEl || !this._inputWrapperEl) {
                return;
            }
            const inputRect = this._inputWrapperEl.getBoundingClientRect();
            this._skillMenuEl.style.left = inputRect.left + 'px';
            this._skillMenuEl.style.width = inputRect.width + 'px';
            // Bound the menu by the chat panel's own top edge so it stays within the
            // widget. In floating mode that keeps it inside the card; in inline mode the
            // panel top sits just below the app header/breadcrumb, so it stays clear of
            // those too. An 8px gap keeps it off both edges.
            const topLimit = this._panelEl.getBoundingClientRect().top + 8;
            const spaceAbove = inputRect.top - 8 - topLimit;
            this._skillMenuEl.style.maxHeight = spaceAbove + 'px';
            // Measure after the cap is applied so the upward anchor uses the real
            // height, then clamp the top edge to the panel bound.
            const menuRect = this._skillMenuEl.getBoundingClientRect();
            const top = Math.max(topLimit, inputRect.top - menuRect.height - 8);
            this._skillMenuEl.style.top = top + 'px';
        });
    }
    // --- Public methods ---
    /**
     * Stops the current stream without clearing the conversation.
     */
    stop() {
        this._handleStop();
    }
    /** Whether a turn is in flight — true from send until the stream ends. */
    isStreaming() {
        return this._isStreaming;
    }
    /** Open the chat panel. */
    open() {
        if (!this.opened) {
            this._handleToggle();
        }
    }
    /** Close the chat panel. */
    close() {
        if (this.opened) {
            this._handleToggle();
        }
    }
    /** Toggle the chat panel open/closed. */
    toggle() {
        this._handleToggle();
    }
    /**
     * Open the panel and send `payload.message` as if the user had typed it and pressed Enter.
     *
     * Lets a host app hand the assistant a predefined prompt (e.g. a "Migrate" button that
     * starts a skill for the current record). Goes through `_handleSubmit`, not `_send`, so
     * the streaming and pending-confirmation guards and the cancelable `message-submit`
     * event all apply exactly as they do for a typed message. Nothing is sent while a turn
     * is in flight; the message is dropped rather than queued.
     */
    prompt(payload) {
        this.open();
        this._inputValue = payload.message;
        this._handleSubmit();
    }
    /**
     * Open the panel and place `payload.message` in the composer, selected, without sending it.
     *
     * For a host app that wants to suggest an opening message rather than send one (e.g. a site
     * editor deep link): Enter sends it as is, typing replaces it. Nothing is persisted beyond the
     * composer draft state any typed text gets.
     */
    draft(payload) {
        this.open();
        this._inputValue = payload.message;
        this._saveState();
        // Same delay as the open-focus in `updated`: the panel has to finish showing before the
        // textarea can take focus, and this has to run after that focus so the selection sticks.
        this.updateComplete.then(() => {
            setTimeout(() => {
                if (this._inputEl) {
                    this._inputEl.focus();
                    this._inputEl.select();
                }
            }, 200);
        });
    }
    // --- Private methods ---
    /**
     * Shows a full-viewport transparent overlay during drag/resize operations.
     *
     * This overlay solves a critical rendering issue when the chatbot is used inside
     * a page that contains iframes (e.g., the AI editor shell where the page content
     * is rendered in an iframe and the chatbot floats on top).
     *
     * Without the overlay, modifying the iframe's `pointer-events` style during drag
     * (to prevent it from swallowing mousemove events) causes Chrome to recalculate
     * compositing layers. This layer recalculation blocks the browser from repainting
     * the chatbot panel's style changes (width/height/position) even though they are
     * correctly applied to the DOM. The result is that the panel appears frozen while
     * the user drags, despite the inline styles updating on every mousemove.
     *
     * The overlay approach avoids this entirely: instead of touching the iframe's styles,
     * we place a transparent div (z-index 9999) above the iframe but below the chatbot
     * (z-index 10000). This div intercepts all mouse events that would otherwise hit the
     * iframe, without triggering any compositing layer changes on the iframe itself.
     */
    _showOverlay() {
        if (!this._overlay) {
            this._overlay = document.createElement('div');
            this._overlay.style.cssText = 'position:fixed; inset:0; z-index:9999; display:none;';
            document.body.appendChild(this._overlay);
        }
        this._overlay.style.display = 'block';
    }
    _handleHeaderMouseDown(e) {
        // Inline mode fills its parent — dragging the panel doesn't apply.
        if (this.variant === 'inline') {
            return;
        }
        // Ignore clicks on buttons inside the header
        if (e.target.closest('button')) {
            return;
        }
        if (!this._panelEl) {
            return;
        }
        this._isDragging = true;
        this._panelEl.classList.add('panel--dragging');
        this._showOverlay();
        const rect = this._panelEl.getBoundingClientRect();
        this._panelEl.style.left = rect.left + 'px';
        this._panelEl.style.top = rect.top + 'px';
        this._panelEl.style.right = 'auto';
        this._panelEl.style.bottom = 'auto';
        this._panelEl.style.position = 'fixed';
        this._dragStartX = e.clientX;
        this._dragStartY = e.clientY;
        this._dragStartLeft = rect.left;
        this._dragStartTop = rect.top;
        e.preventDefault();
        document.addEventListener('mousemove', this._handleMouseMove);
        document.addEventListener('mouseup', this._handleMouseUp);
    }
    _handleResizeMouseDown(e) {
        // Inline mode fills its parent — resizing the panel doesn't apply.
        if (this.variant === 'inline') {
            return;
        }
        if (!this._panelEl) {
            return;
        }
        this._isResizing = true;
        this._resizeDir = e.currentTarget.dataset.dir || '';
        this._panelEl.classList.add('panel--resizing');
        this._showOverlay();
        const rect = this._panelEl.getBoundingClientRect();
        this._panelEl.style.left = rect.left + 'px';
        this._panelEl.style.top = rect.top + 'px';
        this._panelEl.style.right = 'auto';
        this._panelEl.style.bottom = 'auto';
        this._panelEl.style.position = 'fixed';
        this._dragStartX = e.clientX;
        this._dragStartY = e.clientY;
        this._dragStartLeft = rect.left;
        this._dragStartTop = rect.top;
        this._resizeStartW = rect.width;
        this._resizeStartH = rect.height;
        e.preventDefault();
        e.stopPropagation();
        document.addEventListener('mousemove', this._handleMouseMove);
        document.addEventListener('mouseup', this._handleMouseUp);
    }
    _handleToggle() {
        this.opened = !this.opened;
        if (this.opened && this._panelEl) {
            this._applyStoredLayout();
        }
        this._saveState();
    }
    /**
     * Persist the pinned conversation id per-tab so a reload redisplays this exact
     * conversation. sessionStorage (not localStorage) is deliberate: it's scoped to
     * the tab, so two tabs can sit on different conversations without clobbering
     * each other. Only meaningful for the floating widget's built-in flow — inline
     * embeds and custom-`load` consumers own their own targeting.
     */
    _persistConversationId() {
        // Floating-only: inline embeds (and custom-`load` consumers) own their own
        // targeting and never resume from this key, so they must not write it either —
        // otherwise an inline chat sharing the page/origin would clobber the floating
        // widget's pinned id.
        if (this.variant !== 'floating') {
            return;
        }
        try {
            if (this.conversationId != null) {
                sessionStorage.setItem('kr-chatbot-conversation-id', String(this.conversationId));
            }
            else {
                sessionStorage.removeItem('kr-chatbot-conversation-id');
            }
        }
        catch { /* sessionStorage not available */ }
    }
    /**
     * Two stores, split by lifetime:
     *
     * - Preferences (composer draft, mode, tool toggles) go to localStorage so they
     *   follow the user across tabs.
     * - Per-tab state goes to sessionStorage: the debug drawer (open flag, view,
     *   width) and, for the floating widget only, the panel's open flag and its
     *   dragged/resized layout. A reload brings the tab back exactly as it was, but a
     *   brand-new tab starts with the panel closed in the default spot and the drawer
     *   shut. Inline embeds are always open and fill their container, so they never
     *   write the panel fields.
     *
     * Both stores use separate keys for the floating widget and inline embeds so
     * neither clobbers the other.
     */
    _saveState() {
        let storageKey = 'kr-chatbot-state';
        let tabKey = 'kr-chatbot-tab';
        if (this.variant === 'inline') {
            storageKey = 'kr-chatbot-state-inline';
            tabKey = 'kr-chatbot-tab-inline';
        }
        try {
            const state = {
                message: this._inputValue,
                mode: this._mode,
                showTools: String(this._showTools),
                showToolInputs: String(this._showToolInputs),
                showConfirmationInput: String(this._showConfirmationInput),
            };
            localStorage.setItem(storageKey, JSON.stringify(state));
        }
        catch { /* localStorage not available */ }
        try {
            // `toolDetail` is a transient peek, not a saved view — persist only the two
            // real preference views so a reload never reopens a stale tool.
            let persistedDrawerView = this._drawerView;
            if (persistedDrawerView === 'toolDetail') {
                persistedDrawerView = 'settings';
            }
            const tab = {
                drawerOpen: String(this._drawerOpen),
                drawerView: persistedDrawerView,
                drawerWidth: String(this._drawerWidth),
            };
            if (this.variant === 'floating') {
                tab.opened = String(this.opened);
                if (this._panelEl) {
                    const s = this._panelEl.style;
                    if (s.left) {
                        tab.left = s.left;
                    }
                    if (s.top) {
                        tab.top = s.top;
                    }
                    if (s.width) {
                        tab.width = s.width;
                    }
                    if (s.height) {
                        tab.height = s.height;
                    }
                    if (s.position) {
                        tab.position = s.position;
                    }
                }
            }
            sessionStorage.setItem(tabKey, JSON.stringify(tab));
        }
        catch { /* sessionStorage not available */ }
    }
    _restoreState() {
        let storageKey = 'kr-chatbot-state';
        let tabKey = 'kr-chatbot-tab';
        if (this.variant === 'inline') {
            storageKey = 'kr-chatbot-state-inline';
            tabKey = 'kr-chatbot-tab-inline';
        }
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const state = JSON.parse(raw);
                // A lone `/` is dropped — it's the in-progress half of a menu interaction,
                // not a draft, and restoring it would revive a stale menu the user never
                // used. Anything with an actual command after the slash (`/code`, `/code do
                // the thing`) is a real draft and restores normally. Trimmed before testing
                // so a stray space around the slash doesn't defeat the check.
                if (typeof state.message === 'string' && state.message.trim() !== '/') {
                    this._inputValue = state.message;
                }
                if (state.mode === 'NORMAL' || state.mode === 'EDIT' || state.mode === 'PLAN') {
                    this._mode = state.mode;
                }
                this._showTools = state.showTools === 'true';
                this._showToolInputs = state.showToolInputs === 'true';
                this._showConfirmationInput = state.showConfirmationInput === 'true';
            }
        }
        catch { /* localStorage not available */ }
        try {
            const raw = sessionStorage.getItem(tabKey);
            if (!raw) {
                return;
            }
            const tab = JSON.parse(raw);
            this._drawerOpen = tab.drawerOpen === 'true';
            if (tab.drawerView === 'settings' || tab.drawerView === 'debug' || tab.drawerView === 'history') {
                this._drawerView = tab.drawerView;
                // History's list isn't persisted (it can go stale); refetch it so a reload
                // that was on the conversations panel comes back to a populated list rather
                // than an empty one.
                if (tab.drawerView === 'history') {
                    this._loadConversations();
                }
            }
            const w = Number(tab.drawerWidth);
            if (w >= 300) {
                this._drawerWidth = w;
            }
            // The panel's open flag and layout only apply to the floating widget — inline
            // mode is always open.
            if (this.variant === 'floating' && tab.opened === 'true') {
                this.opened = true;
                // Apply layout after first render
                this.updateComplete.then(() => this._applyStoredLayout());
            }
        }
        catch { /* sessionStorage not available */ }
    }
    /** Floating only — inline embeds never store a layout and never reach this. */
    _applyStoredLayout() {
        try {
            const raw = sessionStorage.getItem('kr-chatbot-tab');
            if (!raw || !this._panelEl) {
                return;
            }
            const tab = JSON.parse(raw);
            if (tab.position) {
                this._panelEl.style.position = tab.position;
            }
            if (tab.left) {
                this._panelEl.style.left = tab.left;
            }
            if (tab.top) {
                this._panelEl.style.top = tab.top;
            }
            if (tab.width) {
                this._panelEl.style.width = tab.width;
            }
            if (tab.height) {
                this._panelEl.style.height = tab.height;
            }
        }
        catch { /* sessionStorage not available */ }
    }
    _handleInputChange(e) {
        this._inputValue = e.target.value;
        this._saveState();
        // Auto-resize textarea
        const textarea = e.target;
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
        // Open the skill menu while the message is a `/` command being typed (a
        // leading slash with no space yet). The text after the slash is the search
        // query, run server-side via `_searchInvocableSkills`.
        // (org skills + installed globals — everything invocable by /code)
        const slashMatch = this._inputValue.match(/^\/(\S*)$/);
        if (slashMatch) {
            this._skillMenuOpened = true;
            this._skillQuery = slashMatch[1];
            this._skillHighlighted = 0;
            this._searchInvocableSkills(this._skillQuery);
        }
        else {
            this._skillMenuOpened = false;
        }
    }
    _handleKeyDown(e) {
        // When the skill menu is open, arrow keys / Enter / Escape drive it. The top
        // actions (Attach Files, Attach Files for Reference, Browse All Skills, plus
        // the two debug test triggers when enabled) come first, then the filtered
        // skills — all one navigable list.
        if (this._skillMenuOpened) {
            const debugActions = this.variant === 'inline' && this._aiFrameworkConfig.debugTelemetryEnabled === true;
            let actionCount = 3;
            if (debugActions) {
                actionCount = 5;
            }
            const count = actionCount + this._invocableSkills.length;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                this._skillHighlighted = (this._skillHighlighted + 1) % count;
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                this._skillHighlighted = (this._skillHighlighted - 1 + count) % count;
                return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this._activateSkillMenuItem(this._skillHighlighted);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                this._skillMenuOpened = false;
                return;
            }
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this._handleSubmit();
        }
    }
    /** Run the skill-menu item at the given flat index (actions first, then skills). */
    _activateSkillMenuItem(index) {
        const debugActions = this.variant === 'inline' && this._aiFrameworkConfig.debugTelemetryEnabled === true;
        let actionCount = 3;
        if (debugActions) {
            actionCount = 5;
        }
        if (index === 0) {
            this._openFilePicker(true);
            return;
        }
        if (index === 1) {
            this._openFilePicker(false);
            return;
        }
        if (index === 2) {
            this._handleSkillBrowseOpen();
            return;
        }
        if (debugActions && index === 3) {
            this._skillMenuOpened = false;
            this._send({
                message: '/test-context-warning',
                conversationFiles: [],
            });
            return;
        }
        if (debugActions && index === 4) {
            this._skillMenuOpened = false;
            this._send({
                message: '/test-context-limit',
                conversationFiles: [],
            });
            return;
        }
        const skill = this._invocableSkills[index - actionCount];
        if (skill) {
            this._handleSkillSelect(skill);
        }
    }
    // --- Skills ---
    /**
     * Load the browse dialog's "Your Org" tab into `_orgSkills` from
     * `/conversation/localSkillList` — this instance's own active skills, already
     * role-filtered to what the caller may run and ranked by the server.
     *
     * Not the `aiskills` search endpoint: that requires a skill-authoring role
     * (`skillView`/`skillEdit`), so an ordinary chat user got a 403 and an empty
     * tab. This one only needs a logged-in employee. It also means the query
     * matching, active-only filter and "exclude globally-published" rule all live
     * server-side instead of being rebuilt as OpenSearch DSL here.
     *
     * EXCEPTION — on kodaris.com this list also carries that instance's own
     * globally-published skills (flagged `global: true`), because there is no
     * Kodaris tab there for them to appear on.
     *
     * Request-id guard drops responses that arrive out of order (kr-combo-box
     * `_fetch` pattern).
     */
    _searchOrgSkills(query) {
        this._orgSkillRequestId++;
        const requestId = this._orgSkillRequestId;
        KRHttp.fetch({
            url: '/api/system/conversation/localSkillList',
            method: 'POST',
            body: { query: query.trim() },
        })
            .then((r) => r.json())
            .then((res) => {
            // Ignore a stale response superseded by a newer search.
            if (requestId !== this._orgSkillRequestId) {
                return;
            }
            this._orgSkills = (res?.data || []);
        })
            .catch((error) => {
            console.error('kr-chatbot: skill search failed', error);
        });
    }
    /**
     * Load the skills for the `/` composer menu into `_invocableSkills` from
     * `/conversation/availableSkills` — this instance's own skills plus the installed
     * global ones, already merged, role-filtered and ranked by the server. That's the
     * same set the model sees via `list_skills`, so the menu can't offer a skill the
     * chat would then refuse.
     *
     * Not the `aiskills` endpoints the browse dialog's Org tab uses: those require a
     * skill-authoring role, which an ordinary chat user doesn't have.
     *
     * Request-id guard drops responses that arrive out of order (kr-combo-box `_fetch`
     * pattern).
     */
    _searchInvocableSkills(query) {
        this._invocableSkillRequestId++;
        const requestId = this._invocableSkillRequestId;
        KRHttp.fetch({
            url: '/api/system/conversation/availableSkills',
            method: 'POST',
            body: { query: query.trim() },
        })
            .then((r) => r.json())
            .then((res) => {
            // Ignore a stale response superseded by a newer search.
            if (requestId !== this._invocableSkillRequestId) {
                return;
            }
            this._invocableSkills = (res?.data || []);
        })
            .catch((error) => {
            console.error('kr-chatbot: invocable skill search failed', error);
        });
    }
    /**
     * Load the empty-state action chips from `/conversation/suggestedActions`, passing the user's
     * current page/selection context (captured live, same as a send). The server picks a few skills
     * the user can run that fit where they are and writes a label for each. Called every time the
     * panel opens on an empty conversation (per product decision — context can change between opens).
     *
     * AI-only, no fallback: any failure, or v2 chat being off (server returns `[]`), just leaves the
     * empty state with no chips. A request-id guard drops a response superseded by a newer open, so a
     * slow reply from a previous open can't land after the user has started typing.
     */
    _loadSuggestedActions() {
        this._suggestedActionRequestId++;
        const requestId = this._suggestedActionRequestId;
        const context = this._captureContext();
        KRHttp.fetch({
            url: '/api/system/conversation/suggestedActions',
            method: 'POST',
            body: {
                pageContext: context.pageContext,
                selectionContext: context.selectionContext,
            },
        })
            .then((r) => r.json())
            .then((res) => {
            // Ignore a stale response from a prior open, and never overwrite chips once a conversation
            // has started (the user opened, the reply was slow, they typed and sent in the meantime).
            if (requestId !== this._suggestedActionRequestId || this.messages.length > 0) {
                return;
            }
            this._suggestedActions = (res?.data || []);
        })
            .catch((error) => {
            console.error('kr-chatbot: suggested actions load failed', error);
        });
    }
    /**
     * Load the browse dialog's "Kodaris" tab into `_globalSkills` — the whole
     * published Kodaris catalog for discovery, installed or not, runnable by this
     * user or not. Each row carries `enabled` (installed here), `canEnable` (may
     * install it) and `canRun` (roles allow invoking it).
     *
     * Uses `/conversation/globalSkillList` rather than the `/aiskills/global/search`
     * twin, which requires a skill-authoring role an ordinary chat user doesn't have.
     *
     * Returns `[]` — not an error — when there's no reachable registry, and always
     * on kodaris.com, which IS the catalog source and so has no remote to pull. The
     * tab itself is hidden there (see `_renderSkillBrowse`).
     */
    _searchGlobalSkills(query) {
        this._globalSkillRequestId++;
        const requestId = this._globalSkillRequestId;
        KRHttp.fetch({
            url: '/api/system/conversation/globalSkillList',
            method: 'POST',
            body: { query: query.trim() },
        })
            .then((r) => r.json())
            .then((res) => {
            // Ignore a stale response superseded by a newer search.
            if (requestId !== this._globalSkillRequestId) {
                return;
            }
            this._globalSkills = (res?.data || []);
        })
            .catch((error) => {
            console.error('kr-chatbot: global skill search failed', error);
        });
    }
    /**
     * Enable or disable a global skill on this instance via
     * `/aiskills/global/{code}/enabled`, then reflect the server's result on the
     * matching card. Ignores clicks while a request for the same code is pending.
     * Installing a billable skill opens a confirmation instead; see
     * `_handleSkillInstallConfirm` for the request that runs once the user accepts.
     */
    _handleGlobalSkillEnable(skill, e) {
        // The card body opens the detail view; the install button must not also do that.
        e.stopPropagation();
        // The button is shown to everyone so the catalog reads the same for all users,
        // but the enable endpoint requires administrator/superuser/skillEdit. Without
        // it, say who to ask instead of firing a request that comes back 403.
        if (!skill.canEnable) {
            KRSnackbar.show({
                title: 'Permission Required',
                message: 'You do not have permission to install skills. Please contact your site administrator.',
                type: 'error',
            });
            return;
        }
        if (this._globalSkillPending.has(skill.code)) {
            return;
        }
        // Installing a billable skill charges the account, so confirm before sending.
        // Uninstalling is never billed and free installs are one click to undo.
        if (!skill.enabled && skill.billable) {
            this._skillInstallConfirm = skill;
            return;
        }
        // Drive the clicked button's own spinner (KRButton disables itself while
        // loading, so a second click can't fire until the request settles).
        const btn = e.currentTarget;
        this._globalSkillPending.add(skill.code);
        btn?.showLoading();
        KRHttp.fetch({
            url: `/api/system/opensearch/aiskills/global/${encodeURIComponent(skill.code)}/enabled`,
            method: 'POST',
            body: { enabled: !skill.enabled },
        })
            .then((r) => r.json())
            .then((res) => {
            // Reflect the server's returned flag on the card. The button label/style
            // re-render from skill.enabled; reset() clears the spinner to idle.
            skill.enabled = res?.data?.enabled;
            btn?.reset();
            this.requestUpdate();
            if (skill.enabled) {
                KRSnackbar.show({
                    title: 'Skill Installed',
                    message: `${skill.name} is now available to use.`,
                    type: 'success',
                });
            }
            else {
                KRSnackbar.show({
                    title: 'Skill Uninstalled',
                    message: `${skill.name} is no longer available to use.`,
                    type: 'success',
                });
            }
        })
            .catch((error) => {
            console.error('kr-chatbot: global skill enable failed', error);
            btn?.showError('Error');
        })
            .finally(() => {
            this._globalSkillPending.delete(skill.code);
        });
    }
    /** Cancel the billable install confirmation without sending anything. */
    _handleSkillInstallCancel() {
        this._skillInstallConfirm = null;
    }
    /**
     * The user accepted the charge: run the install with the dialog's own Install
     * button spinning, and close the dialog once the server confirms. On failure
     * the button shows the error and the dialog stays open so they can retry or cancel.
     */
    _handleSkillInstallConfirm(e) {
        if (!this._skillInstallConfirm) {
            return;
        }
        if (this._globalSkillPending.has(this._skillInstallConfirm.code)) {
            return;
        }
        const skill = this._skillInstallConfirm;
        const btn = e.currentTarget;
        this._globalSkillPending.add(skill.code);
        btn?.showLoading();
        KRHttp.fetch({
            url: `/api/system/opensearch/aiskills/global/${encodeURIComponent(skill.code)}/enabled`,
            method: 'POST',
            body: { enabled: true },
        })
            .then((r) => r.json())
            .then((res) => {
            // Reflect the server's returned flag on the card and close the confirmation.
            skill.enabled = res?.data?.enabled;
            btn?.reset();
            this._skillInstallConfirm = null;
            this.requestUpdate();
            KRSnackbar.show({
                title: 'Skill Installed',
                message: `${skill.name} is now available to use.`,
                type: 'success',
            });
        })
            .catch((error) => {
            console.error('kr-chatbot: global skill install failed', error);
            btn?.showError('Error');
        })
            .finally(() => {
            this._globalSkillPending.delete(skill.code);
        });
    }
    /**
     * Install on a transcript offer: install every skill the assistant offered, together. The
     * offer is all-or-nothing because the task needs all of them (one without the other would
     * not get it done). Same permission check and paid-skill confirmation as the Browse dialog's
     * Install button, applied to the set: without the role, say who to ask; with any paid skill
     * in the set, confirm the charges first (`_handleSkillOfferConfirm` runs the install once the
     * user accepts).
     */
    _handleOfferedSkillsInstall(msg, e) {
        const offer = JSON.parse(msg.data.toolResultString);
        if (!offer.canInstall) {
            KRSnackbar.show({
                title: 'Permission Required',
                message: 'You do not have permission to install skills. Please contact your site administrator.',
                type: 'error',
            });
            return;
        }
        if (offer.skills.some((skill) => this._globalSkillPending.has(skill.code))) {
            return;
        }
        if (offer.skills.some((skill) => skill.billable)) {
            this._skillOfferConfirm = msg;
            return;
        }
        this._installOfferedSkills(msg, e.currentTarget);
    }
    /** Cancel the offer's paid-skill confirmation without installing anything. */
    _handleSkillOfferCancel() {
        this._skillOfferConfirm = null;
    }
    /** The user accepted the charges: install the offered set with the dialog's own button spinning. */
    _handleSkillOfferConfirm(e) {
        if (!this._skillOfferConfirm) {
            return;
        }
        this._installOfferedSkills(this._skillOfferConfirm, e.currentTarget);
    }
    /**
     * Install the offered skills one after another through the same enable request the Browse
     * dialog's card uses, driving the clicked button's spinner. A skill already on (say, installed
     * from the Browse dialog meanwhile) is a no-op for the server, so none is skipped. When every
     * one is on: record the outcome on the offer's tool_result data (so this render updates at
     * once) and on its row (so a reload shows the same thing; best effort, the transcript already
     * shows it), close the paid confirmation if it was open, and tell the assistant, as the user,
     * that the skills are installed so it re-runs list_skills and carries on with the original
     * request. A failure stops the sequence: the ones already on stay on, the button shows the
     * error, and nothing is sent, so the user can retry the offer.
     */
    _installOfferedSkills(msg, btn) {
        const skills = JSON.parse(msg.data.toolResultString).skills;
        for (const skill of skills) {
            this._globalSkillPending.add(skill.code);
        }
        btn?.showLoading();
        let chain = Promise.resolve();
        for (const skill of skills) {
            chain = chain
                .then(() => KRHttp.fetch({
                url: `/api/system/opensearch/aiskills/global/${encodeURIComponent(skill.code)}/enabled`,
                method: 'POST',
                body: { enabled: true },
            }));
        }
        chain
            .then(() => {
            btn?.reset();
            this._skillOfferConfirm = null;
            msg.data.skillOfferOutcome = 'installed';
            this.requestUpdate();
            if (this.conversationId != null) {
                KRHttp.fetch({
                    url: `/api/system/conversation/${this.conversationId}/skillOffer/${encodeURIComponent(msg.data.toolUseId)}`,
                    method: 'POST',
                    body: {
                        outcome: 'installed',
                    },
                }).catch((error) => {
                    console.error('kr-chatbot: failed to record skill offer outcome', error);
                });
            }
            this._inputValue = `I installed ${skills.map((skill) => skill.name).join(' and ')}. Please continue.`;
            this._handleSubmit();
        })
            .catch((error) => {
            console.error('kr-chatbot: skill offer install failed', error);
            btn?.showError('Error');
        })
            .finally(() => {
            for (const skill of skills) {
                this._globalSkillPending.delete(skill.code);
            }
        });
    }
    /**
     * Not now on a transcript offer: record the decline (the box stays, showing Declined in
     * place of the buttons) and tell the assistant, as the user, that they declined, so it can
     * carry on without the skills instead of waiting for an install. The outcome is set on the
     * offer's tool_result data so this render updates at once, and written to that row on the
     * server so a reload shows the same thing. The write is best effort: the transcript already
     * shows the outcome, and the user message tells the assistant. The row is addressed by the
     * conversation and the tool call's own toolUseId, which every tool row already carries.
     */
    _handleOfferedSkillsDecline(msg) {
        msg.data.skillOfferOutcome = 'declined';
        this.requestUpdate();
        if (this.conversationId != null) {
            KRHttp.fetch({
                url: `/api/system/conversation/${this.conversationId}/skillOffer/${encodeURIComponent(msg.data.toolUseId)}`,
                method: 'POST',
                body: {
                    outcome: 'declined',
                },
            }).catch((error) => {
                console.error('kr-chatbot: failed to record skill offer outcome', error);
            });
        }
        this._inputValue = `I don't want to install ${JSON.parse(msg.data.toolResultString).skills.map((skill) => skill.name).join(' and ')} right now.`;
        this._handleSubmit();
    }
    /** The `+` button toggles the skill/attachment menu open (showing all skills). */
    _handlePlusClick() {
        if (this._skillMenuOpened) {
            this._skillMenuOpened = false;
            return;
        }
        this._skillQuery = '';
        this._skillHighlighted = 0;
        this._skillMenuOpened = true;
        this._searchInvocableSkills('');
    }
    _handleModeToggle() {
        this._modeMenuOpened = !this._modeMenuOpened;
    }
    _handleModeSelect(mode) {
        this._mode = mode;
        this._modeMenuOpened = false;
        // Persist the choice so it survives a reload, alongside the other composer
        // state. The server is still the source of truth on the next turn's
        // CONVERSATION_STATE, but this keeps the selector from flashing the default.
        this._saveState();
    }
    _handleShowToolsChange(e) {
        this._showTools = e.target.checked;
        this._saveState();
    }
    _handleShowToolInputsChange(e) {
        this._showToolInputs = e.target.checked;
        this._saveState();
    }
    _handleShowConfirmationInputChange(e) {
        this._showConfirmationInput = e.target.checked;
        this._saveState();
    }
    /**
     * The mic button toggles dictation. Speech is transcribed straight into the
     * composer as editable text — the user still reviews and presses send, so a
     * misheard word is fixable and nothing is sent by voice alone.
     *
     * Recognition runs entirely in the browser's Web Speech API (Chrome/Edge send
     * audio to Google's service, Safari to Apple's). No audio reaches Kodaris, and
     * there's no backend call or cost on our side.
     */
    _handleMicClick() {
        // Second click stops. `stop()` flushes the pending utterance as a final
        // result, so the last words aren't lost; onend clears the state.
        if (this._speechRecognition) {
            this._speechRecognition.stop();
            return;
        }
        // Prefixed in Chrome/Edge/Safari; the unprefixed name is checked first for
        // whenever they drop the prefix. The mic button isn't rendered without one of
        // these, so reaching here unsupported means a mid-session change — bail quietly.
        const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognitionCtor) {
            return;
        }
        const recognition = new SpeechRecognitionCtor();
        // Keep listening through natural pauses — a chat message is often several
        // sentences, and the default stops at the first one.
        recognition.continuous = true;
        // Show words as they're recognized rather than only at end of utterance.
        recognition.interimResults = true;
        // `lang` is deliberately left unset so the recognizer follows the page/OS
        // language, which is correct for every consumer of this component.
        // Dictation ADDS to whatever is already typed rather than replacing it, so
        // the user can type half a message and speak the rest. The separating space
        // is part of the base so the transcript never runs into the existing word.
        this._speechBaseValue = this._inputValue;
        if (this._speechBaseValue && !this._speechBaseValue.endsWith(' ')) {
            this._speechBaseValue += ' ';
        }
        this._speechFinalTranscript = '';
        recognition.onresult = (e) => {
            // Everything from resultIndex on is new since the last event: finalized
            // pieces move to _speechFinalTranscript permanently, while non-final ones
            // are rebuilt each event because the browser revises its guess as it hears
            // more. Rebuilding the whole value (base + final + interim) is what keeps
            // those revisions from stacking up as duplicated text.
            let interim = '';
            for (let i = e.resultIndex; i < e.results.length; i++) {
                if (e.results[i].isFinal) {
                    this._speechFinalTranscript += e.results[i][0].transcript;
                }
                else {
                    interim += e.results[i][0].transcript;
                }
            }
            this._inputValue = this._speechBaseValue + this._speechFinalTranscript + interim;
            this._saveState();
            // Grow the textarea as dictated text wraps — the same clamp the typing path
            // uses, since @input doesn't fire for programmatic value changes.
            if (this._inputEl) {
                this._inputEl.style.height = 'auto';
                this._inputEl.style.height = Math.min(this._inputEl.scrollHeight, 120) + 'px';
            }
        };
        recognition.onerror = (e) => {
            // Denied mic permission is the one error worth explaining — the user has to
            // fix it in browser settings, and the button would otherwise look broken.
            if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
                this._error = 'Microphone access is blocked. Allow it in your browser settings to use voice input.';
                return;
            }
            // `no-speech` (silence) and `aborted` (user stopped) are normal; anything
            // else is a transient service hiccup. onend still runs, so the mic resets.
        };
        // Fires on stop(), on error, and when the browser ends recognition on its own
        // (e.g. a long silence) — the single place the live state is torn down.
        recognition.onend = () => {
            this._speechRecognition = null;
            this._isDictating = false;
        };
        // start() is what triggers the browser's mic permission prompt.
        this._speechRecognition = recognition;
        this._isDictating = true;
        this._error = '';
        recognition.start();
        this._inputEl?.focus();
    }
    /**
    /** Insert `/code ` into the composer and focus it, ready for the message. */
    _handleSkillSelect(skill) {
        // The user's roles don't allow this skill — inserting /code would just fail in the
        // chat. Name the roles that would grant it, so the user knows what to ask for and
        // whoever grants it knows what to give.
        if (skill.canRun === false) {
            let message = `You do not have access to "${skill.name}". Ask your site administrator to grant it.`;
            if (skill.allowedRoles?.length) {
                message = `"${skill.name}" requires the ${skill.allowedRoles.join(' or ')} role. Ask your site administrator to grant it.`;
            }
            KRSnackbar.show({
                title: 'Access Required',
                message: message,
                type: 'error',
            });
            return;
        }
        // A global skill nobody has installed here yet isn't runnable either — point at the
        // Install button rather than appearing to ignore the click. Local skills carry no
        // install state, so this only applies to global ones.
        if (skill.global && !skill.enabled) {
            KRSnackbar.show({
                title: 'Not Installed',
                message: `"${skill.name}" has to be installed on this site before you can use it — use the Install button on the card.`,
                type: 'info',
            });
            return;
        }
        this._inputValue = `/${skill.code} `;
        this._saveState();
        this._skillMenuOpened = false;
        this._skillBrowseOpened = false;
        this._inputEl?.focus();
    }
    /** Open a skill card via keyboard (Enter/Space), since it's a div role=button. */
    _handleSkillCardKeydown(e, skill) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._handleSkillCardOpen(skill);
        }
    }
    /**
     * Click an empty-state suggestion chip: prefill `/code ` into the composer, same as picking the
     * skill from the `/` menu, and focus so the user can add details before sending. No permission
     * guards here (unlike `_handleSkillSelect`) — a suggested action's code always comes from the
     * server's role-filtered runnable set, so it's already known to be invokable by this user.
     */
    _handleSuggestedActionClick(action) {
        this._inputValue = `/${action.code} `;
        this._saveState();
        this._inputEl?.focus();
    }
    /** Copy a message's text to the clipboard and briefly flash "Copied". */
    _handleCopyMessage(content, index) {
        navigator.clipboard.writeText(content).then(() => {
            this._copiedIndex = index;
            setTimeout(() => {
                if (this._copiedIndex === index) {
                    this._copiedIndex = -1;
                }
            }, 2000);
        }).catch(() => { });
    }
    _handleSkillBrowseOpen() {
        this._skillMenuOpened = false;
        this._skillBrowseQuery = '';
        this._skillBrowseOpened = true;
        // Fill both tabs up front so switching between them is instant, not stale.
        // The Kodaris tab isn't rendered on the registry server (except a localhost dev box,
        // which is server and client at once), so skip its fetch there.
        this._searchOrgSkills('');
        if (this._aiFrameworkConfig.registryServer !== true || KRHttp.getBaseUrl().includes('localhost')) {
            this._searchGlobalSkills('');
        }
    }
    _handleSkillBrowseClose() {
        this._skillBrowseOpened = false;
        this._skillBrowseDetail = null;
    }
    /** Open a skill's detail view inside the browse dialog. */
    _handleSkillCardOpen(skill) {
        this._skillBrowseDetail = skill;
    }
    /** Return from the detail view to the grid, keeping the tab and search text. */
    _handleSkillDetailBack() {
        this._skillBrowseDetail = null;
    }
    /** Try It on the detail view: insert the open skill into the composer. */
    _handleSkillDetailTry() {
        if (!this._skillBrowseDetail) {
            return;
        }
        this._handleSkillSelect(this._skillBrowseDetail);
    }
    /** Install/Uninstall on the detail view for the open global skill. */
    _handleSkillDetailEnable(e) {
        if (!this._skillBrowseDetail) {
            return;
        }
        this._handleGlobalSkillEnable(this._skillBrowseDetail, e);
    }
    _handleSkillBrowseSearch(e) {
        this._skillBrowseQuery = e.target.value;
        // Filter both catalogs so whichever tab the user switches to is already current.
        this._searchOrgSkills(this._skillBrowseQuery);
        if (this._aiFrameworkConfig.registryServer !== true || KRHttp.getBaseUrl().includes('localhost')) {
            this._searchGlobalSkills(this._skillBrowseQuery);
        }
    }
    _handleSkillBrowseTabChange(e) {
        // Both buffers are kept current by open/search, so switching just swaps which
        // one is shown — no re-search needed.
        this._skillBrowseSource = e.detail.activeTabId;
    }
    // --- Attachments ---
    /**
     * Open the OS file picker. `aiAnalyze` records which attach action was used so
     * the change handler stages the picked files in the right mode: true for
     * "Attach Files" (the model reads them), false for "Attach Files for Reference"
     * (attached for a tool to act on — the model is told about them but not asked
     * to read them). Chosen once at the picker, so selecting 30 files never means
     * toggling 30 chips.
     */
    _openFilePicker(aiAnalyze = true) {
        // Close the skill/attachment menu — opening the OS file picker is the end of
        // the menu interaction, and the picker is a separate window so the menu would
        // otherwise linger open behind it (and after a file is chosen).
        this._skillMenuOpened = false;
        // A lone `/` was only the means of reaching this action, so it shouldn't be
        // left behind in the composer once the picker opens. Nothing else is touched:
        // a real command (`/code`) stays because `/code` plus an attachment is a valid
        // invocation, and the plus button reaches this same method with an arbitrary
        // draft in the composer, which must survive.
        if (this._inputValue.trim() === '/') {
            this._inputValue = '';
            this._saveState();
        }
        this._pendingAttachAnalyze = aiAnalyze;
        this._fileInputEl?.click();
    }
    _handleFileInputChange(e) {
        const input = e.target;
        if (input.files) {
            this._addFiles(input.files, this._pendingAttachAnalyze);
        }
        // Reset so picking the same file again re-fires change.
        input.value = '';
    }
    _handlePaste(e) {
        const items = e.clipboardData?.items;
        if (!items) {
            return;
        }
        const files = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                const file = items[i].getAsFile();
                if (file) {
                    files.push(file);
                }
            }
        }
        if (files.length) {
            e.preventDefault();
            this._addFiles(files);
        }
    }
    _handleDragEnter(e) {
        if (!this._hasFiles(e)) {
            return;
        }
        e.preventDefault();
        this._dragDepth++;
        this._isDraggingFile = true;
    }
    _handleDragOver(e) {
        if (!this._hasFiles(e)) {
            return;
        }
        // Required so the drop event fires.
        e.preventDefault();
    }
    _handleDragLeave(e) {
        if (!this._hasFiles(e)) {
            return;
        }
        this._dragDepth--;
        if (this._dragDepth <= 0) {
            this._dragDepth = 0;
            this._isDraggingFile = false;
        }
    }
    _handleDrop(e) {
        if (!this._hasFiles(e)) {
            return;
        }
        e.preventDefault();
        this._dragDepth = 0;
        this._isDraggingFile = false;
        if (e.dataTransfer?.files) {
            this._addFiles(e.dataTransfer.files);
        }
    }
    _hasFiles(e) {
        return Array.from(e.dataTransfer?.types || []).includes('Files');
    }
    /** Lower-cased extension of a filename (without the dot), or '' if none. */
    _getFileExtension(name) {
        const dot = name.lastIndexOf('.');
        if (dot < 0) {
            return '';
        }
        return name.slice(dot + 1).toLowerCase();
    }
    /**
     * Stage a set of files in the composer (held locally until send). `aiAnalyze`
     * comes from which attach action was used — true for "Attach Files" (the model
     * reads them), false for "Attach Files for Reference" (tool-only, not read).
     * Drag-and-drop and paste default to analyze.
     */
    _addFiles(fileList, aiAnalyze = true) {
        // Only accept what the picker's `accept` filter allows (extension-based, so a
        // drag/paste can't sneak in a type the picker itself wouldn't offer).
        const files = Array.from(fileList).filter((f) => ACCEPTED_FILE_EXTENSIONS.includes(this._getFileExtension(f.name)));
        if (!files.length) {
            return;
        }
        files.forEach((file) => {
            // Only images get a local preview thumbnail; documents (and videos, which are
            // tiles too) render an icon. Prefer the File's MIME, falling back to the
            // extension when it's absent.
            let previewUrl;
            if (file.type.startsWith('image/') || IMAGE_EXTENSIONS.includes(this._getFileExtension(file.name))) {
                previewUrl = URL.createObjectURL(file);
            }
            const pending = {
                localId: 'att-' + this._pendingSeq++,
                file,
                name: file.name,
                mimeType: file.type,
                previewUrl,
                aiAnalyze,
            };
            this._pendingConversationFiles = [...this._pendingConversationFiles, pending];
        });
    }
    _handlePendingRemove(localId) {
        const target = this._pendingConversationFiles.find((p) => p.localId === localId);
        if (target?.previewUrl) {
            URL.revokeObjectURL(target.previewUrl);
        }
        this._pendingConversationFiles = this._pendingConversationFiles.filter((p) => p.localId !== localId);
    }
    _clearPending() {
        this._pendingConversationFiles.forEach((p) => {
            if (p.previewUrl) {
                URL.revokeObjectURL(p.previewUrl);
            }
        });
        this._pendingConversationFiles = [];
    }
    /** Open the shared full-screen previewer for a message attachment (image or video). */
    _handleFileOpen(file) {
        // Use the local preview if the upload hasn't finished yet; otherwise the
        // backend view URL (prefixed with the base URL — a raw fetch of a relative
        // path would hit the page origin, e.g. the dev server, not the backend).
        // The previewer picks image vs video playback off the name's extension, so
        // niceName has to carry it — hence the generic fallback keeps no extension
        // only when there's no name at all.
        const src = file.previewUrl
            || `${KRHttp.getBaseUrl()}/api/system/conversationFile/${file.conversationFileID}/view`;
        const preview = KRFilePreview.open({
            src,
            name: file.niceName || 'file',
        });
        // The previewer's download button only fires a `download` event — the
        // consumer performs the actual download. Hit the view endpoint with
        // `?download=true` (Content-Disposition: attachment) in a new tab.
        preview.addEventListener('download', () => {
            window.open(`${KRHttp.getBaseUrl()}/api/system/conversationFile/${file.conversationFileID}/view?download=true`, '_blank');
        });
    }
    _handleSubmit() {
        if (this._isStreaming) {
            return;
        }
        // A guarded-tool batch is awaiting a decision. The turn lock released with
        // its finished:true, but the server rejects a normal chat message with
        // awaitingConfirmation until it's resolved — so the composer stays blocked
        // and the approve/deny prompt owns the next call.
        if (this._pendingActions.length) {
            return;
        }
        const messageText = this._inputValue.trim();
        const pending = this._pendingConversationFiles;
        // Allow sending an image with no text, but require at least one of them.
        if (!messageText && !pending.length) {
            return;
        }
        // The files sent to the backend, each paired with its analyze flag. Sent
        // files are shown once the conversation reloads and they come back as stored
        // conversation files; the just-sent message itself carries text only.
        const conversationFiles = pending.map((p) => ({
            file: p.file,
            aiAnalyze: p.aiAnalyze,
        }));
        const submitEvent = new CustomEvent('message-submit', {
            detail: {
                message: messageText,
                conversationFiles,
            },
            bubbles: true,
            composed: true,
            cancelable: true,
        });
        this.dispatchEvent(submitEvent);
        if (submitEvent.defaultPrevented) {
            return;
        }
        this._send({
            message: messageText,
            conversationFiles
        });
    }
    /**
     * Append the user message and stream the response. Split out of `_handleSubmit`
     * so debug test triggers can send a specific message without going through the
     * composer input. `_handleSubmit` owns reading/validating the composer and
     * firing the cancelable `message-submit` event; this owns the send + stream.
     */
    _send(params) {
        const userMessage = {
            type: 'user',
            content: params.message,
        };
        // Attach the staged files' local previews to the message so its thumbnails
        // show INSTANTLY, without waiting on the upload. Each carries a temporary
        // previewUrl (images only; documents render an icon from mimeType) and
        // conversationFileID -1 as a placeholder; the built-in send swaps these for
        // the real server files once the upload returns.
        if (this._pendingConversationFiles.length) {
            userMessage.conversationFiles = this._pendingConversationFiles.map((p) => ({
                conversationFileID: -1,
                niceName: p.name,
                mimeType: p.mimeType,
                aiAnalyze: p.aiAnalyze,
                previewUrl: p.previewUrl,
            }));
        }
        // The message is on its way, so stop the mic if it's still live — otherwise
        // it would keep transcribing into the now-cleared composer. abort() rather
        // than stop() because a trailing pending utterance belongs to the message
        // just sent; flushing it would drop stray words into the next one.
        if (this._speechRecognition) {
            this._speechRecognition.abort();
        }
        this.messages = [...this.messages, userMessage];
        this._inputValue = '';
        // The conversation has started — drop the empty-state chips so they don't linger above the
        // transcript. A newer suggestedActions reply is also ignored now (its guard checks messages.length).
        this._suggestedActions = [];
        this._saveState();
        this._error = '';
        this._userHasScrolledUp = false;
        this._followStream = true;
        // Clear the composer, but DON'T revoke the preview object URLs — the sent
        // message now renders them until the upload's real files replace them.
        this._pendingConversationFiles = [];
        // Reset textarea height
        if (this._inputEl) {
            this._inputEl.style.height = 'auto';
        }
        // Add empty assistant message to stream into
        this.messages = [...this.messages, {
                type: 'assistant',
                content: '',
            }];
        this._isStreaming = true;
        // Fresh turn: not yet finished, no reconnects used. These gate the drop-vs-done
        // decision in _readStreamChunk when the stream closes.
        this._turnFinishedCleanly = false;
        this._reconnectAttempts = 0;
        // Scroll user's message to the top (Gemini-style). The runway (min-height =
        // viewport) goes on the agent-turn WRAPPER, not an individual message — the
        // whole turn (reasoning, tool lines, text, decision) streams inside that one
        // element, so nothing strands below it. Enough scroll room below the user
        // message to push it to the top.
        this.updateComplete.then(() => {
            if (!this._messagesEl) {
                return;
            }
            // Clear runway from previous turns.
            this._messagesEl.querySelectorAll('.agent-turn').forEach((el) => {
                el.style.minHeight = '';
            });
            const userEls = this._messagesEl.querySelectorAll('.message--user');
            const userMsgEl = userEls[userEls.length - 1];
            const turnEls = this._messagesEl.querySelectorAll('.agent-turn');
            const turnEl = turnEls[turnEls.length - 1];
            if (turnEl) {
                turnEl.style.minHeight = this._messagesEl.clientHeight + 'px';
            }
            if (userMsgEl) {
                this._messagesEl.scrollTop = userMsgEl.offsetTop - this._messagesEl.offsetTop - 24;
            }
        });
        // Default to streaming from the Kodaris Bedrock conversation endpoints when
        // the consumer hasn't provided their own `send`.
        if (!this.send) {
            this.send = (params) => {
                // Rail-3 confirmation decision: resolve a paused guarded-tool batch by
                // hitting the SAME streaming endpoint with `confirmation` set. There's no
                // userText/files on this path (the server ignores message text here), and
                // the conversation is always already pinned (a batch can't pause before
                // the conversation exists), so no upload and no createNew.
                if (params.confirmation) {
                    return KRHttp.fetch({
                        url: '/api/system/integration/aws/bedrock/stream/invokeModelForConversationWithState',
                        method: 'POST',
                        headers: { Accept: 'text/event-stream' },
                        body: {
                            confirmation: params.confirmation,
                            conversationID: this.conversationId,
                        },
                    });
                }
                // Images are upload-first: each file is stored as a conversation file
                // (type LIBRARY — the format lives in mimeType), then the resulting
                // conversationFileIDs are attached to the message via `fileIds` on the
                // normal JSON stream call. There is no multipart chat endpoint. With no
                // images the upload resolves to an empty list immediately; a failed
                // upload is dropped so an undefined id never reaches `fileIds`.
                let uploaded = Promise.resolve([]);
                if (params.conversationFiles.length) {
                    uploaded = Promise.all(params.conversationFiles.map(({ file, aiAnalyze }) => {
                        const form = new FormData();
                        form.append('file', file);
                        form.append('type', 'LIBRARY');
                        form.append('niceName', file.name);
                        // Reference-only files (from "Attach Files for Reference") aren't read
                        // by the model — the endpoint defaults aiAnalyze=true, so only send
                        // the flag when it's false.
                        if (!aiAnalyze) {
                            form.append('aiAnalyze', 'false');
                        }
                        return KRHttp.fetch({
                            url: '/api/system/conversationFile/upload',
                            method: 'POST',
                            body: form,
                        })
                            .then((r) => r.json())
                            // Keep the whole file DTO the upload returns (id + niceName +
                            // mimeType + …), not just the id — it's attached to the sent message
                            // below so the attachment shows immediately.
                            .then((res) => res.data);
                    }));
                }
                return uploaded.then((files) => {
                    // Replace the just-sent user message's local previews with the real
                    // uploaded files (backed by the stored file's view URL). The last user
                    // message is the one _send appended before the trailing empty assistant
                    // message; revoke its now-defunct preview object URLs first.
                    if (files.length) {
                        for (let i = this.messages.length - 1; i >= 0; i--) {
                            if (this.messages[i].type === 'user') {
                                (this.messages[i].conversationFiles || []).forEach((f) => {
                                    if (f.previewUrl) {
                                        URL.revokeObjectURL(f.previewUrl);
                                    }
                                });
                                this.messages[i].conversationFiles = files;
                                this.requestUpdate();
                                break;
                            }
                        }
                    }
                    // Conversation targeting: continue `conversationId` when set, else
                    // start a fresh conversation (createNew) for this turn. The pin below
                    // means createNew only fires on the very first untargeted turn.
                    const body = {
                        userText: params.message,
                        fileIds: files.map((f) => f.conversationFileID),
                        // Posture for guarded tools this turn (Ask / Auto / Plan). The server
                        // validates and persists it, and only re-sets it when it changes.
                        mode: this._mode,
                    };
                    // Ground the model in where the user is and what they've highlighted.
                    // Only sent when present so an empty selection / failed capture doesn't
                    // add noise to the request.
                    if (params.pageContext) {
                        body.pageContext = params.pageContext;
                    }
                    if (params.selectionContext) {
                        body.selectionContext = params.selectionContext;
                    }
                    if (this.conversationId != null) {
                        body.conversationID = this.conversationId;
                    }
                    else {
                        body.createNew = true;
                    }
                    return KRHttp.fetch({
                        url: '/api/system/integration/aws/bedrock/stream/invokeModelForConversationWithState',
                        method: 'POST',
                        headers: { Accept: 'text/event-stream' },
                        body,
                    }).then((response) => {
                        // Pin the session to the conversation this turn used. The stream
                        // doesn't echo the resolved id, so when we let the server create one
                        // (no conversationId set) we resolve it via `/last` and keep it, so
                        // subsequent turns continue this conversation instead of each sending
                        // createNew and spawning a new one. Best-effort, fire-and-forget: a
                        // failure leaves it unpinned and the next turn resolves server-side.
                        if (this.conversationId == null) {
                            KRHttp.fetch({ url: '/api/system/conversation/last' })
                                .then((r) => r.json())
                                .then((res) => {
                                const conversation = res?.data;
                                const id = conversation?.conversationID;
                                if (typeof id === 'number') {
                                    this.conversationId = id;
                                    // Pin it to this tab so a reload redisplays this new conversation.
                                    this._persistConversationId();
                                    // Announce the newly-created conversation so consumers can react
                                    // (e.g. update the route from /chat/new to /chat/{id}). The full
                                    // conversation object is passed, not just the id, so consumers
                                    // have the title/timestamps too.
                                    this.dispatchEvent(new CustomEvent('conversation-created', {
                                        detail: { conversation },
                                        bubbles: true,
                                        composed: true,
                                    }));
                                }
                            })
                                .catch(() => { });
                        }
                        return response;
                    });
                });
            };
        }
        // Capture where the user is and what they've highlighted at send time (not
        // earlier — selection is transient and the route may have changed since the
        // panel opened), so every turn grounds the model in the user's current spot.
        const context = this._captureContext();
        this.send({
            message: params.message,
            messages: this.messages.slice(0, -1),
            conversationFiles: params.conversationFiles,
            pageContext: context.pageContext,
            selectionContext: context.selectionContext,
        }).then((response) => {
            if (!response.ok) {
                this._handleStreamError('Request failed: ' + response.status);
                return;
            }
            if (!response.body) {
                this._handleStreamError('No response body');
                return;
            }
            this._reader = response.body.getReader();
            this._decoder = new TextDecoder();
            this._buffer = '';
            this._readStreamChunk();
        }).catch((err) => {
            if (err.name !== 'AbortError') {
                this._handleStreamError(err.message);
            }
        });
    }
    /**
     * Resolve the paused guarded-tool batch (Rail 3). Hits the SAME streaming
     * endpoint as a normal turn but with a `confirmation` value instead of a
     * message; the server runs (approve/approveAlways) or denies (reject) the batch
     * and continues the turn, streaming the outcome into a fresh assistant bubble.
     *
     * The decision uses the same `send` callback so a custom `send` can own its own
     * targeting — it just receives `confirmation` set and no message.
     */
    _handleConfirmationDecision(decision) {
        if (this._confirmationInFlight) {
            return;
        }
        this._confirmationInFlight = true;
        this._error = '';
        // The decision line is authored by the server (single source of truth): it
        // persists an `action_decision` row AND streams an `ACTION_DECISION` event
        // back. We keep the prompt in place — the clicked button shows a loading
        // spinner — until that event arrives, which is our ack that the server
        // accepted the decision. The event handler then drops the prompt, appends the
        // decision line, and the follow-up turn streams into a new assistant bubble.
        const btn = this._messagesEl?.querySelector(`.confirmation__btn[data-decision="${decision}"]`);
        btn?.showLoading();
        this._isStreaming = true;
        // Fresh turn (confirmation decision runs a model turn too): reset the
        // drop-vs-done gates used by _readStreamChunk.
        this._turnFinishedCleanly = false;
        this._reconnectAttempts = 0;
        this._userHasScrolledUp = false;
        this._followStream = true;
        this.send({
            message: '',
            messages: this.messages,
            conversationFiles: [],
            pageContext: '',
            selectionContext: '',
            confirmation: decision,
        }).then((response) => {
            // Leave _confirmationInFlight true — the buttons stay loading until the
            // streamed ACTION_DECISION event resolves the prompt. Only clear it on an
            // error path, where no such event will arrive.
            if (!response.ok) {
                this._confirmationInFlight = false;
                btn?.reset();
                this._handleStreamError('Request failed: ' + response.status);
                return;
            }
            if (!response.body) {
                this._confirmationInFlight = false;
                btn?.reset();
                this._handleStreamError('No response body');
                return;
            }
            this._reader = response.body.getReader();
            this._decoder = new TextDecoder();
            this._buffer = '';
            this._readStreamChunk();
        }).catch((err) => {
            this._confirmationInFlight = false;
            btn?.reset();
            if (err.name !== 'AbortError') {
                this._handleStreamError(err.message);
            }
        });
    }
    /**
     * Capture ambient "where is the user / what are they looking at" context at
     * send time, so the AI is grounded without the host passing anything in.
     *
     * Two universal sources that exist on every page the chatbot mounts in:
     * - `pageContext` — the location (full url, which already carries the path and
     *   query) and title, plus, when the page renders the server-side
     *   `#kodaris-page` JSON island (storefront content pages do), its parsed
     *   contents (page name/type/ids). A page without the island still yields url
     *   + title, so context is never empty on a real page.
     * - `selectionContext` — the text the user currently has highlighted, read
     *   live here because a selection is transient (it may be gone a moment later).
     *
     * Both are returned as strings because that's what the backend `ModelForText`
     * fields (`pageContext` / `selectionContext`) accept. Capture is best-effort:
     * any failure yields an empty string for that field rather than blocking send.
     */
    _captureContext() {
        let pageContext = '';
        try {
            const page = {
                url: window.location.href,
                title: document.title,
            };
            // Structured page context is opt-in by convention: a page that renders the
            // `#kodaris-page` island gets its name/type/ids folded in for free. The
            // island's text is JSON; a malformed/absent island just leaves it out.
            const island = document.getElementById('kodaris-page');
            if (island && island.textContent) {
                try {
                    page.page = JSON.parse(island.textContent);
                }
                catch { /* island present but not valid JSON — location context still stands */ }
            }
            pageContext = JSON.stringify(page);
        }
        catch { /* leave pageContext empty — never block a send on context capture */ }
        let selectionContext = '';
        try {
            const selection = window.getSelection();
            if (selection) {
                selectionContext = selection.toString().trim();
            }
        }
        catch { /* leave selectionContext empty */ }
        return {
            pageContext,
            selectionContext,
        };
    }
    _handleStop() {
        if (this._reader) {
            this._reader.cancel();
            this._reader = null;
        }
        this._isStreaming = false;
    }
    _handleMessagesScroll(e) {
        const container = e.target;
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceFromBottom > 50) {
            this._userHasScrolledUp = true;
        }
        else {
            this._userHasScrolledUp = false;
            // Back at the bottom mid-turn: resume following the stream end.
            if (this._isStreaming) {
                this._followStream = true;
            }
        }
    }
    /**
     * User-driven scroll intent while a response streams. Scroll events alone can't
     * tell the user apart from our own follow-scroll (and from scrollTop clamping
     * when the runway clears), so listen for the gestures instead: a wheel/trackpad
     * move up, any touch drag, or a pointer down on the scrollbar track. Any of
     * these stops the follow; scrolling back to the bottom restores it.
     */
    _handleMessagesWheel(e) {
        if (this._isStreaming && e.deltaY < 0) {
            this._followStream = false;
        }
    }
    _handleMessagesTouchMove() {
        if (this._isStreaming) {
            this._followStream = false;
        }
    }
    _handleMessagesPointerDown(e) {
        const container = e.currentTarget;
        if (this._isStreaming && e.offsetX >= container.clientWidth) {
            this._followStream = false;
        }
    }
    _handleErrorDismiss() {
        this._error = '';
    }
    _handleContextWarningDismiss() {
        this._contextWarning = '';
    }
    /**
     * A header button either opens the drawer to its view, switches the view if the
     * drawer is already open on the other one, or closes the drawer if it's already
     * showing this view. `_drawerView` is left intact on close so the content stays
     * visible while the drawer slides shut.
     */
    _toggleDrawer(view) {
        if (this._drawerOpen && this._drawerView === view) {
            this._drawerOpen = false;
        }
        else {
            this._drawerOpen = true;
            this._drawerView = view;
            // Opening the history view refetches the list so a just-created or renamed
            // conversation shows without a reload.
            if (view === 'history') {
                this._loadConversations();
            }
        }
        this._saveState();
    }
    /**
     * Fetch the user's previous conversations for the history list, most-recently
     * modified first. Ownership is enforced server-side (the endpoint scopes to the
     * current administrator), so we only filter to active ones.
     */
    _loadConversations() {
        this._conversationsLoading = true;
        KRHttp.fetch({
            url: '/api/system/conversation/list',
            method: 'POST',
            body: {
                page: 0,
                size: 50,
                sortBy: 'lastModified',
                sortDirection: 'desc',
                filterFields: [{
                        name: 'active',
                        operation: 'IS',
                        value: 'true',
                    }],
            },
        })
            .then((r) => r.json())
            .then((res) => {
            this._conversations = res?.data?.content || [];
        })
            .catch(() => {
            this._conversations = [];
        })
            .then(() => {
            this._conversationsLoading = false;
        });
    }
    /**
     * Switch the floating widget to a previously-created conversation (clicked in the
     * history list): stop any live turn, reset the transient turn/context state, pin
     * the new id (persisted per-tab so a reload stays here), then rebuild its
     * transcript via the built-in `load`. The drawer closes so the user lands back
     * in the conversation.
     */
    _handleSwitchConversation(conversationID) {
        if (conversationID === this.conversationId) {
            this._handleCloseDrawer();
            return;
        }
        this._handleStop();
        this.conversationId = conversationID;
        // Adopt the picked conversation's title for the header (empty for an untitled
        // one, so the header falls back to the default label).
        this._conversationTitle = this._conversations.find((c) => c.conversationID === conversationID)?.title || '';
        this._persistConversationId();
        // Clear the transient, per-conversation state so it doesn't bleed into the one
        // we're switching to — pending confirmations, context banners, errors, the
        // reconnect counter, and staged composer files (the transcript is loaded below).
        this._pendingActions = [];
        this._inlineConfirm = true;
        this._contextWarning = '';
        this._contextLimitReached = false;
        this._contextLimitMessage = '';
        this._error = '';
        this._reconnectAttempts = 0;
        this._pendingConversationFiles.forEach((p) => {
            if (p.previewUrl) {
                URL.revokeObjectURL(p.previewUrl);
            }
        });
        this._pendingConversationFiles = [];
        this._drawerOpen = false;
        this._saveState();
        if (this.load) {
            this.load().then((messages) => {
                this.messages = messages || [];
                // A turn may still be streaming server-side for the conversation we just
                // opened (started in another tab/device) — attach to it if so.
                if (this.conversationId && !this._isStreaming) {
                    this._reconnectStream();
                }
            }).catch(() => { });
        }
    }
    /**
     * Start a brand-new conversation (clicked in the history list): unpin the id (the
     * next turn sends `createNew` and the resolved id re-pins), clear the transcript
     * and transient state, and close the drawer back to an empty composer.
     */
    _handleNewConversation() {
        this._handleStop();
        this.conversationId = null;
        // New conversation has no title yet — the header shows the default label until
        // the server generates one at the end of the first turn.
        this._conversationTitle = '';
        this._persistConversationId();
        this.messages = [];
        // Clear the transient, per-conversation state so it doesn't bleed into the new
        // conversation — pending confirmations, context banners, errors, the reconnect
        // counter, and staged composer files.
        this._pendingActions = [];
        this._inlineConfirm = true;
        this._contextWarning = '';
        this._contextLimitReached = false;
        this._contextLimitMessage = '';
        this._error = '';
        this._reconnectAttempts = 0;
        this._pendingConversationFiles.forEach((p) => {
            if (p.previewUrl) {
                URL.revokeObjectURL(p.previewUrl);
            }
        });
        this._pendingConversationFiles = [];
        this._drawerOpen = false;
        this._saveState();
    }
    /** Close the drawer. `_drawerView` is left intact so its content stays visible
     *  while the drawer slides shut. */
    _handleCloseDrawer() {
        this._drawerOpen = false;
        this._saveState();
    }
    /**
     * Open the drawer to a single tool call's full input/output. Called when the
     * user clicks a tool-activity line in the transcript. Independent of the
     * `_showToolInputs` setting — this is the on-demand "show me everything for
     * THIS call" peek, so it works even when inline inputs are hidden. `result` is
     * the paired `tool_result` message data (may be absent if the tool hasn't
     * returned yet).
     */
    _openToolDetail(use) {
        // Pair the tool_use to the tool_result that immediately follows it (matched by
        // toolUseId) — the same pairing the transcript render uses for the ⎿ preview.
        const next = this.messages[this.messages.indexOf(use) + 1];
        this._toolDetail = {
            use,
            result: null,
        };
        if (next?.type === 'tool_result' && next.data?.toolUseId === use.data?.toolUseId) {
            this._toolDetail.result = next;
        }
        this._toolDetailCopied = null;
        this._toolDetailInputOpen = true;
        this._toolDetailOutputOpen = true;
        this._drawerView = 'toolDetail';
        this._drawerOpen = true;
        this._saveState();
        // Reset the panel scroll so a newly-opened tool starts at the top, rather
        // than inheriting the previous tool's scroll position. Wait for the render.
        this.updateComplete.then(() => {
            if (this._drawerBodyEl) {
                this._drawerBodyEl.scrollTop = 0;
            }
        });
    }
    /** Toggle the tool-detail Input section open/closed. */
    _handleToggleToolDetailInput() {
        this._toolDetailInputOpen = !this._toolDetailInputOpen;
    }
    /** Toggle the tool-detail Output section open/closed. */
    _handleToggleToolDetailOutput() {
        this._toolDetailOutputOpen = !this._toolDetailOutputOpen;
    }
    /**
     * Pretty-print a tool input/result for the detail view. The values arrive as
     * strings that usually hold JSON; reindent when they do, otherwise show as-is.
     * Never throws — falls back to the original text.
     *
     * A large/overflowing result is stashed server-side and returned as a preview
     * wrapped in `<<<uuid_BEGIN>>> … <<<uuid_END>>>` markers, followed by a paging
     * note. Two things make `JSON.parse` unusable here: the marker wrapper isn't
     * JSON, and the payload between the markers is a truncated SLICE of a bigger
     * blob (cut off mid-string), so it never parses on its own. So we reindent the
     * payload STRUCTURALLY (see `_reindentJson`) rather than parse it, and keep the
     * markers + note exactly where they were so it still reads as a paged response.
     */
    _formatToolJson(value) {
        if (value === undefined || value === null) {
            return '';
        }
        const raw = String(value);
        const marker = raw.match(/(<<<[0-9a-f-]+_BEGIN>>>\n?)([\s\S]*?)(\n?<<<[0-9a-f-]+_END>>>)/);
        if (marker && marker.index != null) {
            // Splice by index rather than String.replace — the reindented JSON can
            // contain `$` sequences (e.g. "$dtoRef") that replace() treats as special.
            const start = marker.index + marker[1].length;
            const end = start + marker[2].length;
            return raw.slice(0, start) + this._reindentJson(marker[2]) + raw.slice(end);
        }
        return this._reindentJson(raw);
    }
    /**
     * Reindent JSON-ish text for display. Tries a strict parse+stringify first (the
     * clean path for complete, valid JSON). If that fails — which it does for the
     * truncated slices returned by the stashed-response pager — it falls back to a
     * structural walk that inserts newlines/indentation around `{}[],:` while
     * tracking string state (so braces and commas INSIDE string values, and escaped
     * quotes, are left alone). This formats even incomplete JSON. Non-JSON text
     * (no leading `{`/`[`) is returned untouched.
     */
    _reindentJson(text) {
        const trimmed = text.trim();
        if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
            return text;
        }
        try {
            return JSON.stringify(JSON.parse(trimmed), null, 2);
        }
        catch {
            // Fall through to the tolerant structural reindent below.
        }
        let out = '';
        let depth = 0;
        let inString = false;
        let escaped = false;
        const pad = (level) => '\n' + '  '.repeat(Math.max(0, level));
        for (let i = 0; i < trimmed.length; i++) {
            const ch = trimmed[i];
            if (inString) {
                out += ch;
                if (escaped) {
                    escaped = false;
                }
                else if (ch === '\\') {
                    escaped = true;
                }
                else if (ch === '"') {
                    inString = false;
                }
                continue;
            }
            if (ch === '"') {
                inString = true;
                out += ch;
            }
            else if (ch === '{' || ch === '[') {
                depth++;
                out += ch + pad(depth);
            }
            else if (ch === '}' || ch === ']') {
                depth--;
                out += pad(depth) + ch;
            }
            else if (ch === ',') {
                out += ch + pad(depth);
            }
            else if (ch === ':') {
                out += ': ';
            }
            else if (ch === ' ' || ch === '\n' || ch === '\t' || ch === '\r') {
                // Collapse the source's own whitespace between tokens — our pad() owns layout.
            }
            else {
                out += ch;
            }
        }
        return out;
    }
    /** Copy the tool-detail Input to the clipboard. Stops propagation so the click
     *  doesn't also toggle the (parent) section header. */
    _handleCopyToolDetailInput(e) {
        e.stopPropagation();
        this._copyToolDetail('input', this._formatToolJson(this._toolDetail?.use.data?.toolInput));
    }
    /** Copy the tool-detail Output to the clipboard (see input handler). */
    _handleCopyToolDetailOutput(e) {
        e.stopPropagation();
        this._copyToolDetail('output', this._formatToolJson(this._toolDetail?.result?.data?.toolResultString));
    }
    /** Write one tool-detail block to the clipboard and flash "Copied" on it. */
    _copyToolDetail(block, content) {
        navigator.clipboard.writeText(content).then(() => {
            this._toolDetailCopied = block;
            setTimeout(() => {
                if (this._toolDetailCopied === block) {
                    this._toolDetailCopied = null;
                }
            }, 2000);
        }).catch(() => { });
    }
    _handleDrawerResizeMouseDown(e) {
        this._isDrawerResizing = true;
        this._drawerResizeStartX = e.clientX;
        this._drawerResizeStartW = this._drawerWidth;
        e.preventDefault();
        e.stopPropagation();
        document.addEventListener('mousemove', this._handleDrawerResizeMouseMove);
        document.addEventListener('mouseup', this._handleDrawerResizeMouseUp);
    }
    _readStreamChunk() {
        if (!this._reader) {
            return;
        }
        this._reader.read().then((result) => {
            if (result.done) {
                // The stream closed. Two very different cases:
                // 1. A `finished:true` chunk already arrived (_turnFinishedCleanly) — the
                //    turn is genuinely over; finishing was already handled below. Nothing to do.
                // 2. No finished chunk yet — the connection DROPPED mid-turn (5-min emitter
                //    timeout, tab backgrounded, network blip) but the server-side turn is
                //    still running. Do NOT unlock the composer (that's the old bug that made
                //    the input look ready while the AI was still working). Reconnect to the
                //    live stream and keep receiving events.
                if (this._turnFinishedCleanly) {
                    return;
                }
                this._reconnectStream();
                return;
            }
            this._buffer = this._buffer + this._decoder.decode(result.value, { stream: true });
            const events = this._buffer.split('\n\n');
            this._buffer = events.pop() || '';
            for (let i = 0; i < events.length; i++) {
                if (!events[i].trim()) {
                    continue;
                }
                const lines = events[i].split('\n');
                for (let j = 0; j < lines.length; j++) {
                    if (!lines[j].startsWith('data:')) {
                        continue;
                    }
                    const raw = lines[j].slice(5).trim();
                    if (!raw) {
                        continue;
                    }
                    let data;
                    try {
                        data = JSON.parse(raw);
                    }
                    catch {
                        continue;
                    }
                    // Dispatch a generic event for every SSE message so consumers can listen and react
                    this.dispatchEvent(new CustomEvent('stream-event', {
                        detail: data,
                        bubbles: true,
                        composed: true,
                    }));
                    if (data.type === 'REASONING_PART') {
                        if (data.message) {
                            // Text after a tool line belongs in a NEW assistant bubble — the
                            // last message may be a tool line, which text can't append to.
                            // _ensureAssistantBubble opens a fresh bubble when needed.
                            const target = this._ensureAssistantBubble();
                            if (!target.reasoning) {
                                target.reasoning = '';
                            }
                            target.reasoning += String(data.message);
                            this.requestUpdate();
                        }
                    }
                    else if (data.type === 'RESPONSE_PART') {
                        if (data.message) {
                            this._ensureAssistantBubble().content += String(data.message);
                            this.requestUpdate();
                        }
                    }
                    else if (data.type === 'tool_use' || data.type === 'tool_result') {
                        // Push the event straight into the transcript — the message IS the event
                        // ({type, data}). The render reads msg.data.* and pairs a tool_result to
                        // its tool_use by data.toolUseId. Same shape the DB persists and reload
                        // replays, so live and reload share one representation — no transform.
                        this.messages.push(data);
                        this.requestUpdate();
                    }
                    else if (data.type === 'action_decision') {
                        // The server accepted the confirmation decision and authored the record
                        // (single source of truth). Its arrival is the ack: drop the prompt,
                        // stop the buttons' loading, and push the event straight in — the render
                        // reads msg.data.{outcome,tools}. Same type + shape the DB persists, so
                        // live and reload share one representation.
                        this._pendingActions = [];
                        this._confirmationHighlighted = -1;
                        this._confirmationInFlight = false;
                        this.messages.push(data);
                        this.requestUpdate();
                    }
                    else if (data.type === 'ERROR') {
                        this._handleStreamError(String(data.message || 'Unknown error'));
                    }
                    else if (data.type === 'CONVERSATION_STATE') {
                        // Turn-start state sync: the server is the source of truth for the
                        // conversation's mode (it may have decayed EDIT → NORMAL after idle).
                        // Adopt it so the composer's selector reflects the real posture.
                        if (data.data?.mode === 'NORMAL' || data.data?.mode === 'EDIT' || data.data?.mode === 'PLAN') {
                            this._mode = data.data.mode;
                        }
                    }
                    else if (data.type === 'CONVERSATION_TOKEN_WARNING') {
                        // User-facing: friendly "conversation is getting long" guidance.
                        // Text only, never sent to the model — render verbatim as a banner.
                        this._contextWarning = String(data.message || '');
                    }
                    else if (data.type === 'CONVERSATION_TITLE') {
                        // The server generated a real title for a brand-new conversation
                        // (it was created with a truncated first-message placeholder that we
                        // deliberately never show). Adopt it as the header label and pass it
                        // up so consumers can update their own chrome, e.g. a breadcrumb.
                        if (data.data?.title) {
                            this._conversationTitle = data.data.title;
                            this.dispatchEvent(new CustomEvent('conversation-title', {
                                detail: { title: data.data.title },
                                bubbles: true,
                                composed: true,
                            }));
                        }
                    }
                    else if (data.type === 'CONVERSATION_LIMIT_REACHED') {
                        // User-facing hard stop. Carries finished:true (handled below).
                        // Clear any soft warning so we don't double-message the user.
                        this._contextLimitReached = true;
                        this._contextLimitMessage = String(data.message || '');
                        this._contextWarning = '';
                    }
                    else if (data.type === 'ACTION_CONFIRMATION_REQUIRED') {
                        // A guarded tool paused for approval (Rail 3). Carries finished:true
                        // (handled below), so the turn lock releases — but the conversation
                        // is now awaiting a decision, and a normal chat message would be
                        // rejected with awaitingConfirmation. Capture the batch so the
                        // composer stays blocked and the approve/deny prompt renders instead;
                        // the next call must be a confirmation, not a userText.
                        const actions = data.data?.actions;
                        if (Array.isArray(actions) && actions.length) {
                            this._pendingActions = actions;
                            // Default the keyboard highlight to Yes so Enter approves.
                            this._confirmationHighlighted = 0;
                        }
                    }
                    else if (data.type === 'TOKEN_USAGE') {
                        // INTERNAL-ONLY debug telemetry. Update the panel snapshot and the
                        // per-iteration log; never render as a chat bubble.
                        if (data.data) {
                            const usage = data.data;
                            this._telemetry = usage;
                            // A new turn starts at iteration 1 — clear the prior turn's log
                            // only now (not at send), so it stays visible until fresh data
                            // actually arrives rather than blanking out on submit.
                            if (usage.iteration === 1) {
                                this._iterationLog = [usage];
                                this._cacheCheckpoints = {};
                            }
                            else {
                                this._iterationLog = [...this._iterationLog, usage];
                            }
                        }
                    }
                    else if (data.type === 'CACHE_CHECKPOINT') {
                        // INTERNAL-ONLY debug telemetry. Records when/under which mode the
                        // history checkpoint was placed, keyed by iteration for the table.
                        if (data.data) {
                            const checkpoint = data.data;
                            this._cacheCheckpoints = {
                                ...this._cacheCheckpoints,
                                [checkpoint.iteration]: checkpoint,
                            };
                        }
                    }
                    if (data.finished) {
                        // Primary unlock signal: the turn is over and the server released
                        // the turn lock. The SSE stream closes right after this chunk. Mark
                        // it clean so the imminent `result.done` doesn't trigger a reconnect.
                        this._turnFinishedCleanly = true;
                        this._finishStream();
                    }
                }
            }
            // Stop reading once a terminal chunk ended the turn — the reader is nulled
            // by _finishStream and the stream is closing anyway.
            if (!this._reader) {
                return;
            }
            this._readStreamChunk();
        }).catch((err) => {
            if (err.name === 'AbortError') {
                return;
            }
            // A read error mid-turn is a drop, not a completion — reconnect (same as a
            // clean `done` without a finished chunk) unless the turn already finished.
            if (!this._turnFinishedCleanly) {
                this._reconnectStream();
                return;
            }
            this._handleStreamError(err.message);
        });
    }
    /**
     * Reconnect to an in-flight turn after the SSE stream dropped without finishing
     * (5-min emitter timeout, tab backgrounded, network blip). Hits the read-only
     * `/stream/attach/{conversationID}` endpoint, which re-attaches a fresh SSE
     * connection to the still-running server-side turn without starting anything new.
     *
     * - 200 with a body → resume reading; the turn's events keep flowing (events
     *   emitted during the gap are not replayed, so the live text may jump forward —
     *   the full transcript reconciles from the DB when the turn ends).
     * - 204 → no live turn: it already finished server-side while we were
     *   disconnected. Reload the transcript so the final answer appears, then unlock.
     * - failure / retry cap hit → give up and finish so the composer never wedges.
     *
     * The composer stays locked (`_isStreaming` untouched) throughout, so the input
     * never looks ready while the AI is still working.
     */
    _reconnectStream() {
        // Without a pinned conversation there's nothing to re-attach to (a custom
        // `send` owns its own targeting). Fall back to finishing.
        if (!this.conversationId) {
            this._finishStream();
            return;
        }
        // Bound the retries so a genuinely dead turn can't loop forever.
        if (this._reconnectAttempts >= 5) {
            this._handleStreamError('Lost connection to the assistant. Please reload the conversation.');
            return;
        }
        this._reconnectAttempts = this._reconnectAttempts + 1;
        KRHttp.fetch({
            url: `/api/system/integration/aws/bedrock/stream/attach/${this.conversationId}`,
            method: 'GET',
            headers: { Accept: 'text/event-stream' },
        }).then((response) => {
            // 204 No Content: the turn finished server-side during the gap. Pull the
            // final transcript from the DB (via the same `load` callback the component
            // initializes with) so the answer that streamed during the disconnect
            // appears, then unlock.
            if (response.status === 204) {
                this._reconnectAttempts = 0;
                if (this.load) {
                    this.load().then((messages) => {
                        this.messages = messages;
                        this.requestUpdate();
                    }).catch(() => {
                        // Reload failed — leave the transcript as-is; the user can refresh manually.
                    });
                }
                this._finishStream();
                return;
            }
            if (!response.ok || !response.body) {
                // Transient failure — back off and try again on the same drop.
                setTimeout(() => this._reconnectStream(), 1000);
                return;
            }
            // Re-attached to the live turn: reset the retry counter and resume reading
            // into the same buffer/decoder from the fresh stream. Mark streaming so the
            // composer locks and the typing indicator shows — load-triggered attaches (a
            // page reloaded mid-turn) start with _isStreaming false, so this is what
            // surfaces "the AI is still working"; on a mid-session drop it's already true
            // (a no-op).
            this._reconnectAttempts = 0;
            this._isStreaming = true;
            this._followStream = true;
            this._reader = response.body.getReader();
            this._decoder = new TextDecoder();
            this._buffer = '';
            // Close the gap: events the turn emitted between the drop and this re-attach
            // aren't replayed on the live stream. Every COMPLETED iteration during the
            // gap was persisted (assistant text, tool_use/tool_result rows), so reload the
            // transcript from the DB to backfill them, THEN start reading live. Ordering
            // matters — the reader buffers incoming bytes until _readStreamChunk runs, so
            // starting live only after the async reload resolves prevents the reload from
            // clobbering freshly-streamed chunks. Only the current in-flight iteration's
            // partial text is missing from the reload; it streams live from here.
            if (this.load) {
                this.load().then((messages) => {
                    this.messages = messages;
                    this.requestUpdate();
                    this._readStreamChunk();
                }).catch(() => {
                    // Reload failed — go live anyway; the gap's completed iterations just
                    // won't show until the next full reload, but the turn keeps streaming.
                    this._readStreamChunk();
                });
            }
            else {
                this._readStreamChunk();
            }
        }).catch(() => {
            // Network error reaching the attach endpoint — back off and retry.
            setTimeout(() => this._reconnectStream(), 1000);
        });
    }
    /**
     * The assistant bubble streamed text should append to. Returns the last message
     * when it's already an assistant bubble; otherwise (the last message is a tool
     * line, or there's nothing yet) opens a fresh assistant bubble so post-tool text
     * doesn't get dropped or glued to a tool line.
     */
    _ensureAssistantBubble() {
        const last = this.messages[this.messages.length - 1];
        if (last && last.type === 'assistant') {
            return last;
        }
        const bubble = {
            type: 'assistant',
            content: '',
        };
        this.messages = [...this.messages, bubble];
        return bubble;
    }
    /**
     * End the current turn: release the turn lock and the reader. Called on the
     * primary unlock signal (`finished:true`) and the fallback (stream close).
     *
     * A turn that paused for confirmation (`_pendingActions` set) leaves the
     * composer blocked even though the lock released — the approve/deny prompt
     * owns the next call. Its trailing empty assistant bubble is dropped so the
     * prompt doesn't sit under an empty message with a typing indicator.
     */
    _finishStream() {
        this._isStreaming = false;
        this._reader = null;
        if (this._pendingActions.length) {
            const lastIndex = this.messages.length - 1;
            // Drop the trailing assistant bubble only when it's truly empty — no
            // streamed text AND no reasoning. A reasoning-only bubble (the model thought
            // out loud, then went straight to the guarded tool) must be kept, else its
            // "Thinking" trace is lost when the confirmation card renders.
            if (lastIndex >= 0
                && this.messages[lastIndex].type === 'assistant'
                && !this.messages[lastIndex].content
                && !this.messages[lastIndex].reasoning) {
                this.messages = this.messages.slice(0, -1);
            }
            // Scroll the confirmation into view so the user actually sees the ask — an
            // agentic turn can stream many tool lines past the fold, leaving the prompt
            // below it. `block: 'end'` brings its bottom up to just above the composer.
            // Match on the alertdialog role: an earlier skill-offer card in the transcript
            // shares the .confirmation class and would otherwise win as the first match.
            if (this._inlineConfirm) {
                this.updateComplete.then(() => {
                    this._messagesEl?.querySelector('.confirmation[role="alertdialog"]')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                    });
                });
            }
        }
    }
    _handleStreamError(message) {
        this._isStreaming = false;
        this._error = message;
        if (this._reader) {
            this._reader.cancel();
            this._reader = null;
        }
        // Remove the empty assistant message if it has no content
        if (this.messages.length > 0) {
            const lastMessage = this.messages[this.messages.length - 1];
            if (lastMessage.type === 'assistant' && !lastMessage.content) {
                this.messages = this.messages.slice(0, -1);
            }
        }
    }
    // --- Render ---
    /**
     * The composer's mode selector — a Gemini-style inline label + caret sitting
     * just before the mic, that opens a small menu (Ask / Auto / Plan). Inline text
     * rather than a pill so it doesn't grow the single-row composer's height. The
     * menu opens upward above the trigger.
     */
    _renderModeSelector() {
        const active = CHATBOT_MODES.find((m) => m.mode === this._mode) || CHATBOT_MODES[0];
        return html `
      <div class="mode">
        ${this._modeMenuOpened ? html `
          <div class="mode-menu">
            ${CHATBOT_MODES.map((m) => html `
              <button
                class=${classMap({
            'mode-menu__item': true,
            'mode-menu__item--active': m.mode === this._mode,
        })}
                @click=${() => this._handleModeSelect(m.mode)}
              >
                <span class="mode-menu__label">${m.label}</span>
                <span class="mode-menu__desc">${m.description}</span>
              </button>
            `)}
          </div>
        ` : nothing}
        <button
          class="mode-trigger"
          aria-haspopup="true"
          aria-expanded=${this._modeMenuOpened ? 'true' : 'false'}
          @click=${this._handleModeToggle}
        >
          <span class="mode-trigger__label">${active.label}</span>
          <svg
            class="mode-trigger__caret"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>
    `;
    }
    /**
     * The approve/deny card shown when guarded tools are paused (Rail 3). Floats
     * OVER the messages, anchored just above the composer — not part of the message
     * scroll — so it stays in view while the transcript scrolls behind it.
     *
     * Written for a non-technical user. A single action reads as one plain-language
     * ask (`Run "{tool}"?` + the model's `confirmationMessage`). When the model
     * batches several guarded tools, they're listed together under one header and
     * the user accepts or rejects them all at once — the backend decision is
     * all-or-nothing. Either way: NO tool code, NO raw arguments/JSON — those are
     * noise (or alarming) to a normal user.
     */
    _renderConfirmation() {
        // Title: name the single tool, else a generic header for a batch.
        let title = 'Run this action?';
        if (this._pendingActions.length > 1) {
            title = 'These actions need your approval';
        }
        else if (this._pendingActions[0]) {
            title = `Run "${this._pendingActions[0].displayName || this._pendingActions[0].toolName}"?`;
        }
        return html `
      <div
        class=${classMap({
            'confirmation': true,
            'confirmation--inline': this._inlineConfirm,
        })}
        role="alertdialog"
        aria-label="Confirm before continuing"
      >
        <div class="confirmation__title">${title}</div>
        ${this._pendingActions.length > 1
            ? html `
            <ol class="confirmation__list">
              ${this._pendingActions.map((action) => html `
                <li class="confirmation__list-item">
                  <span class="confirmation__list-name">${action.displayName || action.toolName}</span>
                  ${action.confirmationMessage
                ? html `<span class="confirmation__list-desc">${action.confirmationMessage}</span>`
                : nothing}
                  ${this._showConfirmationInput && this._inlineConfirm && action.input && Object.keys(action.input).length
                ? html `<pre class="confirmation__details-input">${JSON.stringify(action.input, null, 2)}</pre>`
                : nothing}
                </li>
              `)}
            </ol>
          `
            : html `
            <p class="confirmation__message">${this._pendingActions[0]?.confirmationMessage || 'The assistant wants to take an action. Is that OK?'}</p>
            ${this._showConfirmationInput && this._inlineConfirm && this._pendingActions[0]?.input && Object.keys(this._pendingActions[0].input).length
                ? html `<pre class="confirmation__details-input">${JSON.stringify(this._pendingActions[0].input, null, 2)}</pre>`
                : nothing}
          `}
        <div class="confirmation__actions">
          ${CONFIRMATION_DECISIONS.map((option, index) => html `
            <kr-button
              class=${classMap({
            'confirmation__btn': true,
            'confirmation__btn--highlighted': index === this._confirmationHighlighted,
        })}
              variant=${option.decision === 'approve' ? 'flat' : 'outline'}
              color=${option.decision === 'reject' ? 'secondary' : 'primary'}
              size="small"
              data-decision=${option.decision}
              @click=${() => this._handleConfirmationDecision(option.decision)}
            >${option.label}</kr-button>
          `)}
        </div>
      </div>
    `;
    }
    /** The `/` skill menu shown above the composer while typing a slash command. */
    _renderSkillMenu() {
        const skills = this._invocableSkills;
        // Debug test triggers are extra actions after Attach Files / Browse. The
        // flat keyboard index counts actions first, then skills, so the skills'
        // index offset is the number of actions shown.
        const debugActions = this.variant === 'inline' && this._aiFrameworkConfig.debugTelemetryEnabled === true;
        let actionCount = 3;
        if (debugActions) {
            actionCount = 5;
        }
        return html `
      <div class="skill-menu">
        <div class="skill-menu__actions">
          <button
            class=${classMap({
            'skill-menu__action': true,
            'skill-menu__item--highlighted': this._skillHighlighted === 0,
        })}
            @click=${() => this._openFilePicker(true)}
          >
            <span class="skill-menu__name">Attach Files</span>
            <span class="skill-menu__desc">The AI reads these files</span>
          </button>
          <button
            class=${classMap({
            'skill-menu__action': true,
            'skill-menu__item--highlighted': this._skillHighlighted === 1,
        })}
            @click=${() => this._openFilePicker(false)}
          >
            <span class="skill-menu__name">Attach Files for Reference</span>
            <span class="skill-menu__desc">Attached for a tool — not read by the AI</span>
          </button>
          <button
            class=${classMap({
            'skill-menu__action': true,
            'skill-menu__item--highlighted': this._skillHighlighted === 2,
        })}
            @click=${this._handleSkillBrowseOpen}
          >
            Browse All Skills
          </button>
          ${debugActions ? html `
            <button
              class=${classMap({
            'skill-menu__action': true,
            'skill-menu__item--highlighted': this._skillHighlighted === 3,
        })}
              @click=${() => this._activateSkillMenuItem(3)}
            >
              Test Context Warning
            </button>
            <button
              class=${classMap({
            'skill-menu__action': true,
            'skill-menu__item--highlighted': this._skillHighlighted === 4,
        })}
              @click=${() => this._activateSkillMenuItem(4)}
            >
              Test Context Limit
            </button>
          ` : nothing}
        </div>
        ${skills.length ? html `
          <ul class="skill-menu__list">
            ${skills.map((skill, index) => html `
              <li>
                <button
                  class=${classMap({
            'skill-menu__item': true,
            'skill-menu__item--highlighted': index + actionCount === this._skillHighlighted,
        })}
                  @click=${() => this._handleSkillSelect(skill)}
                >
                  <span class="skill-menu__name">${skill.name}</span>
                  ${skill.summary ? html `
                    <span class="skill-menu__desc">${skill.summary}</span>
                  ` : nothing}
                </button>
              </li>
            `)}
          </ul>
        ` : nothing}
      </div>
    `;
    }
    /**
     * The "Browse all skills" dialog — a searchable grid of Org / Kodaris cards.
     *
     * The Kodaris tab is omitted on the registry server (kodaris.com): that instance
     * IS the catalog source, so `globalSkillList` has no remote to pull and always
     * returns `[]`. Its own published skills appear on the Your Org tab instead,
     * carrying a "Global" badge. A localhost backend is the exception: a dev box is
     * usually registry server and client at once (the self-loopback), and the server
     * flag alone would hide the catalog it pulls from itself.
     */
    _renderSkillBrowse() {
        return html `
      <kr-dialog
        opened
        width="720px"
        @close=${this._handleSkillBrowseClose}
      >
        <kr-dialog-header>
          <div class="skill-browse__header">
            ${this._skillBrowseDetail ? html `
              <button
                type="button"
                class="skill-browse__back"
                aria-label="Back"
                @click=${this._handleSkillDetailBack}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
            ` : html `
              <h2>Browse Skills</h2>
            `}
            <button
              type="button"
              class="skill-browse__close"
              aria-label="Close"
              @click=${this._handleSkillBrowseClose}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M6 18 18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </kr-dialog-header>
        <div class="skill-browse">
          ${this._skillBrowseDetail ? html `
            <div class="skill-detail">
              <div class="skill-detail__body">
                <div class="skill-detail__section">
                  <span class="skill-detail__title">${this._skillBrowseDetail.name}</span>
                  ${this._skillBrowseDetail.summary ? html `
                    <p class="skill-detail__summary">${this._skillBrowseDetail.summary}</p>
                  ` : nothing}
                </div>
                <div class="skill-detail__row">
                  <span class="skill-detail__label">Author</span>
                  <span class="skill-detail__value">${this._skillBrowseDetail.global ? 'Kodaris' : 'Your organization'}</span>
                </div>
                ${this._skillBrowseDetail.global ? html `
                  <div class="skill-detail__row">
                    <span class="skill-detail__label">Price</span>
                    <span class="skill-detail__value">${this._skillBrowseDetail.priceLabel}</span>
                  </div>
                ` : nothing}
                ${this._skillBrowseDetail.allowedRoles?.length ? html `
                  <div class="skill-detail__row">
                    <span class="skill-detail__label">Roles</span>
                    <div class="skill-detail__chips">
                      ${this._skillBrowseDetail.allowedRoles.map((role) => html `
                        <span class="skill-detail__chip">${role}</span>
                      `)}
                    </div>
                  </div>
                ` : nothing}
              </div>
              <div class="skill-detail__footer">
                ${this._skillBrowseDetail.canRun !== false && (!this._skillBrowseDetail.global || this._skillBrowseDetail.enabled) ? html `
                  <kr-button
                    variant="flat"
                    color="primary"
                    @click=${this._handleSkillDetailTry}
                  >Try It</kr-button>
                ` : nothing}
                ${this._skillBrowseDetail.global ? html `
                  <kr-button
                    variant=${this._skillBrowseDetail.enabled ? 'outline' : 'flat'}
                    color=${this._skillBrowseDetail.enabled ? 'secondary' : 'primary'}
                    @click=${this._handleSkillDetailEnable}
                  >${this._skillBrowseDetail.enabled ? 'Uninstall' : 'Install'}</kr-button>
                ` : nothing}
              </div>
            </div>
          ` : html `
            <kr-text-field
              placeholder="Search skills..."
              .value=${this._skillBrowseQuery}
              @input=${this._handleSkillBrowseSearch}
            ></kr-text-field>
            <kr-tab-group
              active-tab-id=${this._skillBrowseSource}
              @tab-change=${this._handleSkillBrowseTabChange}
            >
              <kr-tab id="org" label="Your Org">
                ${this._renderSkillGrid()}
              </kr-tab>
              ${this._aiFrameworkConfig.registryServer !== true || KRHttp.getBaseUrl().includes('localhost') ? html `
                <kr-tab id="kodaris" label="Kodaris">
                  ${this._renderSkillGrid(true)}
                </kr-tab>
              ` : nothing}
            </kr-tab-group>
          `}
        </div>
      </kr-dialog>
    `;
    }
    /**
     * The card grid of skill search results for the browse dialog. Clicking a card
     * opens its detail view inside `_renderSkillBrowse`, which is where Try It lives.
     * In the shared-Kodaris (`global`) tab each card also gets a footer row with an
     * Install/Uninstall button as a shortcut.
     *
     * The Kodaris tab is the whole published catalog, deliberately NOT role-filtered so
     * users can discover skills they don't have yet. Every card is clickable; the detail
     * view lists the roles and shows Install when the skill isn't installed here.
     */
    _renderSkillGrid(global = false) {
        let skills = this._orgSkills;
        if (global) {
            skills = this._globalSkills;
        }
        if (!skills.length) {
            return nothing;
        }
        return html `
      <div class="skill-browse__grid">
        ${skills.map((skill) => html `
          <div
            class="skill-card"
            role="button"
            tabindex="0"
            @click=${() => this._handleSkillCardOpen(skill)}
            @keydown=${(e) => this._handleSkillCardKeydown(e, skill)}
          >
            <span class="skill-card__name">${skill.name}</span>
            ${skill.summary ? html `
              <span class="skill-card__desc">${skill.summary}</span>
            ` : nothing}
            ${!global && skill.global ? html `
              <span class="skill-card__badge">Global</span>
            ` : nothing}
            ${global ? html `
              <div class="skill-card__footer">
                <span class="skill-card__price">${skill.priceLabel}</span>
                <kr-button
                  class="skill-card__install-btn"
                  size="small"
                  variant=${skill.enabled ? 'outline' : 'flat'}
                  color=${skill.enabled ? 'secondary' : 'primary'}
                  @click=${(e) => this._handleGlobalSkillEnable(skill, e)}
                >${skill.enabled ? 'Uninstall' : 'Install'}</kr-button>
              </div>
            ` : nothing}
          </div>
        `)}
      </div>
    `;
    }
    /**
     * Render a user message, highlighting a leading `/skill-code` token (like the
     * way Claude highlights slash commands). The composer only inserts valid skill
     * codes, so any leading slash token is treated as one.
     */
    _renderUserContent(content) {
        const match = content.match(/^\/(\S+)(\s[\s\S]*)?$/);
        if (match) {
            return html `<span class="message-skill">/${match[1]}</span>${match[2] || ''}`;
        }
        return content;
    }
    /** Compact integer formatting for the debug panel (e.g. 88928 → "88,928"). */
    _fmtTokens(n) {
        if (n == null || isNaN(n)) {
            return '—';
        }
        return n.toLocaleString('en-US');
    }
    /** Approximate USD formatting for the debug panel (e.g. 0.1434 → "~$0.1434"). */
    _fmtCost(n) {
        if (n == null || isNaN(n)) {
            return '—';
        }
        return '~$' + n.toFixed(4);
    }
    _fmtPrice(n) {
        if (n == null || isNaN(n)) {
            return '—';
        }
        return '$' + n.toFixed(2);
    }
    _fmtPriceDetail(n) {
        if (n == null || isNaN(n)) {
            return '—';
        }
        return '$' + n.toFixed(4);
    }
    _getCustomerPriceLabel() {
        const rate = this._telemetry?.customerPricePerThousandTokens;
        if (rate == null || isNaN(rate)) {
            return 'Customer price';
        }
        return `Customer price ($${rate}/1K)`;
    }
    _getMarkupPriceLabel() {
        const pct = this._telemetry?.customerMarkupPercent;
        if (pct == null || isNaN(pct)) {
            return 'Customer price (markup)';
        }
        return `Customer price (${pct}% markup)`;
    }
    _getTurnCacheWriteTokens() {
        return this._iterationLog.reduce((sum, it) => sum + (it.iterationCacheWrite || 0), 0);
    }
    _renderMarkupBreakdown(input, output, cacheRead, cacheWrite) {
        return html `
      <span class="debug-metrics__key debug-metrics__key--sub">Input</span>
      <span class="debug-metrics__val debug-metrics__val--sub">${this._fmtPriceDetail(input)}</span>
      <span class="debug-metrics__key debug-metrics__key--sub">Output</span>
      <span class="debug-metrics__val debug-metrics__val--sub">${this._fmtPriceDetail(output)}</span>
      <span class="debug-metrics__key debug-metrics__key--sub">Cache read</span>
      <span class="debug-metrics__val debug-metrics__val--sub">${this._fmtPriceDetail(cacheRead)}</span>
      <span class="debug-metrics__key debug-metrics__key--sub">Cache write</span>
      <span class="debug-metrics__val debug-metrics__val--sub">${this._fmtPriceDetail(cacheWrite)}</span>
    `;
    }
    /**
     * INTERNAL-ONLY debug telemetry panel. Rendered only in the inline variant
     * (guarded by the caller) and only when the user has toggled it open. Shows
     * three scopes from the latest TOKEN_USAGE event — context gauge, this turn,
     * lifetime — plus a verbose per-iteration table. Never customer-facing.
     */
    /**
     * The History view's body — the user's previous conversations (most recent
     * first) plus a "New conversation" action. Clicking a row switches the widget to
     * that conversation; the current one is marked so the user sees where they are.
     * Floating-only (opened from the header history button).
     */
    _renderHistoryContent() {
        return html `
      <div class="history">
        ${this._conversationsLoading ? html `
          <div class="history__empty">Loading…</div>
        ` : this._conversations.length === 0 ? html `
          <div class="history__empty">No previous conversations</div>
        ` : html `
          <div class="history__list">
            ${this._conversations.map((conversation) => {
            // Format lastModified for the row: today shows the time, this year
            // shows month/day, older shows month/day/year; the raw value if it
            // isn't a parseable date. Empty when the conversation has no date.
            let dateLabel = '';
            if (conversation.lastModified) {
                const date = new Date(conversation.lastModified);
                const now = new Date();
                if (isNaN(date.getTime())) {
                    dateLabel = conversation.lastModified;
                }
                else if (date.toDateString() === now.toDateString()) {
                    dateLabel = date.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                    });
                }
                else if (date.getFullYear() === now.getFullYear()) {
                    dateLabel = date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                    });
                }
                else {
                    dateLabel = date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                    });
                }
            }
            return html `
                <button
                  type="button"
                  class=${classMap({
                'history__item': true,
                'history__item--active': conversation.conversationID === this.conversationId,
            })}
                  @click=${() => this._handleSwitchConversation(conversation.conversationID)}
                >
                  <span class="history__item-title">${conversation.title || 'Untitled conversation'}</span>
                  ${dateLabel ? html `
                    <span class="history__item-date">${dateLabel}</span>
                  ` : nothing}
                </button>
              `;
        })}
          </div>
        `}
      </div>
    `;
    }
    /**
     * The side drawer — one sliding panel that holds the Settings, Debug, History,
     * and tool-detail views. The header buttons switch `_drawerView`; the title and
     * body swap inside. In inline mode it slides in beside the transcript at a
     * consistent (resizable) width; in floating mode it fills the panel over the
     * transcript with a back button (the panel is too narrow to show both).
     */
    _renderDrawer() {
        // The debug view is gated on the server-side flag (inline variant + debug
        // telemetry enabled); if it's not enabled, the drawer only ever shows settings.
        const isDebug = this._drawerView === 'debug'
            && this.variant === 'inline'
            && this._aiFrameworkConfig.debugTelemetryEnabled === true;
        let title = 'Settings';
        let body = this._renderSettingsContent();
        if (isDebug) {
            title = 'Debug';
            body = this._renderDebugContent();
        }
        else if (this._drawerView === 'toolDetail' && this._toolDetail) {
            title = this._toolDetail.use.data?.displayName || this._toolDetail.use.data?.toolName || 'Tool';
            body = this._renderToolDetailContent();
        }
        else if (this._drawerView === 'history') {
            title = 'Conversations';
            body = this._renderHistoryContent();
        }
        // In the floating widget the drawer fills the panel over the transcript (a
        // full-panel view with a back button) rather than sliding in beside it — the
        // panel is too narrow to show both. Inline keeps the side-drawer behavior.
        return html `
      <div
        class=${classMap({
            'drawer': true,
            'drawer--open': this._drawerOpen,
            'drawer--resizing': this._isDrawerResizing,
            'drawer--full': this.variant === 'floating',
        })}
        style=${styleMap(this.variant === 'floating' ? {} : { width: `${this._drawerWidth}px` })}
      >
        ${this.variant === 'floating' ? nothing : html `
          <div
            class="drawer__resize"
            @mousedown=${this._handleDrawerResizeMouseDown}
          ></div>
        `}
        <div class="drawer__header">
          ${this.variant === 'floating' ? html `
            <button
              class="header-action"
              @click=${this._handleCloseDrawer}
              title="Back"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="header-action-icon"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15.75 19.5 8.25 12l7.5-7.5"
                />
              </svg>
            </button>
          ` : nothing}
          <span class="drawer__title">${title}</span>
          ${this._drawerView === 'history' ? html `
            <kr-button
              class="history__new"
              variant="flat"
              color="primary"
              size="small"
              @click=${this._handleNewConversation}
            >
              <svg
                slot="icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.75"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
              New conversation
            </kr-button>
          ` : nothing}
          ${this.variant === 'floating' ? nothing : html `
            <button
              class="header-action"
              @click=${this._handleCloseDrawer}
              title="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="header-action-icon"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          `}
        </div>
        <div class="drawer__body">
          ${body}
        </div>
      </div>
    `;
    }
    /** The Settings view's body — user preferences, grouped in sections. */
    _renderSettingsContent() {
        return html `
      <div class="settings-section">
        <div class="settings-section__title">Tools</div>
        <div class="settings-row">
          <kr-checkbox
            label="Show tools"
            .checked=${this._showTools}
            @change=${this._handleShowToolsChange}
          ></kr-checkbox>
        </div>
        <div class="settings-row">
          <kr-checkbox
            label="Show tool inputs"
            .checked=${this._showToolInputs}
            @change=${this._handleShowToolInputsChange}
          ></kr-checkbox>
        </div>
        <div class="settings-row">
          <kr-checkbox
            label="Show tool confirmation inputs"
            .checked=${this._showConfirmationInput}
            @change=${this._handleShowConfirmationInputChange}
          ></kr-checkbox>
        </div>
      </div>
    `;
    }
    /**
     * The Tool detail view's body — the full input and output for one tool call,
     * pretty-printed in scrollable code blocks with a per-block copy button. This
     * is the "occasionally see everything" path: the transcript stays terse, and
     * clicking a tool line drops its complete input/output here, uncapped (the
     * blocks scroll rather than truncating).
     */
    _renderToolDetailContent() {
        return html `
      <div class="tool-detail">
        <div class="tool-detail__section">
          <button
            type="button"
            class="tool-detail__section-head"
            aria-expanded=${this._toolDetailInputOpen}
            @click=${this._handleToggleToolDetailInput}
          >
            <span class=${classMap({
            'tool-detail__caret': true,
            'tool-detail__caret--open': this._toolDetailInputOpen,
        })}>›</span>
            <span class="tool-detail__section-title">Input</span>
            <span
              class="tool-detail__copy"
              role="button"
              @click=${this._handleCopyToolDetailInput}
            >
              ${this._toolDetailCopied === 'input'
            ? html `
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                `
            : html `
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      x="9"
                      y="9"
                      width="13"
                      height="13"
                      rx="2"
                    />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                `}
            </span>
          </button>
          ${this._toolDetailInputOpen
            ? html `<pre class="tool-detail__code">${this._formatToolJson(this._toolDetail.use.data?.toolInput) || '(no input)'}</pre>`
            : nothing}
        </div>
        <div class="tool-detail__section">
          <button
            type="button"
            class="tool-detail__section-head"
            aria-expanded=${this._toolDetailOutputOpen}
            @click=${this._handleToggleToolDetailOutput}
          >
            <span class=${classMap({
            'tool-detail__caret': true,
            'tool-detail__caret--open': this._toolDetailOutputOpen,
        })}>›</span>
            <span class="tool-detail__section-title">Output</span>
            <span
              class="tool-detail__copy"
              role="button"
              @click=${this._handleCopyToolDetailOutput}
            >
              ${this._toolDetailCopied === 'output'
            ? html `
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                `
            : html `
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      x="9"
                      y="9"
                      width="13"
                      height="13"
                      rx="2"
                    />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                `}
            </span>
          </button>
          ${this._toolDetailOutputOpen
            ? html `<pre class="tool-detail__code">${this._formatToolJson(this._toolDetail.result?.data?.toolResultString) || '(no output yet)'}</pre>`
            : nothing}
        </div>
      </div>
    `;
    }
    /** The Debug view's body — telemetry scopes and the per-iteration table. */
    _renderDebugContent() {
        return html `
      ${this._telemetry ? html `
        <div class="debug-section">
          <div class="debug-section__label">Context</div>
          <div class="debug-gauge">
            <div class="debug-gauge__bar">
              <div
                class="debug-gauge__fill"
                style=${styleMap({ width: `${Math.min(100, Math.max(0, this._telemetry.contextUsedPercent || 0))}%` })}
              ></div>
            </div>
            <span class="debug-gauge__text">
              ${this._fmtTokens(this._telemetry.currentContextTokens)} / ${this._fmtTokens(this._telemetry.contextWindowTokens)}
              · ${this._telemetry.contextUsedPercent ?? 0}%
            </span>
          </div>
        </div>

        <div class="debug-section">
          <div class="debug-section__label">This turn</div>
          <div class="debug-metrics">
            <span class="debug-metrics__key">Input</span>
            <span class="debug-metrics__val">${this._fmtTokens(Math.max(0, (this._telemetry.turnInputTokens || 0) - this._getTurnCacheWriteTokens()))}</span>
            <span class="debug-metrics__key">Output</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._telemetry.turnOutputTokens)}</span>
            <span class="debug-metrics__key">Cache read</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._telemetry.turnCacheReadTokens)}</span>
            <span class="debug-metrics__key">Cache write</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._getTurnCacheWriteTokens())}</span>
            <span class="debug-metrics__key">Hit rate</span>
            <span class="debug-metrics__val">${this._telemetry.turnHitRate ?? '—'}%</span>
            <span class="debug-metrics__key">Total tokens</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._telemetry.turnTotalTokens)}</span>
            <span class="debug-metrics__key">Reported total</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._telemetry.turnReportedTotalTokens)}</span>
            <span class="debug-metrics__key">Cost</span>
            <span class="debug-metrics__val debug-cost">${this._fmtCost(this._telemetry.turnCostUsd)}</span>
            <span class="debug-metrics__key">${this._getCustomerPriceLabel()}</span>
            <span class="debug-metrics__val debug-cost">${this._fmtPrice(this._telemetry.turnCustomerUsd)}</span>
            <span class="debug-metrics__key">${this._getMarkupPriceLabel()}</span>
            <span class="debug-metrics__val debug-cost">${this._fmtPrice(this._telemetry.turnMarkupCustomerUsd)}</span>
            ${this._renderMarkupBreakdown(this._telemetry.turnMarkupInputUsd, this._telemetry.turnMarkupOutputUsd, this._telemetry.turnMarkupCacheReadUsd, this._telemetry.turnMarkupCacheWriteUsd)}
          </div>
        </div>

        <div class="debug-section">
          <div class="debug-section__label">Lifetime</div>
          <div class="debug-metrics">
            <span class="debug-metrics__key">Input</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._telemetry.conversationInputTokens)}</span>
            <span class="debug-metrics__key">Output</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._telemetry.conversationOutputTokens)}</span>
            <span class="debug-metrics__key">Cache read</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._telemetry.conversationCacheReadTokens)}</span>
            <span class="debug-metrics__key">Cache write</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._telemetry.conversationCacheWriteTokens)}</span>
            <span class="debug-metrics__key">Total tokens</span>
            <span class="debug-metrics__val">${this._fmtTokens(this._telemetry.conversationTotalTokens)}</span>
            <span class="debug-metrics__key">Cost so far</span>
            <span class="debug-metrics__val debug-cost">${this._fmtCost(this._telemetry.conversationCostUsd)}</span>
            <span class="debug-metrics__key">${this._getCustomerPriceLabel()} so far</span>
            <span class="debug-metrics__val debug-cost">${this._fmtPrice(this._telemetry.conversationCustomerUsd)}</span>
            <span class="debug-metrics__key">${this._getMarkupPriceLabel()} so far</span>
            <span class="debug-metrics__val debug-cost">${this._fmtPrice(this._telemetry.conversationMarkupCustomerUsd)}</span>
            ${this._renderMarkupBreakdown(this._telemetry.conversationMarkupInputUsd, this._telemetry.conversationMarkupOutputUsd, this._telemetry.conversationMarkupCacheReadUsd, this._telemetry.conversationMarkupCacheWriteUsd)}
          </div>
        </div>
          ` : html `
            <div class="debug-empty">No telemetry yet — send a message to populate.</div>
          `}

      ${this._iterationLog.length ? html `
        <div class="debug-section">
          <div class="debug-section__label">Iterations (this turn)</div>
          <div class="debug-iterations-scroll">
            <table class="debug-iterations">
              <thead>
                <tr>
                  <th>#</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Cache R</th>
                  <th>Cache W</th>
                  <th>Hit</th>
                  <th>Total</th>
                  <th>Reported</th>
                  <th>Cost</th>
                  <th>Price</th>
                  <th>Markup $</th>
                  <th>Ckpt</th>
                </tr>
              </thead>
              <tbody>
                ${this._iterationLog.map((it) => {
            const ckpt = this._cacheCheckpoints[it.iteration];
            return html `
                    <tr>
                      <td>${it.iteration}</td>
                      <td>${this._fmtTokens(it.iterationInput)}</td>
                      <td>${this._fmtTokens(it.iterationOutput)}</td>
                      <td>${this._fmtTokens(it.iterationCacheRead)}</td>
                      <td>${this._fmtTokens(it.iterationCacheWrite)}</td>
                      <td>${it.iterationHitRate ?? '—'}%</td>
                      <td>${this._fmtTokens(it.iterationTotalTokens)}</td>
                      <td>${this._fmtTokens(it.reportedTotalTokens)}</td>
                      <td class="debug-cost">${this._fmtCost(it.iterationCostUsd)}</td>
                      <td class="debug-cost">${this._fmtPrice(it.iterationCustomerUsd)}</td>
                      <td class="debug-cost">${this._fmtPrice(it.iterationMarkupCustomerUsd)}</td>
                      <td>${ckpt ? ckpt.mode : '—'}</td>
                    </tr>
                      `;
        })}
              </tbody>
            </table>
          </div>
        </div>
          ` : nothing}
    `;
    }
    render() {
        // Debug affordances (drawer toggle, drawer, /test-* options) require the
        // inline variant AND the server-side debug flag from aiFrameworkConfig.
        const isDebugEnabled = this.variant === 'inline' && this._aiFrameworkConfig.debugTelemetryEnabled === true;
        // Push the content over by the open drawer's width so the drawer sits beside
        // the content rather than overlaying it. One consistent (resizable) width for
        // both views. Only pushes while the drawer is open, and only inline — the
        // floating drawer fills the panel over the transcript, so it must not push.
        const contentPadding = {};
        if (this._drawerOpen && this.variant === 'inline') {
            contentPadding.paddingRight = `${this._drawerWidth}px`;
        }
        // Group the transcript into runs so an agent turn (its reasoning/tool/text/
        // decision messages, which stream in as separate entries) renders inside ONE
        // wrapper. The scroll-to-top runway goes on that wrapper, so everything the
        // turn appends fills the SAME tall element instead of stacking as siblings
        // below it (which is what stranded tool lines below the fold). A user message
        // is its own group and is unaffected.
        const messageGroups = [];
        this.messages.forEach((msg) => {
            const lastGroup = messageGroups[messageGroups.length - 1];
            if (msg.type === 'user' || !lastGroup || lastGroup[0].type === 'user') {
                messageGroups.push([msg]);
            }
            else {
                lastGroup.push(msg);
            }
        });
        return html `
      ${(this.hideToggle || this.variant === 'inline') ? nothing : html `
        <button
          class=${classMap({
            'toggle': true,
            'toggle--hidden': this.opened,
        })}
          @click=${this._handleToggle}
          title="Open chat"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            class="toggle-icon"
          >
            <path d="M12 2C6.48 2 2 6.03 2 11c0 2.61 1.19 4.94 3.05 6.55L4 22l4.8-2.4C9.82 19.85 10.88 20 12 20c5.52 0 10-4.03 10-9S17.52 2 12 2z"/>
            <path d="M8 11h.01M12 11h.01M16 11h.01" stroke-width="2.5"/>
          </svg>
        </button>
      `}

      <div
        class=${classMap({
            'panel': true,
            'panel--opened': this.opened,
            'panel--resizing-drawer': this._isDrawerResizing,
        })}
        style=${styleMap(contentPadding)}
        @dragenter=${this._handleDragEnter}
        @dragover=${this._handleDragOver}
        @dragleave=${this._handleDragLeave}
        @drop=${this._handleDrop}
      >
        ${this._isDraggingFile ? html `
          <div class="drop-overlay">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
              />
            </svg>
            <span>Drop images to attach</span>
          </div>
        ` : nothing}
        ${this.variant === 'inline' ? nothing : html `
          <div
            class="resize resize--n"
            data-dir="n"
            @mousedown=${this._handleResizeMouseDown}
          ></div>
          <div
            class="resize resize--s"
            data-dir="s"
            @mousedown=${this._handleResizeMouseDown}
          ></div>
          <div
            class="resize resize--w"
            data-dir="w"
            @mousedown=${this._handleResizeMouseDown}
          ></div>
          <div
            class="resize resize--e"
            data-dir="e"
            @mousedown=${this._handleResizeMouseDown}
          ></div>
          <div
            class="resize resize--nw"
            data-dir="nw"
            @mousedown=${this._handleResizeMouseDown}
          ></div>
          <div
            class="resize resize--ne"
            data-dir="ne"
            @mousedown=${this._handleResizeMouseDown}
          ></div>
          <div
            class="resize resize--sw"
            data-dir="sw"
            @mousedown=${this._handleResizeMouseDown}
          ></div>
          <div
            class="resize resize--se"
            data-dir="se"
            @mousedown=${this._handleResizeMouseDown}
          ></div>
        `}
        <div class="header" @mousedown=${this._handleHeaderMouseDown}>
          <div class="header-left">
            <div class="header-info">
              <span class="header-title">${this._conversationTitle || this.label}</span>
            </div>
          </div>
          <div class="header-actions">
            ${this.variant === 'floating' ? html `
              <button
                class="header-action"
                @click=${this._handleNewConversation}
                title="New conversation"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.75"
                  stroke="currentColor"
                  class="header-action-icon"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                  />
                </svg>
              </button>
              <button
                class="header-action"
                @click=${() => this._toggleDrawer('history')}
                title="Conversations"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="header-action-icon"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </button>
            ` : nothing}
            <button
              class="header-action"
              @click=${() => this._toggleDrawer('settings')}
              title="Settings"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="header-action-icon"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                />
              </svg>
            </button>
            ${isDebugEnabled ? html `
              <button
                class="header-action"
                @click=${() => this._toggleDrawer('debug')}
                title="Debug telemetry"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="header-action-icon"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6"
                  />
                </svg>
              </button>
            ` : nothing}
            ${this.variant === 'inline' ? nothing : html `
              <button
                class="header-action"
                @click=${this._handleToggle}
                title="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="header-action-icon"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </button>
            `}
          </div>
        </div>

        <div
          class="messages"
          @scroll=${this._handleMessagesScroll}
          @wheel=${this._handleMessagesWheel}
          @touchmove=${this._handleMessagesTouchMove}
          @pointerdown=${this._handleMessagesPointerDown}
        >
          <div class="messages-inner">
            ${this.messages.length === 0 ? html `
              <div class="empty">
                <span class="empty-text">What can I help you with?</span>
                ${this._suggestedActions.length ? html `
                  <div class="empty-suggestions">
                    ${this._suggestedActions.map((action) => html `
                      <button
                        type="button"
                        class="empty-suggestions__chip"
                        @click=${() => this._handleSuggestedActionClick(action)}
                      >${action.label}</button>
                    `)}
                  </div>
                ` : nothing}
              </div>
            ` : nothing}
            ${messageGroups.map((group) => {
            // A user group is a single message rendered bare. An agent group wraps
            // its items in one `.agent-turn` so the scroll-to-top runway sits on
            // that wrapper and the whole turn fills it (no stranded siblings).
            const items = group.map((msg) => {
                const index = this.messages.indexOf(msg);
                // Skip an empty assistant placeholder (no content, no reasoning) —
                // _send and _ensureAssistantBubble append these to stream into,
                // but between tool calls with no text they stay empty. An invisible
                // .message div still occupies a flex-gap slot, doubling the spacing
                // between tool lines. Keep it only while it's the actively-streaming
                // last message (it shows the typing dots).
                if (msg.type === 'assistant'
                    && !msg.content
                    && !msg.reasoning
                    && !(this._isStreaming && msg === this.messages[this.messages.length - 1])) {
                    return nothing;
                }
                // Tool visibility is user-controlled ("Show tools" in the settings
                // panel). When off, drop the whole tool row so it doesn't leave an
                // empty flex-gap slot in the transcript.
                // A tool_result renders nothing on its own — its tool_use line pulls
                // it in (matched by data.toolUseId) and renders it under the ⎿ cliff.
                // The exception is an offer_skill result: its result string is the
                // offer JSON ({skills, canInstall, note}) the model read too, and the
                // skills in it render as an Install box, even with tool rows hidden,
                // since installing is the user's action rather than tool activity.
                // Parsed here, where statements are allowed; an offer_skill error
                // result isn't JSON and renders like any other tool result.
                let offeredSkills = [];
                if (msg.type === 'tool_result' && msg.data?.toolName === 'offer_skill') {
                    try {
                        offeredSkills = JSON.parse(msg.data.toolResultString).skills || [];
                    }
                    catch { /* not an offer */ }
                }
                if ((msg.type === 'tool_use' || msg.type === 'tool_result') && !this._showTools && !offeredSkills.length) {
                    return nothing;
                }
                if (msg.type === 'tool_result' && !offeredSkills.length) {
                    return nothing;
                }
                // A tool_use line (Claude-CLI style): header "› ToolName(input)" and,
                // when "Show tool inputs" is on, the result under a ⎿ cliff pulled
                // from the next tool_result with the same toolUseId. Precompute the
                // result and the two display strings (they need statements — JSON
                // reformat, truncation — that can't live in a template expression).
                let result;
                let inputSummary = '';
                let resultPreview = '';
                let resultRemainder = 0;
                if (msg.type === 'tool_use') {
                    if (this.messages[index + 1]?.type === 'tool_result'
                        && this.messages[index + 1].data?.toolUseId === msg.data?.toolUseId) {
                        result = this.messages[index + 1].data;
                    }
                    if (this._showToolInputs && msg.data?.toolInput) {
                        inputSummary = String(msg.data.toolInput).replace(/\s+/g, ' ').trim();
                        if (inputSummary.length > 80) {
                            inputSummary = `${inputSummary.slice(0, 79)}…`;
                        }
                    }
                    if (this._showToolInputs && result?.toolResultString) {
                        let formatted;
                        try {
                            formatted = JSON.stringify(JSON.parse(result.toolResultString), null, 2);
                        }
                        catch {
                            formatted = String(result.toolResultString);
                        }
                        // Cap the inline preview by BOTH lines and characters. Line-only
                        // truncation blows up on results whose first line is huge (e.g. a
                        // minified-JSON blob or a stashed-response marker with a 30k-char
                        // opening line) — 3 lines then still dumps tens of thousands of
                        // chars into the transcript. Char-cap first (content-blind, like
                        // the backend chokepoint), then keep at most 3 lines of that.
                        const lines = formatted.split('\n');
                        resultPreview = lines.slice(0, 3).join('\n');
                        if (resultPreview.length > 200) {
                            resultPreview = `${resultPreview.slice(0, 200)}…`;
                        }
                        resultRemainder = Math.max(0, lines.length - 3);
                    }
                }
                // A resolved-decision line (Rail 3) — reads "Approved Create AI Tool"
                // / "Denied …" / "Always allowed …". Precompute the verb and the
                // friendly tool names (an if/else and a map — not template exprs).
                let decisionVerb = 'Approved';
                let decisionTools = [];
                if (msg.type === 'action_decision') {
                    if (msg.data?.outcome === 'denied') {
                        decisionVerb = 'Denied';
                    }
                    else if (msg.data?.outcome === 'alwaysAllowed') {
                        decisionVerb = 'Always allowed';
                    }
                    decisionTools = (msg.data?.tools || []).map((t) => t.displayName || t.toolName);
                }
                // A tool line and a decision line ARE assistant-side messages —
                // render them inside the same .message wrapper as everything else so
                // they share its spacing/structure, not as separate element types.
                return html `
                  <div class=${classMap({
                    'message': true,
                    'message--user': msg.type === 'user',
                    'message--assistant': msg.type !== 'user',
                })}>
                    ${msg.type === 'tool_use' ? html `
                      <div class="tool-activity-block">
                        <button
                          type="button"
                          class=${classMap({
                    'tool-activity': true,
                    'tool-activity--error': result?.toolResultStatus === 'error',
                })}
                          @click=${() => this._openToolDetail(msg)}
                        >
                          <span class="tool-activity__chevron">›</span>
                          <span class="tool-activity__label">${msg.data?.displayName || msg.data?.toolName || 'Tool'}</span>
                          ${inputSummary ? html `<span class="tool-activity__input">(${inputSummary})</span>` : nothing}
                        </button>
                        ${this._showToolInputs && result?.toolResultString ? html `
                          <button
                            type="button"
                            class=${classMap({
                    'tool-activity__result': true,
                    'tool-activity__result--error': result.toolResultStatus === 'error',
                })}
                            @click=${() => this._openToolDetail(msg)}
                          >
                            <span class="tool-activity__connector">⎿</span>
                            <pre class="tool-activity__result-body">${resultPreview}${resultRemainder > 0
                    ? html `\n<span class="tool-activity__more">… +${resultRemainder} lines</span>`
                    : nothing}</pre>
                          </button>
                        ` : nothing}
                      </div>
                    ` : nothing}
                    ${offeredSkills.length ? html `
                      <div
                        class="confirmation confirmation--inline skill-offer"
                        role="group"
                        aria-label="Install skills"
                      >
                        <div class="confirmation__title">
                          ${offeredSkills.length > 1 ? 'Install These Skills?' : `Install "${offeredSkills[0].name}" Skill?`}
                        </div>
                        ${offeredSkills.length > 1 ? html `
                          <ol class="confirmation__list">
                            ${offeredSkills.map((skill) => html `
                              <li class="confirmation__list-item">
                                <span class="confirmation__list-name">${skill.name}</span>
                                ${skill.summary ? html `
                                  <span class="confirmation__list-desc">${skill.summary}</span>
                                ` : nothing}
                                <span class="confirmation__list-desc">
                                  <strong>Price:</strong>
                                  ${skill.priceLabel}
                                </span>
                              </li>
                            `)}
                          </ol>
                        ` : html `
                          ${offeredSkills[0].summary ? html `
                            <p class="confirmation__message">${offeredSkills[0].summary}</p>
                          ` : nothing}
                          <p class="confirmation__message">
                            <strong>Price:</strong>
                            ${offeredSkills[0].priceLabel}
                          </p>
                        `}
                        <div class="confirmation__actions">
                          ${msg.data.skillOfferOutcome === 'installed' ? html `
                            <span class="skill-offer__installed">Installed</span>
                          ` : msg.data.skillOfferOutcome === 'declined' ? html `
                            <span class="skill-offer__declined">Declined</span>
                          ` : html `
                            <kr-button
                              variant="flat"
                              color="primary"
                              size="small"
                              @click=${(e) => this._handleOfferedSkillsInstall(msg, e)}
                            >Install</kr-button>
                            <kr-button
                              variant="outline"
                              color="secondary"
                              size="small"
                              @click=${() => this._handleOfferedSkillsDecline(msg)}
                            >Not now</kr-button>
                          `}
                        </div>
                      </div>
                    ` : nothing}
                    ${msg.type === 'action_decision' ? html `
                      <div class=${classMap({
                    'decision': true,
                    'decision--denied': msg.data?.outcome === 'denied',
                })}>
                        <span class="decision__icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            ${msg.data?.outcome === 'denied'
                    ? html `<path d="M18 6 6 18M6 6l12 12" />`
                    : html `<path d="M20 6 9 17l-5-5" />`}
                          </svg>
                        </span>
                        <span class="decision__label">${decisionVerb}${decisionTools.length ? ' ' + decisionTools.join(', ') : nothing}</span>
                      </div>
                    ` : nothing}
                    ${msg.reasoning ? (this._reasoningAsMessage ? html `
                      <div class="message-content">${msg.reasoning}</div>
                    ` : html `
                      <details class="reasoning">
                        <summary>Thinking</summary>
                        <div class="reasoning-content">${msg.reasoning}</div>
                      </details>
                    `) : nothing}
                    ${msg.conversationFiles && msg.conversationFiles.length ? html `
                      <div class="attachments">
                        ${msg.conversationFiles.map((file) => {
                    const fileName = file.niceName || file.name || 'File';
                    // A stored file is an image by its MIME when it has one, else
                    // by its extension.
                    let isImage = IMAGE_EXTENSIONS.includes(this._getFileExtension(fileName));
                    if (file.mimeType) {
                        isImage = file.mimeType.startsWith('image/');
                    }
                    // An image shows its thumbnail; everything else shows a
                    // file-type tile. Both open the previewer on click — it renders
                    // images, video, PDFs, and text inline, and shows a download
                    // tile for the rest (Office formats), so a click always does
                    // something useful.
                    if (isImage) {
                        return html `
                              <button
                                class="attachment"
                                type="button"
                                title=${file.niceName || 'View image'}
                                @click=${() => this._handleFileOpen(file)}
                              >
                                <img
                                  src=${file.previewUrl || `${KRHttp.getBaseUrl()}/api/system/conversationFile/${file.conversationFileID}/view`}
                                  alt=${file.niceName || ''}
                                />
                              </button>
                            `;
                    }
                    return html `
                            <button
                              class="attachment attachment--doc attachment--openable"
                              type="button"
                              title=${fileName}
                              @click=${() => this._handleFileOpen(file)}
                            >
                              <span class="attachment__type">${this._getFileExtension(fileName) || 'File'}</span>
                              <span class="attachment__name">${fileName}</span>
                            </button>
                          `;
                })}
                      </div>
                    ` : nothing}
                    ${msg.content ? html `
                      <div class="message-content">
                        ${msg.type === 'user'
                    ? this._renderUserContent(msg.content)
                    : unsafeHTML(marked.parse(msg.content, { breaks: true }))}
                      </div>
                    ` : nothing}
                    ${msg.type === 'assistant' && this._isStreaming && msg === this.messages[this.messages.length - 1] ? html `
                      <div class="typing">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    ` : nothing}
                    ${msg.content ? html `
                      <button
                        class="message-copy"
                        title=${this._copiedIndex === index ? 'Copied' : 'Copy'}
                        @click=${() => this._handleCopyMessage(msg.content, index)}
                      >
                        ${this._copiedIndex === index ? html `
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        ` : html `
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect
                              x="9"
                              y="9"
                              width="13"
                              height="13"
                              rx="2"
                            />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        `}
                      </button>
                    ` : nothing}
                  </div>
                `;
            });
            // The inline confirmation is this turn's final step, so it renders
            // INSIDE the agent-turn wrapper (not as a sibling) — it sits right
            // after the last message and the runway falls below it naturally.
            // Only on the last group, which is the current (paused) turn.
            const isLastGroup = group === messageGroups[messageGroups.length - 1];
            const showConfirmation = isLastGroup && this._pendingActions.length && this._inlineConfirm;
            if (group[0].type === 'user') {
                // The model can pause for confirmation before writing any text. The
                // user's message is then the last group and there is no agent turn to
                // hold the card, so give the card its own wrapper right after it.
                return html `
                  ${items}
                  ${showConfirmation ? html `
                    <div class="agent-turn">
                      ${this._renderConfirmation()}
                    </div>
                  ` : nothing}
                `;
            }
            // Keep the "thinking" indicator visible while a tool is running or
            // the model is between steps with no text yet. The trailing empty
            // assistant bubble shows the dots itself; but once a tool line is
            // the last message, that bubble is gone — so render the dots here
            // at the end of the live turn instead. Not while paused for a
            // confirmation (the ask replaces the indicator).
            const lastMsg = this.messages[this.messages.length - 1];
            const showTyping = isLastGroup
                && this._isStreaming
                && !this._pendingActions.length
                && lastMsg?.type !== 'assistant';
            return html `
                    <div class="agent-turn">
                      ${items}
                      ${showTyping ? html `
                        <div class="typing">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                    ` : nothing}
                      ${showConfirmation ? this._renderConfirmation() : nothing}
                    </div>
              `;
        })}
              </div>
              </div>

              ${this._pendingActions.length && !this._inlineConfirm ? this._renderConfirmation() : nothing}

              ${this._error ? html `
                <div class="error">
                  <span>${this._error}</span>
                  <button class="error-dismiss" @click=${this._handleErrorDismiss}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      class="error-dismiss-icon"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M6 18 18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
        ` : this._contextLimitReached ? html `
          <div class="context-limit" role="alert">
            ${this._contextLimitMessage
            || 'This conversation has reached its limit. Please start a new one to continue.'}
          </div>
        ` : this._contextWarning ? html `
          <div class="context-warning" role="status">
            <span>${this._contextWarning}</span>
            <button
              class="context-warning-dismiss"
              title="Dismiss"
              @click=${this._handleContextWarningDismiss}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="context-warning-dismiss-icon"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        ` : nothing}

              <div class="footer">
              ${this._pendingConversationFiles.length ? html `
                <div class="composer-attachments">
                  ${repeat(this._pendingConversationFiles, (p) => p.localId, (p) => html `
                    <div
                      class=${classMap({
            chip: true,
            'chip--doc': !p.previewUrl,
        })}
                      title=${p.name}
                    >
                      <div
                        class=${classMap({
            'chip-body': true,
            'chip-body--image': Boolean(p.previewUrl),
        })}
                      >
                        ${p.previewUrl
            ? html `<img src=${p.previewUrl} alt=${p.name} />`
            : html `
                            <span class="chip-type">${this._getFileExtension(p.name) || 'File'}</span>
                            <span class="chip-name">${p.name}</span>
                      `}
                      </div>
                      <button
                        class="chip-remove"
                        title="Remove"
                        @click=${() => this._handlePendingRemove(p.localId)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.75"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M6 18 18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
              `)}
                </div>
          ` : nothing}
              <input
              class="file-input"
              type="file"
              accept=${ACCEPTED_FILE_EXTENSIONS.map((ext) => '.' + ext).join(',')}
              multiple
              hidden
              @change=${this._handleFileInputChange}
              />
              ${this._skillMenuOpened ? this._renderSkillMenu() : nothing}
              <div class="input-wrapper">
              <button
              class="input-plus"
              @click=${this._handlePlusClick}
              >+</button>
              <textarea
              class="input"
              .value=${this._inputValue}
              placeholder=${this.placeholder}
              rows="1"
              @input=${this._handleInputChange}
              @keydown=${this._handleKeyDown}
              @paste=${this._handlePaste}
              ></textarea>
              ${this._renderModeSelector()}
              ${window.SpeechRecognition || window.webkitSpeechRecognition ? html `
                ${this._isDictating ? html `
                  <button
                    class="mic-stop"
                    title="Stop dictation"
                    aria-label="Stop dictation"
                    @click=${this._handleMicClick}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      class="mic-stop-icon"
                      aria-hidden="true"
                    >
                      <rect
                        x="6"
                        y="6"
                        width="12"
                        height="12"
                        rx="2"
                      />
                    </svg>
                  </button>
              ` : html `
                <button
                  class="mic"
                  title="Dictate a message"
                  aria-label="Dictate a message"
                  @click=${this._handleMicClick}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="mic-icon"
                    aria-hidden="true"
                  >
                    <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"/>
                    <path d="M19 11a7 7 0 0 1-14 0"/>
                    <path d="M12 18v3"/>
                  </svg>
                </button>
              `}
            ` : nothing}
              ${this._isStreaming ? html `
                <button
                  class="stop"
                  @click=${this._handleStop}
                  title="Stop generating"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    class="stop-icon"
                  >
                    <rect
                      x="6"
                      y="6"
                      width="12"
                      height="12"
                      rx="2"
                    />
                  </svg>
                </button>
            ` : html `
              <button
                class=${classMap({
            'send': true,
            'send--disabled': (!this._inputValue.trim() && !this._pendingConversationFiles.length) || this._pendingActions.length > 0,
        })}
                @click=${this._handleSubmit}
                title=${this._pendingActions.length ? 'Approve or deny the pending action first' : 'Send message'}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  class="send-icon"
                >
                  <path d="M12 19V5M5 12l7-7 7 7"/>
                </svg>
              </button>
            `}
              </div>
              </div>
              ${this._skillBrowseOpened ? this._renderSkillBrowse() : nothing}
              ${this._skillInstallConfirm ? html `
                <kr-dialog
                  opened
                  label="Install Paid Skill"
                  width="480px"
                  @close=${this._handleSkillInstallCancel}
                >
                  <kr-dialog-content>
                    <div class="skill-install-confirm">
                      <p>
                        <strong>${this._skillInstallConfirm.name}</strong> is a paid skill. Installing it will
                        charge your account <strong>${this._skillInstallConfirm.priceLabel}</strong>${this._skillInstallConfirm.minimumLabel && html `, with a <strong>${this._skillInstallConfirm.minimumLabel}</strong>`}.
                      </p>
                      <p>Please confirm you would like to proceed.</p>
                    </div>
                  </kr-dialog-content>
                  <kr-dialog-footer>
                    <kr-button color="secondary" @click=${this._handleSkillInstallCancel}>Cancel</kr-button>
                    <kr-button
                      variant="flat"
                      color="primary"
                      @click=${this._handleSkillInstallConfirm}
                    >Install</kr-button>
                  </kr-dialog-footer>
                </kr-dialog>
              ` : nothing}
              ${this._skillOfferConfirm ? html `
                <kr-dialog
                  opened
                  label="Install Paid Skills"
                  width="480px"
                  @close=${this._handleSkillOfferCancel}
                >
                  <kr-dialog-content>
                    <div class="skill-install-confirm">
                      <p>These are paid skills. Installing them will charge your account:</p>
                      <ul class="skill-install-confirm__list">
                        ${JSON.parse(this._skillOfferConfirm.data.toolResultString).skills.filter((skill) => skill.billable).map((skill) => html `
                          <li>
                            <strong>${skill.name}</strong>
                            ${skill.priceLabel}${skill.minimumLabel && html `, with a <strong>${skill.minimumLabel}</strong>`}
                          </li>
                        `)}
                      </ul>
                      <p>Please confirm you would like to proceed.</p>
                    </div>
                  </kr-dialog-content>
                  <kr-dialog-footer>
                    <kr-button color="secondary" @click=${this._handleSkillOfferCancel}>Cancel</kr-button>
                    <kr-button
                      variant="flat"
                      color="primary"
                      @click=${this._handleSkillOfferConfirm}
                    >Install</kr-button>
                  </kr-dialog-footer>
                </kr-dialog>
              ` : nothing}
              ${this._renderDrawer()}
              </div>
              `;
    }
}
KRChatbot.styles = css `
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    /* Shadow DOM blocks page selectors but not inherited properties, so every
       inheritable text property the host page sets on body would otherwise flow
       into the chatbot. Pin each one here so the chatbot looks the same on any
       page it is embedded in. */
    :host {
      display: block;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 400;
      font-style: normal;
      line-height: 1.6;
      letter-spacing: normal;
      word-spacing: normal;
      text-align: left;
      text-transform: none;
      text-indent: 0;
      text-shadow: none;
      white-space: normal;
      color: var(--kr-chatbot-text);

      --kr-chatbot-primary: #163052;
      --kr-chatbot-primary-hover: #1e4068;
      --kr-chatbot-bg: #ffffff;
      --kr-chatbot-border: #d9d9d9;
      --kr-chatbot-text: #1a1a2e;
      --kr-chatbot-text-secondary: #64668b;
      --kr-chatbot-user-bg: #e9e9e980;
      --kr-chatbot-error-bg: #fef2f2;
      --kr-chatbot-error-text: #dc2626;
      --kr-chatbot-success: #00b894;
      --kr-chatbot-toggle-size: 56px;
      --kr-chatbot-panel-width: 440px;
      --kr-chatbot-panel-height: 620px;
    }

    :host {
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 10000;
    }

    /* Toggle button */
    .toggle {
      width: var(--kr-chatbot-toggle-size);
      height: var(--kr-chatbot-toggle-size);
      border-radius: 18px;
      background: var(--kr-chatbot-primary);
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 24px rgba(22, 48, 82, 0.4);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .toggle:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 32px rgba(22, 48, 82, 0.5);
    }

    .toggle--hidden {
      transform: scale(0);
      opacity: 0;
      pointer-events: none;
    }

    .toggle-icon {
      width: 26px;
      height: 26px;
    }

    /* Panel */
    .panel {
      display: none;
      flex-direction: column;
      background: var(--kr-chatbot-bg);
      overflow: hidden;
      position: absolute;
      bottom: 0;
      right: 0;
      width: var(--kr-chatbot-panel-width);
      height: var(--kr-chatbot-panel-height);
      max-height: calc(100vh - 72px);
      border-radius: 16px;
      box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08), 0 2px 12px rgba(0, 0, 0, 0.04);
      border: 1px solid rgba(0, 0, 0, 0.08);
      animation: chatbot-panel-in 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      transform-origin: bottom right;
    }

    .panel--opened {
      display: flex;
    }

    /* Inline mode: fill the parent container instead of floating bottom-right. */
    :host([variant='inline']) {
      position: static;
      bottom: auto;
      right: auto;
      z-index: auto;
      height: 100%;
    }

    :host([variant='inline']) .panel {
      position: relative;
      bottom: auto;
      right: auto;
      width: 100%;
      height: 100%;
      max-height: none;
      border-radius: 0;
      box-shadow: none;
      border: none;
      animation: none;
      /* Reserve space for the drawer by animating padding-right in sync with the
         drawer's slide, so the chat column shrinks rather than being overlaid. */
      transition: padding-right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* Track the cursor 1:1 while dragging the drawer's resize handle. */
    :host([variant='inline']) .panel--resizing-drawer {
      transition: none;
    }

    @keyframes chatbot-panel-in {
      from {
        opacity: 0;
        transform: scale(0.92) translateY(20px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }

    /* Header */
    .header {
      padding: 0 16px 0 24px;
      height: 64px;
      background: #ffffff;
      color: #000000;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-shrink: 0;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }

    .header-info {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .header-title {
      font-size: 16px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }

    .header-action {
      width: 32px;
      height: 32px;
      background: transparent;
      border: none;
      color: rgb(0 0 0 / 70%);
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s;
    }

    .header-action:hover {
      background: rgba(0, 0, 0, 0.05);
      color: #000000;
    }

    .header-action-icon {
      width: 20px;
      height: 20px;
    }

    .settings-section {
      padding: 14px 20px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    .settings-section:last-child {
      border-bottom: none;
    }

    .settings-section__title {
      font-size: 14px;
      font-weight: 600;
      color: #000000;
      margin-bottom: 12px;
    }

    .settings-row {
      padding: 6px 0;
    }

    /* Tool detail view — the full input/output for one tool call. */
    .tool-detail {
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding: 16px 20px;
    }

    .tool-detail__section {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 0;
    }

    /* Clickable section header — toggles the code block below. Button chrome is
       stripped so it reads as a heading row. */
    .tool-detail__section-head {
      display: flex;
      align-items: center;
      gap: 8px;
      appearance: none;
      background: none;
      border: none;
      width: 100%;
      font-family: inherit;
      text-align: left;
      cursor: pointer;
      border-radius: 4px;
    }

    .tool-detail__section-head:focus-visible {
      outline: 2px solid var(--kr-chatbot-primary);
      outline-offset: 2px;
    }

    /* Disclosure caret — the same "›" used on tool lines; rotates down when open. */
    .tool-detail__caret {
      flex-shrink: 0;
      font-size: 16px;
      color: rgba(0, 0, 0, 0.55);
      transition: transform 0.15s ease;
    }

    .tool-detail__caret--open {
      transform: rotate(90deg);
    }

    .tool-detail__section-title {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #000000;
    }

    /* Copy icon-button — same treatment as the per-message copy button. */
    .tool-detail__copy {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      padding: 0;
      background: none;
      border: none;
      border-radius: 6px;
      color: #000000;
      cursor: pointer;
      transition: background-color 0.15s, color 0.15s;
    }

    .tool-detail__copy:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    .tool-detail__copy svg {
      width: 14px;
      height: 14px;
    }

    /* The code block — pretty-printed JSON, shown in full (no inner scroll). The
       drawer body is the only scroll container, so long inputs/outputs extend the
       page and you scroll the panel, not a nested box. */
    .tool-detail__code {
      margin: 0;
      padding: 0 18px;
      font-family: 'SF Mono', 'Fira Code', Monaco, monospace;
      font-size: 12px;
      line-height: 1.5;
      color: #000000;
      white-space: pre-wrap;
      word-break: break-word;
    }

    /* Messages */
    /* Full-width scroll container — keeps the scrollbar at the panel edge. */
    .messages {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      scroll-behavior: smooth;
      background: white;
    }

    /* Centered, width-capped column that actually holds the messages. */
    .messages-inner {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 36px;
      padding: 32px;
      width: 100%;
      max-width: 768px;
      margin-left: auto;
      margin-right: auto;
    }

    .empty {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: var(--kr-chatbot-text-secondary);
      gap: 16px;
    }

    .empty-text {
      font-size: 14px;
      color: rgb(0 0 0 / 80%);
    }

    /* Context-aware action chips under the empty-state prompt. A row of pills the user can click to
       prefill a skill — capped small server-side so this reads as a few suggestions, not a menu. */
    .empty-suggestions {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 10px;
      max-width: 420px;
      margin-top: 4px;
    }

    .empty-suggestions__chip {
      padding: 8px 12px;
      background: rgb(0 0 0 / 6%);
      border: none;
      border-radius: 16px;
      color: #000000;
      font: inherit;
      font-size: 13px;
      line-height: 1.2;
      cursor: pointer;
      white-space: nowrap;
    }

    .empty-suggestions__chip:hover {
      background: rgb(0 0 0 / 10%);
      color: #000000;
    }

    .message {
      position: relative;
      display: flex;
      flex-direction: column;
      max-width: 100%;
      animation: chatbot-msg-in 0.3s ease-out;
      /* Reserve room for the copy button INSIDE the message box (so the button is
         part of the message's hover region — no dead zone to cross), then cancel
         that space for layout so message spacing is unchanged. */
      padding-bottom: 30px;
      margin-bottom: -30px;
    }

    /* The reserved padding overhangs into the next message; lift the hovered one
       so its copy button isn't covered by (or stealing hover from) the sibling. */
    .message:hover {
      z-index: 1;
    }

    @keyframes chatbot-msg-in {
      from {
        opacity: 0;
        transform: translateY(6px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .message--user {
      align-self: flex-end;
      align-items: flex-end;
      max-width: 85%;
    }

    .message--assistant {
      align-self: flex-start;
      gap: 24px;
    }

    /* An agent turn wraps all of its messages (reasoning, tool lines, text,
       decision) so the scroll-to-top runway can sit on the whole turn and content
       streams inside it instead of stacking as siblings below the runway. A flex
       column with the same 36px gap as .messages-inner, so item spacing is
       unchanged whether grouped or not. Full width so its children align exactly
       as before (assistant left, tool/decision left). */
    .agent-turn {
      display: flex;
      flex-direction: column;
      gap: 36px;
      width: 100%;
    }

    /* Tool-activity line — "the assistant ran X" row in the transcript. Same text
       style as any other message (black, 14px); a leading › chevron marks it as a
       step (in the same text style, not an icon). */
    /* The tool-activity line and its result preview are <button>s (they open the
       tool-detail drawer on click) — strip the native button chrome so they read
       as plain transcript text, then re-add the layout. */
    .tool-activity {
      align-self: flex-start;
      display: flex;
      align-items: baseline;
      gap: 6px;
      font-size: 14px;
      color: #000000;
      /* Keep the chevron + name + input on a single line; input truncates if long. */
      white-space: nowrap;
      /* Button reset */
      appearance: none;
      background: none;
      border: none;
      margin: 0;
      padding: 2px 4px;
      font-family: inherit;
      text-align: left;
      cursor: pointer;
      border-radius: 4px;
      max-width: 100%;
    }

    .tool-activity:focus-visible {
      outline: 2px solid var(--kr-chatbot-primary);
      outline-offset: 1px;
    }

    .tool-activity--error {
      color: var(--kr-chatbot-error-text);
    }

    /* Leading chevron — plain text in the same style as the name, so it reads as
       one label rather than an icon. */
    .tool-activity__chevron {
      flex-shrink: 0;
    }

    /* The tool name never shrinks — it's the input that truncates if the header
       runs out of room. */
    .tool-activity__label {
      flex-shrink: 0;
    }

    /* The tool input rendered inline in the header, Claude-CLI style:
       "Read(chatbot.ts)". Same color/font as the tool name — it reads as one
       label. Truncates with an ellipsis so the header stays on one line. */
    .tool-activity__input {
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }

    /* Header + result grouped as one unit so the parent .message--assistant 24px
       gap doesn't push them apart. Rows sit tight together inside. */
    .tool-activity-block {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    /* One hover for the whole tool block: hovering anywhere over the header or its
       result underlines the tool name, input, and result together as a single
       link, rather than lighting up header and result separately. */
    .tool-activity-block:hover .tool-activity__label,
    .tool-activity-block:hover .tool-activity__input,
    .tool-activity-block:hover .tool-activity__result-body {
      text-decoration: underline;
      text-decoration-color: rgba(0, 0, 0, 0.25);
    }

    /* The result block indented under the activity line with a ⎿ tree connector,
       like Claude CLI's tool output preview. Uses the normal message text style
       (14px, black) — only the ⎿ connector is muted. */
    .tool-activity__result {
      align-self: flex-start;
      display: flex;
      gap: 2px;
      /* Align the ⎿ connector's visible stroke under the first character of the
         tool name. The name is preceded by the "› " chevron (chevron width + 6px
         gap ≈ 11px), and the ⎿ glyph carries ~5.6px more internal left bearing
         than the name's first letter — so net-shift the block right to land the
         stroke under the name. */
      margin-left: 6px;
      font-size: 14px;
      color: #000000;
      /* Button reset — this preview row is also a click target for the detail view. */
      appearance: none;
      background: none;
      border: none;
      padding: 0;
      font-family: inherit;
      text-align: left;
      cursor: pointer;
    }

    .tool-activity__result:focus-visible {
      outline: 2px solid var(--kr-chatbot-primary);
      outline-offset: 1px;
      border-radius: 4px;
    }

    .tool-activity__result--error {
      color: var(--kr-chatbot-error-text);
    }

    .tool-activity__connector {
      flex-shrink: 0;
      color: rgba(0, 0, 0, 0.55);
      user-select: none;
      /* The ⎿ arm sits near the baseline, ~6px below the result's first-character
         vertical center. Nudge the glyph up so the arm tip lines up with that center. */
      transform: translateY(-6px);
    }

    .tool-activity__result-body {
      /* Small horizontal gap between the ⎿ arm and the result text so they don't
         collide (the ⎿ glyph's own arm already extends most of the way). */
      margin: 0 0 0 6px;
      white-space: pre-wrap;
      word-break: break-word;
      font: inherit;
    }

    .tool-activity__more {
      color: rgba(0, 0, 0, 0.4);
    }

    /* Decision line — the persistent record of an approve/deny (Rail 3). Uses the
       normal message text style (14px, black); a check/deny icon marks it. */
    .decision {
      align-self: flex-start;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #000000;
    }

    .decision--denied {
      color: var(--kr-chatbot-error-text);
    }

    .decision__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    .decision__icon svg {
      width: 15px;
      height: 15px;
    }

    /* anywhere, NOT break-word: both break a long unbreakable run (a pasted URL) for
       layout, but only anywhere also shrinks the element's intrinsic min-content width.
       With break-word the URL still reports its full unbroken width as the minimum,
       which sizes this shrink-to-fit bubble past .messages-inner's 768px cap and
       overflows the page horizontally. */
    .message-content {
      line-height: 1.7;
      overflow-wrap: anywhere;
    }

    .message--user .message-content {
      display: inline-block;
      background: var(--kr-chatbot-user-bg);
      color: #000000;
      padding: 10px 16px;
      border-radius: 20px;
      border-bottom-right-radius: 6px;
      font-size: 14px;
    }

    /* Highlighted leading /skill token in a sent user message. Monospace + a
       stronger color makes it read as a distinct command without a pill. */
    .message-skill {
      font-family: 'SF Mono', SFMono-Regular, ui-monospace, Menlo, Consolas, monospace;
      font-weight: 600;
      color: #b45309;
    }

    /* Per-message copy button. Sits in the message's reserved bottom padding —
       inside the hover box, so moving the cursor down onto it never leaves the
       message. Height is reserved (via the message padding) so spacing doesn't
       jump; only opacity toggles on hover. */
    .message-copy {
      position: absolute;
      bottom: 0;
      left: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      padding: 0;
      background: none;
      border: none;
      border-radius: 6px;
      color: #000000;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s, background-color 0.15s, color 0.15s;
    }

    .message--user .message-copy {
      left: auto;
      right: 0;
    }

    .message:hover .message-copy,
    .message-copy:focus-visible {
      opacity: 1;
    }

    .message-copy:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    .message-copy svg {
      width: 14px;
      height: 14px;
    }

    .message--assistant .message-content {
      background: transparent;
      color: #000000;
      padding: 0;
      font-size: 14px;
    }

    .message--assistant .message-content pre {
      background: #1e1e1e;
      color: #d4d4d4;
      padding: 12px;
      border-radius: 6px;
      overflow-x: auto;
      margin: 8px 0;
      font-size: 13px;
      line-height: 1.4;
    }

    .message--assistant .message-content code {
      font-family: 'SF Mono', 'Fira Code', Monaco, monospace;
      font-size: 13px;
    }

    .message--assistant .message-content :not(pre) > code {
      background: rgba(0, 0, 0, 0.06);
      padding: 2px 5px;
      border-radius: 3px;
    }

    .message--assistant .message-content strong {
      font-weight: 600;
    }

    .message--assistant .message-content p {
      margin: 0 0 8px 0;
    }

    .message--assistant .message-content p:last-child {
      margin-bottom: 0;
    }

    /* Reasoning */
    .reasoning {
      border-radius: 6px;
      background: rgb(244 244 244 / 80%);
      font-size: 12px;
    }

    .reasoning summary {
      cursor: pointer;
      padding: 6px 10px 6px 10px;
      color: black;
      user-select: none;
    }

    .reasoning-content {
      padding: 6px 10px 10px;
      color: black;
      white-space: pre-wrap;
      word-wrap: break-word;
    }

    .typing {
      display: flex;
      gap: 4px;
      padding: 4px 0;
    }

    .typing span {
      width: 6px;
      height: 6px;
      background: var(--kr-chatbot-text-secondary);
      border-radius: 50%;
      animation: chatbot-typing 1.4s infinite;
      opacity: 0.4;
    }

    .typing span:nth-child(2) {
      animation-delay: 0.2s;
    }

    .typing span:nth-child(3) {
      animation-delay: 0.4s;
    }

    @keyframes chatbot-typing {
      0%, 60%, 100% {
        opacity: 0.4;
        transform: translateY(0);
      }
      30% {
        opacity: 1;
        transform: translateY(-4px);
      }
    }

    /* Error */
    .error {
      padding: 8px 16px;
      background: var(--kr-chatbot-error-bg);
      color: var(--kr-chatbot-error-text);
      font-size: 13px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      flex-shrink: 0;
    }

    .error-dismiss {
      background: none;
      border: none;
      color: var(--kr-chatbot-error-text);
      cursor: pointer;
      padding: 2px;
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .error-dismiss-icon {
      width: 14px;
      height: 14px;
    }

    /* Context warning banner (user-facing, dismissible, non-blocking) */
    .context-warning {
      /* Match the message text column: 768px cap minus its 32px side padding
         (704px), and viewport minus that padding on narrower screens. */
      width: 100%;
      max-width: 735px;
      margin: 8px auto 0;
      padding: 10px 14px;
      border: 1px solid #fde68a;
      border-radius: 8px;
      background: #fffbeb;
      color: #92400e;
      font-size: 13px;
      line-height: 1.4;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      flex-shrink: 0;
    }

    .context-warning-dismiss {
      background: none;
      border: none;
      color: #92400e;
      cursor: pointer;
      padding: 2px;
      display: flex;
      align-items: center;
      flex-shrink: 0;
      opacity: 0.7;
    }

    .context-warning-dismiss:hover {
      opacity: 1;
    }

    .context-warning-dismiss-icon {
      width: 14px;
      height: 14px;
    }

    /* Context-limit notice (user-facing, blocking — steers to a new conversation) */
    .context-limit {
      /* Match the message text column: 768px cap minus its 32px side padding
         (704px), and viewport minus that padding on narrower screens. */
      width: 100%;
      max-width: 735px;
      margin: 8px auto 0;
      padding: 10px 14px;
      border: 1px solid #fecaca;
      border-radius: 8px;
      background: var(--kr-chatbot-error-bg);
      color: var(--kr-chatbot-error-text);
      font-size: 13px;
      line-height: 1.4;
      flex-shrink: 0;
    }

    /* Action-confirmation card (Rail 3). Floats OVER the messages, anchored just
       above the composer — not part of the message scroll — so it stays put and
       in view while the transcript scrolls behind it. Its width matches the
       message text column: .messages-inner is 768px max with 32px padding, so the
       text column is 768 - 64. Plain-language ask + Yes/No for a non-technical
       user; no tool codes, no raw arguments. */
    .confirmation {
      position: absolute;
      left: 32px;
      right: 32px;
      /* Sits above the footer/composer with a clear gap between the two. */
      bottom: 104px;
      z-index: 6;
      max-width: 720px;
      margin: 0 auto;
      padding: 24px;
      border: 1px solid #d8d8d8;
      border-radius: 8px;
      background: var(--kr-chatbot-bg);
      color: var(--kr-chatbot-text);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      /* Never taller than the panel leaves room for — scroll the card itself if a
         batched multi-action ask is long, rather than covering the whole panel. */
      max-height: calc(100% - 176px);
      overflow-y: auto;
    }

    /* Inline variant (Claude-CLI style): sits IN the message stream as just
       another assistant message — no card chrome (padding/background/border/
       radius/shadow), no float offsets, no height cap. It flows and scrolls with
       the transcript. */
    .confirmation--inline {
      position: static;
      left: auto;
      right: auto;
      bottom: auto;
      z-index: auto;
      max-width: none;
      margin: 0;
      max-height: none;
      overflow: visible;
      padding: 0;
      border: none;
      border-radius: 0;
      background: none;
      box-shadow: none;
      /* scrollIntoView({ block: 'end' }) aligns the card's bottom to the viewport
         bottom; this margin makes it stop short so the Yes/No buttons aren't flush
         against (or clipped by) the fold. */
      scroll-margin-bottom: 24px;
    }

    /* The raw arguments, pretty-printed. Wraps rather than scrolling — the whole
       card grows with the transcript (the point of the inline variant). */
    .confirmation__details-input {
      margin: 8px 0 0;
      border-radius: 6px;
      color: var(--kr-chatbot-text);
      font-family: 'SF Mono', SFMono-Regular, ui-monospace, Menlo, Consolas, monospace;
      font-size: 13px;
      line-height: 1.5;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    /* "Run {tool}?" heading — names the tool being run. */
    .confirmation__title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 12px;
    }

    .confirmation__message {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
    }

    /* Batch list: one bullet per pending tool — its name, then the model's
       plain-language intent under it. */
    .confirmation__list {
      margin: 0;
      padding-left: 20px;
      font-size: 14px;
      line-height: 1.5;
    }

    .confirmation__list-item:not(:last-child) {
      margin-bottom: 8px;
    }

    .confirmation__list-name {
      display: block;
      font-weight: 600;
    }

    .confirmation__list-desc {
      display: block;
    }

    /* Buttons in a row; wrap on a narrow panel rather than overflowing the card. */
    .confirmation__actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      margin-top: 20px;
    }

    /* Keyboard-highlighted decision — a focus ring around the button, drawn
       outside its border so it reads over both the flat (Yes) and outline
       variants without recoloring or reshaping the button. Don't set
       border-radius here: that's the button's own (its pill shape); outline
       follows it and doesn't affect the button's size. */
    .confirmation__btn--highlighted {
      outline: 2px solid var(--kr-chatbot-primary);
      outline-offset: 2px;
    }

    /*
     * Side drawer, docked to the right of the inline chat, holding the Settings
     * and Debug views. The chat column shrinks (padding-right) to make room; the
     * drawer does not overlay. It stays mounted and slides in/out via a transform,
     * so opening and closing animate; switching views just swaps the content
     * inside (no re-slide, no width change). One consistent resizable width across
     * both views; the width is set inline from _drawerWidth.
     */
    .drawer {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      max-width: 100%;
      background: var(--kr-chatbot-bg);
      display: flex;
      flex-direction: column;
      transform: translateX(100%);
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      color: #000000;
      z-index: 5;
    }

    /* While dragging the resize handle, suppress the slide transition so the
       drawer tracks the cursor 1:1. */
    .drawer--resizing {
      transition: none;
    }

    .drawer--open {
      transform: translateX(0);
      box-shadow: -4px 0 20px rgba(10, 26, 46, 0.07);
    }

    /* Left-edge grab handle for resizing the drawer. */
    .drawer__resize {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: 10px;
      cursor: ew-resize;
      z-index: 10;
    }

    .drawer__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 12px 0 20px;
      height: 64px;
      flex-shrink: 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    /* Full-panel (floating) header: back button then title, left-aligned. */
    .drawer--full .drawer__header {
      justify-content: flex-start;
      gap: 8px;
      padding: 0 12px;
    }

    .drawer__title {
      font-size: 14px;
      font-weight: 600;
      color: #000000;
    }

    .drawer__body {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 4px 0;
    }

    /* Floating variant: the drawer fills the whole panel over the transcript (a
       full-panel view with a back button), rather than sliding in beside it. */
    .drawer--full {
      left: 0;
      width: 100%;
      max-width: 100%;
      z-index: 6;
    }

    .drawer--full.drawer--open {
      box-shadow: none;
    }

    /* History (previous conversations) view. */
    .history {
      display: flex;
      flex-direction: column;
    }

    /* "New conversation" action in the Conversations header — pushed to the right. */
    .history__new {
      margin-left: auto;
    }

    .history__empty {
      padding: 24px 20px;
      text-align: center;
      font-size: 13px;
      color: rgb(0 0 0 / 50%);
    }

    .history__list {
      display: flex;
      flex-direction: column;
      padding: 4px 0;
    }

    .history__item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
      padding: 10px 20px;
      background: transparent;
      border: none;
      text-align: left;
      cursor: pointer;
      transition: background 0.15s;
    }

    .history__item:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    .history__item--active {
      background: rgba(0, 0, 0, 0.06);
    }

    .history__item-title {
      font-size: 14px;
      color: #000000;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }

    .history__item-date {
      font-size: 12px;
      color: rgb(0 0 0 / 60%);
    }

    .debug-section {
      padding: 14px 20px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    .debug-section:last-child {
      border-bottom: none;
    }

    .debug-section__label {
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #000000;
      font-size: 11px;
      font-weight: 600;
      margin-bottom: 10px;
    }

    /* Context-fullness gauge */
    .debug-gauge {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .debug-gauge__bar {
      flex: 1;
      height: 8px;
      border-radius: 4px;
      background: rgb(244 244 244 / 100%);
      overflow: hidden;
    }

    .debug-gauge__fill {
      height: 100%;
      border-radius: 4px;
      background: var(--kr-chatbot-primary);
      transition: width 0.3s ease;
    }

    .debug-gauge__text {
      color: #000000;
      font-size: 12px;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }

    /* Metric rows */
    .debug-metrics {
      display: grid;
      grid-template-columns: auto 1fr;
      column-gap: 12px;
      row-gap: 7px;
      font-size: 13px;
    }

    .debug-metrics__key {
      color: #000000;
    }

    .debug-metrics__val {
      color: #000000;
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    .debug-cost {
      font-weight: 600;
    }

    .debug-metrics__key--sub {
      padding-left: 14px;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.65);
    }

    .debug-metrics__val--sub {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.65);
    }

    /* Per-iteration table (verbose). The wrapper scrolls horizontally on its own
       so a wide table never forces the whole drawer to scroll sideways. */
    .debug-iterations-scroll {
      overflow-x: auto;
      max-width: 100%;
    }

    .debug-iterations {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      font-variant-numeric: tabular-nums;
    }

    .debug-iterations th,
    .debug-iterations td {
      padding: 5px 6px;
      text-align: right;
      white-space: nowrap;
    }

    .debug-iterations th {
      color: #000000;
      font-weight: 600;
      border-bottom: 1px solid rgba(0, 0, 0, 0.10);
    }

    .debug-iterations tbody tr:not(:last-child) td {
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    .debug-iterations td:first-child,
    .debug-iterations th:first-child {
      text-align: left;
    }

    .debug-empty {
      color: #000000;
      padding: 20px;
      font-size: 13px;
      text-align: center;
    }

    /* Footer / Input */
    .footer {
      padding: 16px 16px 16px;
      flex-shrink: 0;
      background: white;
      width: 100%;
      max-width: 768px;
      margin-left: auto;
      margin-right: auto;
    }

    /* Inline mode: keep the composer clear of the bottom edge. */
    :host([variant='inline']) .footer {
      padding-bottom: 40px;
    }

    .input-wrapper {
      display: flex;
      align-items: center;
      gap: 4px;
      background: white;
      border: 1px solid var(--kr-chatbot-border);
      border-radius: 26px;
      padding: 6px;
      transition: border-color 0.2s, box-shadow 0.2s;
    }

    .input-wrapper:focus-within {
      border-color: #b0b0b0;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04);
    }

    /* Inline mode: keep the border and add a soft floating shadow (ChatGPT/Gemini style). */
    :host([variant='inline']) .input-wrapper {
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.1);
    }

    :host([variant='inline']) .input-wrapper:focus-within {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 12px 32px rgba(0, 0, 0, 0.14);
    }

    .input-plus {
      width: 34px;
      height: 34px;
      border: none;
      background: transparent;
      color: var(--kr-chatbot-text);
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 20px;
      font-weight: 300;
      transition: background 0.15s;
    }

    .input-plus:hover {
      background: #f8f9fc;
    }

    .input {
      flex: 1;
      border: none;
      outline: none;
      resize: none;
      font-family: inherit;
      font-size: 15px;
      line-height: 1.5;
      padding: 4px 0;
      min-height: 26px;
      max-height: 120px;
      background: transparent;
      color: black;
    }

    .input::placeholder {
      color: #b0b0b0;
    }

    /* margin-right rather than a wider .input-wrapper gap, which would also push
       the plus button and textarea apart. Applied to both mic states so the
       spacing doesn't shift when dictation swaps one for the other. */
    .mic,
    .mic-stop {
      margin-right: 4px;
    }

    /* Mode selector — Gemini-style inline label + caret before the mic. Anchors
       its upward menu; sized so it never grows the single-row composer height. */
    .mode {
      position: relative;
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }

    .mode-trigger {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      height: 34px;
      padding: 0 8px;
      border: none;
      background: transparent;
      color: var(--kr-chatbot-text);
      border-radius: 17px;
      cursor: pointer;
      font: inherit;
      font-size: 14px;
      white-space: nowrap;
      transition: background 0.15s;
    }

    .mode-trigger:hover {
      background: #f8f9fc;
    }

    .mode-trigger__caret {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    /* Menu opens upward, anchored to the trigger's right edge. */
    .mode-menu {
      position: absolute;
      bottom: calc(100% + 8px);
      right: 0;
      min-width: 220px;
      background: white;
      border: 1px solid #b0b0b0;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      padding: 6px 0;
      z-index: 10000;
      display: flex;
      flex-direction: column;
    }

    .mode-menu__item {
      display: flex;
      flex-direction: column;
      gap: 1px;
      width: 100%;
      padding: 8px 14px;
      background: none;
      border: none;
      text-align: left;
      cursor: pointer;
      font: inherit;
      color: var(--kr-chatbot-text);
    }

    .mode-menu__item:hover {
      background: #f3f4f6;
    }

    .mode-menu__item--active {
      background: #eef1f6;
    }

    .mode-menu__label {
      font-size: 14px;
      font-weight: 500;
    }

    .mode-menu__desc {
      font-size: 12px;
    }

    .mic {
      width: 34px;
      height: 34px;
      border: none;
      background: transparent;
      color: var(--kr-chatbot-text);
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: background 0.15s, color 0.15s;
    }

    .mic:hover {
      background: #f8f9fc;
    }

    .mic-icon {
      width: 21px;
      height: 21px;
    }

    /* Dictation swaps the mic for this stop button, sitting in the mic's slot so
       the composer's layout is unchanged and the textarea stays visible with the
       transcript streaming into it. Structurally identical to .stop; only the
       colors differ — dictation isn't an error state, so it takes a neutral grey
       instead of the error red, and that also keeps it distinct from the solid
       send button sitting right beside it. The icon is dark rather than white
       because white on this grey wouldn't meet contrast. */
    .mic-stop {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: #eae7e7;
      color: #1f1f1f;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: all 0.15s;
    }

    .mic-stop:hover {
      background: #ded9d9;
    }

    /* Larger than .stop-icon's 14px: the rect fills only half its 24-unit
       viewBox, so the drawn square is half this value. */
    .mic-stop-icon {
      width: 20px;
      height: 20px;
    }

    .send {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: var(--kr-chatbot-primary);
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: all 0.15s;
    }

    .send:hover {
      background: var(--kr-chatbot-primary-hover);
    }

    .send--disabled {
      opacity: 0.4;
      cursor: default;
    }

    .send--disabled:hover {
      background: var(--kr-chatbot-primary);
    }

    .send-icon {
      width: 16px;
      height: 16px;
    }

    .stop {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: var(--kr-chatbot-error-text);
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: all 0.15s;
    }

    .stop:hover {
      background: #b91c1c;
    }

    .stop-icon {
      width: 14px;
      height: 14px;
    }

    /* Header drag cursor */
    .header {
      cursor: grab;
      user-select: none;
    }

    .header:active {
      cursor: grabbing;
    }

    /* Inline mode isn't draggable — don't show the grab/hand cursor. */
    :host([variant='inline']) .header,
    :host([variant='inline']) .header:active {
      cursor: default;
    }

    /* Drag / Resize states */
    .panel--dragging,
    .panel--resizing {
      transition: none;
      user-select: none;
    }

    .panel--dragging {
      box-shadow: 0 32px 96px rgba(0, 0, 0, 0.18), 0 12px 40px rgba(0, 0, 0, 0.1);
    }

    /* Resize handles */
    .resize {
      position: absolute;
      z-index: 10;
    }

    .resize--n { top: -4px; left: 12px; right: 12px; height: 8px; cursor: n-resize; }
    .resize--s { bottom: -4px; left: 12px; right: 12px; height: 8px; cursor: s-resize; }
    .resize--w { left: -4px; top: 12px; bottom: 12px; width: 8px; cursor: w-resize; }
    .resize--e { right: -4px; top: 12px; bottom: 12px; width: 8px; cursor: e-resize; }
    .resize--nw { top: -4px; left: -4px; width: 16px; height: 16px; cursor: nw-resize; }
    .resize--ne { top: -4px; right: -4px; width: 16px; height: 16px; cursor: ne-resize; }
    .resize--sw { bottom: -4px; left: -4px; width: 16px; height: 16px; cursor: sw-resize; }
    .resize--se { bottom: -4px; right: -4px; width: 16px; height: 16px; cursor: se-resize; }

    /* Attachment thumbnails inside a sent message bubble */
    .attachments {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .message--user .attachments {
      justify-content: flex-end;
      margin-bottom: 24px;
    }

    /* A sent attachment is the same tile as the composer chip, so a file looks
       identical before and after sending. */
    .attachment {
      width: 112px;
      height: 112px;
      border-radius: 20px;
      overflow: hidden;
      background: #f2f0f0;
      border: none;
      cursor: zoom-in;
      flex-shrink: 0;
      padding: 0;
    }

    .attachment img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    /* Non-image attachment in a sent message — file-type label above the filename. */
    .attachment--doc {
      display: flex;
      flex-direction: column;
      padding: 16px 14px;
      box-sizing: border-box;
      text-align: left;
    }

    /* Opens the previewer on click — same affordance as the composer's doc chip. */
    .attachment--openable {
      cursor: pointer;
    }

    .attachment--openable:hover {
      background: #dfdddd;
    }

    .attachment__type {
      font-size: 13px;
      line-height: 1.2;
      color: #444746;
      text-transform: uppercase;
      flex-shrink: 0;
    }

    .attachment__name {
      margin-top: auto;
      font-size: 13px;
      line-height: 1.25;
      color: #000;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow-wrap: anywhere;
    }

    /* Skill menu (opened by typing '/'). Fixed + JS-positioned above the
       composer, following the krubble select-field dropdown pattern. */
    .skill-menu {
      position: fixed;
      z-index: 10000;
      background: white;
      border: 1px solid #9ba7b6;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      /* max-height is set dynamically to the space above the composer. */
    }

    /* Floating mode is a compact card — use a lighter shadow and softer border. */
    :host(:not([variant='inline'])) .skill-menu {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      border-color: #b0b0b0;
    }

    .skill-menu__list {
      list-style: none;
      margin: 0;
      padding: 8px 0;
      overflow-y: auto;
    }

    .skill-menu__item {
      display: flex;
      flex-direction: column;
      gap: 2px;
      width: 100%;
      padding: 8px 16px;
      background: none;
      border: none;
      text-align: left;
      cursor: pointer;
      font: inherit;
      color: #000000;
    }


    /* Floating mode is a compact card — tighten name/description spacing. */
    :host(:not([variant='inline'])) .skill-menu__item {
      gap: 1px;
    }

    .skill-menu__name {
      font-weight: 500;
    }

    /* Floating mode is a compact card — shrink the skill name a touch. */
    :host(:not([variant='inline'])) .skill-menu__name {
      font-size: 13px;
    }

    .skill-menu__desc {
      font-size: 12px;
      color: #000000;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .skill-menu__actions {
      display: flex;
      flex-direction: column;
      padding: 8px 0;
      border-bottom: 1px solid #eee;
      flex-shrink: 0;
    }

    .skill-menu__action {
      display: flex;
      flex-direction: column;
      gap: 2px;
      width: 100%;
      padding: 6px 16px;
      background: transparent;
      border: none;
      text-align: left;
      cursor: pointer;
      font: inherit;
      font-size: 13px;
      color: var(--kr-chatbot-primary);
      font-weight: 500;
    }

    /* Secondary line on an attach action (e.g. "reads these files") — muted,
       not the primary accent, so it reads as a hint under the action label. */
    .skill-menu__action .skill-menu__desc {
      font-weight: 400;
      color: #6b7280;
    }

    /* Highlight (keyboard) and hover (mouse) are independent — either shades the
       row. Declared after the base rules so it wins the cascade for both the
       action buttons and the skill items. */
    .skill-menu__action.skill-menu__item--highlighted,
    .skill-menu__item.skill-menu__item--highlighted,
    .skill-menu__action:hover,
    .skill-menu__item:hover {
      background: #f3f4f6;
    }

    /* Browse-all dialog body. Owns the height so the dialog is the same size on
       every tab; the search is fixed and the card grid flexes/scrolls to fill. */
    .skill-browse {
      display: flex;
      flex-direction: column;
      height: 70vh;
    }

    /* Slotted replacement for the dialog's built-in header so the detail view can
       put its back control where the title normally sits. Mirrors kr-dialog's
       own header: title left, close on the right. */
    .skill-browse__header {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }

    .skill-browse__header h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    .skill-browse__back {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-left: -8px;
      padding: 0;
      border: none;
      border-radius: 6px;
      background: none;
      color: #000000;
      cursor: pointer;
      transition: background-color 0.15s;
    }

    .skill-browse__back:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    .skill-browse__close {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-right: -8px;
      padding: 0;
      background: none;
      border: none;
      border-radius: 6px;
      color: #000000;
      cursor: pointer;
      transition: background-color 0.15s, color 0.15s;
    }

    .skill-browse__close:hover {
      background: rgba(0, 0, 0, 0.06);
      color: #000000;
    }

    .skill-browse__close svg {
      width: 22px;
      height: 22px;
    }

    .skill-browse kr-text-field {
      display: block;
      margin: 20px 20px 16px;
      flex-shrink: 0;
    }

    .skill-browse kr-tab-group {
      flex: 1;
      min-height: 0;
    }

    /* The grid is the scroller and spans the full width, so its scrollbar sits
       flush against the dialog edge; padding-right insets the cards from it. */
    .skill-browse__grid {
      height: 100%;
      overflow-y: auto;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-auto-rows: min-content;
      align-content: start;
      gap: 12px;
      padding: 16px 20px 20px;
    }

    .skill-card {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 14px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      text-align: left;
      cursor: pointer;
      font: inherit;
      color: #000000;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .skill-card:hover {
      border-color: var(--kr-chatbot-primary);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    /* A Kodaris-tab card the user can't invoke — not in their roles, or not installed
       here yet. Clicking it does nothing, so it drops the pointer cursor and the hover
       highlight. The Install button inside keeps its own pointer cursor. */
    /* Marks a globally-published skill listed on the Your Org tab — only happens on
       kodaris.com, which has no Kodaris tab for its own published skills. */
    .skill-card__badge {
      align-self: flex-start;
      padding: 2px 8px;
      border-radius: 10px;
      background: #f3f4f6;
      font-size: 11px;
      font-weight: 600;
      color: #000000;
    }

    /* Footer row on global skill cards: install button on the left, price on the
       right. margin-top:auto pins it to the bottom so cards in a row align their
       footers regardless of description length. Negative horizontal margins
       (matching the card's 14px padding) pull the top border out to the full
       card width; the content is re-padded horizontally to stay inset. */
    .skill-card__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-top: auto;
      margin-left: -14px;
      margin-right: -14px;
      padding: 14px 14px 0;
      border-top: 1px solid #e5e7eb;
    }

    .skill-card__price {
      font-size: 13px;
      font-weight: 600;
      color: #000000;
    }

    /* The install offer in the transcript for the registry skill the assistant offered
       (offer_skill) because it isn't installed here. Styled as the inline run-tool
       confirmation (title, plain-language lines, button row) so asking the user to
       install reads the same as asking them to approve an action. */
    /* Boxed, unlike the inline run-tool confirmation: the same border, radius and
       background as the floating confirmation card so it reads as one of them, but
       flowing in the transcript. */
    .skill-offer {
      padding: 20px 24px;
      border: 1px solid #d8d8d8;
      border-radius: 8px;
      background: var(--kr-chatbot-bg);
    }

    .skill-offer .confirmation__message + .confirmation__message {
      margin-top: 8px;
    }

    /* The offer's recorded outcome, shown in place of the buttons. */
    .skill-offer__installed,
    .skill-offer__declined {
      font-size: 14px;
      font-weight: 600;
    }

    .skill-offer__installed {
      /* Darker than the alert's success green (#16a34a) so a bold 14px label on white
         keeps text-level contrast. */
      color: #15803d;
    }

    .skill-offer__declined {
      color: var(--kr-chatbot-text-secondary);
    }

    .skill-install-confirm {
      display: flex;
      flex-direction: column;
      gap: 12px;
      font-size: 14px;
      line-height: 1.5;
    }

    .skill-install-confirm p {
      margin: 0;
    }

    .skill-install-confirm__list {
      margin: 0;
      padding-left: 20px;
    }

    .skill-card__name {
      font-weight: 600;
    }

    .skill-card__desc {
      font-size: 12px;
      color: #000000;
      /* Clamp to 3 lines so a long description doesn't make one card tower over
         its row-mates in the grid. */
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .skill-detail {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .skill-detail__body {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      gap: 18px;
      padding: 24px;
      overflow-y: auto;
    }

    /* Pinned to the bottom of the fixed-height dialog, mirroring kr-dialog-footer
       but left-aligned. */
    .skill-detail__footer {
      display: flex;
      justify-content: flex-start;
      flex-shrink: 0;
      gap: 8px;
      padding: 16px 24px;
      border-top: 1px solid rgba(0, 0, 0, 0.1);
    }

    .skill-detail__section {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .skill-detail__row {
      display: flex;
      align-items: baseline;
      gap: 8px;
    }

    /* Fixed label width so the Author and Price values start in the same column. */
    .skill-detail__row .skill-detail__label {
      flex-shrink: 0;
      width: 60px;
    }

    .skill-detail__title {
      font-size: 16px;
      font-weight: 600;
      color: #000000;
    }

    .skill-detail__label {
      font-size: 14px;
      font-weight: 600;
      color: #000000;
    }

    .skill-detail__summary {
      margin: 0;
      color: #000000;
      white-space: pre-wrap;
    }

    .skill-detail__value {
      color: #000000;
    }

    .skill-detail__chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .skill-detail__chip {
      padding: 2px 8px;
      border-radius: 6px;
      background: #f3f4f6;
      font-size: 12px;
      color: #000000;
    }

    .skill-browse__empty {
      height: 100%;
      padding: 32px;
      text-align: center;
      color: #000000;
    }

    /* Composer attachment chips (pre-send) */
    .composer-attachments {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 0 6px 10px;
    }

    /* Every attachment — image or document — is the same square tile, so a mixed
       row reads as one uniform set. */
    .chip {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 112px;
      height: 112px;
      border-radius: 20px;
      overflow: hidden;
      background: #f2f0f0;
      flex-shrink: 0;
      transition: background 0.28s ease;
    }

    .chip--doc {
      cursor: pointer;
    }

    .chip--doc:hover {
      background: #dfdddd;
    }

    /* The preview body — an image fills the tile; a document shows its file-type
       label above the filename, both left-aligned. */
    .chip-body {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
      padding: 16px 14px;
      box-sizing: border-box;
      overflow: hidden;
    }

    /* An image fills the whole tile, so it needs no padding around it. */
    .chip-body--image {
      padding: 0;
    }

    .chip img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    /* File-type label (PDF, XLSX, …) — sits at the top of the tile. */
    .chip-type {
      font-size: 13px;
      line-height: 1.2;
      color: #444746;
      text-transform: uppercase;
      flex-shrink: 0;
    }

    /* Filename — pinned to the bottom of the tile, wrapping to at most three
       lines before it ellipsizes. */
    .chip-name {
      margin-top: auto;
      font-size: 13px;
      line-height: 1.25;
      color: #000;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow-wrap: anywhere;
    }

    /* Remove button — a bare X in the tile's top-right corner, revealed only when
       the tile (or the button itself) is hovered or keyboard-focused. */
    .chip-remove {
      position: absolute;
      /* Inset matches .chip-body's padding so the X sits the same distance from
         the right edge as the file-type label does from the left. */
      top: 14px;
      right: 14px;
      width: 15px;
      height: 15px;
      border: none;
      background: none;
      color: #444746;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      line-height: 1;
      flex-shrink: 0;
      opacity: 0;
      transition: opacity 0.28s ease;
    }

    .chip:hover .chip-remove,
    .chip-remove:focus-visible {
      opacity: 1;
    }

    .chip-remove:hover {
      color: #000;
    }

    .chip-remove svg {
      width: 15px;
      height: 15px;
    }

    /* Drag-and-drop overlay */
    .drop-overlay {
      position: absolute;
      inset: 0;
      z-index: 20;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 10px;
      /* Frosted, mostly-opaque surface so the chat content behind is masked
         and doesn't clash with the overlay label. */
      background: rgba(255, 255, 255, 0.92);
      backdrop-filter: blur(3px);
      border: 2px dashed var(--kr-chatbot-primary);
      border-radius: 16px;
      color: var(--kr-chatbot-primary);
      font-weight: 600;
      font-size: 15px;
      pointer-events: none;
    }

    .drop-overlay svg {
      width: 32px;
      height: 32px;
    }

  `;
__decorate([
    property({ attribute: false })
], KRChatbot.prototype, "send", void 0);
__decorate([
    property({ attribute: false })
], KRChatbot.prototype, "load", void 0);
__decorate([
    property({ attribute: false })
], KRChatbot.prototype, "messages", void 0);
__decorate([
    property({ type: String })
], KRChatbot.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRChatbot.prototype, "placeholder", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true
    })
], KRChatbot.prototype, "opened", void 0);
__decorate([
    property({
        type: Boolean,
        attribute: 'hide-toggle',
        reflect: true
    })
], KRChatbot.prototype, "hideToggle", void 0);
__decorate([
    property({
        type: String,
        reflect: true
    })
], KRChatbot.prototype, "variant", void 0);
__decorate([
    property({
        type: Number,
        attribute: 'conversation-id',
        reflect: true
    })
], KRChatbot.prototype, "conversationId", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_inputValue", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_isStreaming", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_error", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_conversationTitle", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_contextWarning", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_contextLimitReached", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_contextLimitMessage", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_pendingActions", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_confirmationHighlighted", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_confirmationInFlight", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_telemetry", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_iterationLog", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_cacheCheckpoints", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_drawerOpen", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_drawerView", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_conversations", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_conversationsLoading", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_toolDetail", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_toolDetailCopied", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_toolDetailInputOpen", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_toolDetailOutputOpen", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_drawerWidth", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_isDrawerResizing", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_pendingConversationFiles", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_isDraggingFile", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_skillMenuOpened", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_skillQuery", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_orgSkills", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_globalSkills", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_invocableSkills", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_suggestedActions", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_aiFrameworkConfig", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_skillHighlighted", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_copiedIndex", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_skillBrowseOpened", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_skillBrowseDetail", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_skillInstallConfirm", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_skillOfferConfirm", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_skillBrowseSource", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_skillBrowseQuery", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_isDictating", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_mode", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_modeMenuOpened", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_showTools", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_showToolInputs", void 0);
__decorate([
    state()
], KRChatbot.prototype, "_showConfirmationInput", void 0);
__decorate([
    query('.panel')
], KRChatbot.prototype, "_panelEl", void 0);
__decorate([
    query('.messages')
], KRChatbot.prototype, "_messagesEl", void 0);
__decorate([
    query('.input')
], KRChatbot.prototype, "_inputEl", void 0);
__decorate([
    query('.file-input')
], KRChatbot.prototype, "_fileInputEl", void 0);
__decorate([
    query('.input-wrapper')
], KRChatbot.prototype, "_inputWrapperEl", void 0);
__decorate([
    query('.skill-menu')
], KRChatbot.prototype, "_skillMenuEl", void 0);
__decorate([
    query('.drawer__body')
], KRChatbot.prototype, "_drawerBodyEl", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-chatbot')) {
    customElements.define('kr-chatbot', KRChatbot);
}
//# sourceMappingURL=chatbot.js.map